<template>
  <v-col
    data-elementName="TimeView"
  >
    <!-- Loading/Setup State -->
    <v-row
      v-if="!isViewReady"
      data-elementName="TimeLoader"
    >
      <v-col class="text-center pa-8">
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
        />
        <div class="mt-4 text-h6 coolgray--text">
          Loading Time View...
        </div>
      </v-col>
    </v-row>

    <!-- Main Content -->
    <v-row
      v-else
      data-elementName="TimeMainContent"
    >
      <!-- Compact Header Section - Only render when view is fully ready -->
      <v-col
        v-if="isViewReady"
        class="time-view-header-compact mb-3"
      >
        <!-- Main Header Bar with Tabs -->
        <div class="tabs-section">
          <v-toolbar
            flat
            color="white"
            class="header-toolbar px-0 py-0"
            height="56"
          >
            <!-- Title Icon -->
            <div class="header-icon-wrapper mr-3">
              <font-awesome-icon
                :icon="['fas', 'clock']"
                size="lg"
                class="primary--text"
              />
            </div>

            <!-- Inline Tabs -->
            <v-tabs
              v-model="activeTab"
              color="primary"
              slider-color="primary"
              show-arrows
              class="time-view-tabs-inline flex-grow-1"
              height="56"
            >
              <!-- Custom Font Awesome icons for navigation arrows -->
              <template v-slot:prev>
                <font-awesome-icon
                  :icon="['fas', 'chevron-left']"
                  size="xs"
                  class="tab-nav-arrow"
                  focusable="false"
                  style="height: 16px !important; width: 16px !important;"
                />
              </template>

              <template v-slot:next>
                <font-awesome-icon
                  :icon="['fas', 'chevron-right']"
                  size="xs"
                  class="tab-nav-arrow"
                  focusable="false"
                  style="height: 16px !important; width: 16px !important;"
                />
              </template>

              <v-tab
                v-for="tab in tabsWithCounts"
                :key="tab.id"
                class="tab-compact"
                @click="setCurrentTabId(tab.id)"
              >
                <font-awesome-icon
                  :icon="tab.icon"
                  class="mr-2"
                  size="sm"
                />
                {{ tab.label }}
                <!-- Skeleton loader while main loading OR this specific tab is loading -->
                <v-skeleton-loader
                  v-if="loading || tabLoadingStates[tab.id]"
                  type="chip"
                  max-width="40"
                  class="ml-2"
                />
                <!-- Actual count chip when data is loaded for this tab -->
                <v-chip
                  v-else-if="tab.count !== undefined"
                  x-small
                  outlined
                  class="ml-2 tab-count-chip"
                >
                  {{ tab.count }}
                </v-chip>
              </v-tab>
            </v-tabs>

            <v-spacer />

            <!-- Action Buttons -->
            <v-col class="header-actions d-flex align-center col-auto">
              <!-- Help Button -->
              <v-btn
                icon
                class="mr-1"
                aria-label="Time view help"
                @click.stop="openTimeHelpDialog"
              >
                <font-awesome-icon
                  :icon="['fas', 'circle-question']"
                  size="lg"
                  class="coolgray--text"
                />
              </v-btn>

              <!-- Templates Menu (always visible) -->
              <v-menu
                v-if="entryTemplates.length > 0"
                offset-y
                left
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    color="primary"
                    class="mr-1"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-badge
                      :content="entryTemplates.length"
                      color="primary"
                      overlap
                      offset-x="10"
                      offset-y="10"
                    >
                      <font-awesome-icon :icon="['fas', 'list']" />
                    </v-badge>
                  </v-btn>
                </template>

                <v-list>
                  <v-list-item
                    v-for="template in entryTemplates"
                    :key="template.id"
                    @click="applyTemplate(template)"
                  >
                    <v-list-item-content>
                      <v-list-item-title>{{ template.name }}</v-list-item-title>
                      <v-list-item-subtitle>
                        {{ template.clientName }} • {{ template.timeClassificationName }} • {{ durationFormatter(template.duration) }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-btn
                        icon
                        small
                        @click.stop="deleteTemplate(template.id)"
                      >
                        <font-awesome-icon
                          :icon="['fas', 'trash']"
                          size="sm"
                        />
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </v-menu>

              <!-- Conditional: Show Bulk Actions OR Add Entry Button -->
              <v-col class="action-buttons-container justify-end col-auto">
                <!-- Bulk Actions (shown when items are selected) -->
                <v-slide-x-reverse-transition mode="out-in">
                  <div
                    v-if="enableBulkOperations && selectedEntries.length > 0 && currentTabId === 'time-entry'"
                    key="bulk-actions"
                    class="d-flex align-center"
                  >
                    <v-chip
                      color="primary"
                      text-color="white"
                      small
                      close
                      class="mr-2 font-weight-bold"
                      @click:close="clearSelection"
                    >
                      {{ selectedEntries.length }} selected
                    </v-chip>

                    <v-btn
                      color="primary"
                      outlined
                      small
                      class="mr-2"
                      @click="openBulkCopyDialog"
                    >
                      <font-awesome-icon
                        :icon="['fas', 'copy']"
                        size="sm"
                        class="mr-1"
                      />
                      Copy
                    </v-btn>

                    <v-btn
                      color="error"
                      outlined
                      small
                      @click="openBulkDeleteDialog"
                    >
                      <font-awesome-icon
                        :icon="['fas', 'trash']"
                        size="sm"
                        class="mr-1"
                      />
                      Delete
                    </v-btn>
                  </div>

                  <!-- Add Entry Button (shown when no items selected or when bulk operations disabled, hidden on mobile) -->
                  <v-btn
                    v-if="!enableBulkOperations || selectedEntries.length === 0"
                    key="add-entry"
                    color="primary"
                    depressed
                    class="add-time-btn-compact mr-2 d-none d-md-flex"
                    @click="openTimeEntryFormCreate(today)"
                  >
                    <font-awesome-icon
                      :icon="['fas', 'plus']"
                      size="sm"
                    />
                    <span class="ml-2">Add Entry</span>
                  </v-btn>
                </v-slide-x-reverse-transition>
              </v-col>
            </v-col>
          </v-toolbar>

          <v-divider />

          <!-- Compact Controls Bar -->
          <div class="controls-bar">
            <v-row class="align-center ma-0 pa-3 pl-2 controls-row">
              <!-- Search Field -->
              <v-col
                cols="12"
                sm="4"
                md="3"
                lg="2"
                class="py-1"
              >
                <search-field
                  v-model="tableSearch"
                  placeholder="Search entries..."
                />
              </v-col>

              <!-- Data Tools (Filter, Group, Sort, Columns, Jump) -->
              <v-col
                cols="auto"
                class="py-1"
              >
                <tools-toolbar
                  :current-required-columns="currentRequiredColumns"
                  :report-key="reportKey"
                  :table-columns="tableColumns"
                  :view-context="currentTabContext"
                  :show-preview="!!previewFilters"
                  :preview-count="previewCount"
                  :active-filter-name="activeFilterName"
                  saved-filters-view-name="time"
                  :current-filters="appliedFilters"
                  @clear-active-filter="clearActiveFilter"
                  @on-apply-filters="handleToolsDrawerApplyFilters"
                  @on-reset-filters="handleToolsDrawerResetFilters"
                  @on-grouping-change="onGroupingChange"
                  @on-sorting-change="onSortingChange"
                >
                  <template v-slot:filter-controls>
                    <time-filter-panel
                      ref="timeFilterPanel"
                      :applied-filters="appliedFilters"
                      :person-filter-items="personFilterItems"
                      :client-filter-items="clientFilterItems"
                      :time-type-filter-items="timeTypeFilterItems"
                      :project-filter-items="projectFilterItems"
                      @preview="handleFilterPreview"
                    />
                  </template>
                </tools-toolbar>
              </v-col>

              <!-- Refresh Button -->
              <v-col
                cols="auto"
                class="py-1"
              >
                <v-btn
                  outlined
                  class="text-none"
                  :loading="isRefreshing"
                  :disabled="isRefreshing || loading"
                  @click="refreshData"
                >
                  <font-awesome-icon
                    :icon="['fas', 'arrows-rotate']"
                    size="sm"
                    :class="{ 'mr-1': $vuetify.breakpoint.mdAndUp }"
                  />
                  <span class="d-none d-md-inline">Refresh</span>
                </v-btn>
              </v-col>

              <!-- Export Button -->
              <v-col
                v-if="showExportBar"
                cols="auto"
                class="py-1"
              >
                <v-menu
                  offset-y
                  nudge-bottom="6"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      color="primary"
                      depressed
                      v-bind="attrs"
                      v-on="on"
                    >
                      <font-awesome-icon
                        :icon="['fas', 'circle-down']"
                        size="sm"
                        :class="{ 'mr-2': $vuetify.breakpoint.mdAndUp }"
                      />
                      <span class="d-none d-md-inline">Export</span>
                    </v-btn>
                  </template>

                  <v-list>
                    <v-list-item
                      v-for="item in exportOptions"
                      :key="item.key"
                      :disabled="!item.active"
                      @click="item.callback"
                    >
                      <v-list-item-title v-if="item.key !== 'xlsx'">
                        {{ item.text }}
                      </v-list-item-title>
                      <v-list-item-title v-else>
                        <export-xlsx
                          :item="item"
                          :show-export-bar="showExportBar"
                          :columns="exportColumns"
                          :groups="exportGroups"
                          :export-report-name="exportReportName"
                        />
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-col>

              <!-- View Mode Dropdown (Calendar/Column/List for Time Entry) -->
              <v-col
                v-if="currentTabId === 'time-entry'"
                cols="12"
                sm="auto"
                class="py-1 controls-view-mode"
              >
                <v-menu
                  offset-y
                  nudge-bottom="6"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      outlined
                      class="text-none"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <font-awesome-icon
                        :icon="weekViewMode === 'calendar' ? ['fas', 'calendar-week'] : ['fas', 'columns']"
                        size="sm"
                        :class="{ 'mr-2': $vuetify.breakpoint.mdAndUp }"
                      />
                      <span class="d-none d-md-inline">{{ weekViewMode === 'calendar' ? 'Horizontal' : 'Vertical' }}</span>
                      <font-awesome-icon
                        :icon="['fas', 'chevron-down']"
                        size="xs"
                        class="ml-2 d-none d-md-inline"
                      />
                    </v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item
                      :class="{ 'v-list-item--active primary--text': weekViewMode === 'calendar' }"
                      @click="weekViewMode = 'calendar'"
                    >
                      <v-list-item-icon class="mr-3">
                        <font-awesome-icon :icon="['fas', 'calendar-week']" />
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>Horizontal Layout</v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action v-if="weekViewMode === 'calendar'">
                        <font-awesome-icon
                          :icon="['fas', 'check']"
                          class="primary--text"
                        />
                      </v-list-item-action>
                    </v-list-item>
                    <v-list-item
                      :class="{ 'v-list-item--active primary--text': weekViewMode === 'columns' }"
                      @click="weekViewMode = 'columns'"
                    >
                      <v-list-item-icon class="mr-3">
                        <font-awesome-icon :icon="['fas', 'columns']" />
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>Vertical Layout</v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action v-if="weekViewMode === 'columns'">
                        <font-awesome-icon
                          :icon="['fas', 'check']"
                          class="primary--text"
                        />
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-col>

              <!-- View Mode Dropdown (Cards/Table for other tabs) -->
              <v-col
                v-else
                cols="12"
                sm="auto"
                class="py-1 controls-view-mode"
              >
                <v-menu
                  offset-y
                  nudge-bottom="6"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      outlined
                      class="text-none"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <font-awesome-icon
                        :icon="listViewMode === 'cards' ? ['fas', 'th-large'] : ['fas', 'table']"
                        size="sm"
                        :class="{ 'mr-2': $vuetify.breakpoint.mdAndUp }"
                      />
                      <span class="d-none d-md-inline">{{ listViewMode === 'cards' ? 'Cards' : 'Table' }}</span>
                      <font-awesome-icon
                        :icon="['fas', 'chevron-down']"
                        size="xs"
                        class="ml-2 d-none d-md-inline"
                      />
                    </v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item
                      :class="{ 'v-list-item--active primary--text': listViewMode === 'cards' }"
                      @click="listViewMode = 'cards'"
                    >
                      <v-list-item-icon class="mr-3">
                        <font-awesome-icon :icon="['fas', 'th-large']" />
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>Card View</v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action v-if="listViewMode === 'cards'">
                        <font-awesome-icon
                          :icon="['fas', 'check']"
                          class="primary--text"
                        />
                      </v-list-item-action>
                    </v-list-item>
                    <v-list-item
                      :class="{ 'v-list-item--active primary--text': listViewMode === 'table' }"
                      @click="listViewMode = 'table'"
                    >
                      <v-list-item-icon class="mr-3">
                        <font-awesome-icon :icon="['fas', 'table']" />
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>Table View</v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action v-if="listViewMode === 'table'">
                        <font-awesome-icon
                          :icon="['fas', 'check']"
                          class="primary--text"
                        />
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-col>

              <!-- Date and Navigation Controls -->
              <v-col
                cols="12"
                sm="auto"
                class="py-1 d-flex align-center justify-end controls-date-nav"
              >
                <!-- Time Shift Navigation (Time Entry view only) -->
                <template v-if="currentTabId === 'time-entry'">
                  <v-btn
                    icon
                    small
                    color="primary"
                    class="mr-1"
                    @click="timeShiftLeft"
                  >
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <font-awesome-icon
                          style="outline-color: transparent;"
                          :icon="['fas', 'chevron-left']"
                          focusable="false"
                          v-bind="attrs"
                          v-on="on"
                        />
                      </template>
                      <span>Previous Week</span>
                    </v-tooltip>
                  </v-btn>

                  <!-- Week Number Display -->
                  <v-chip
                    small
                    outlined
                    color="primary"
                    class="mx-1 font-weight-medium"
                  >
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <span
                          v-bind="attrs"
                          v-on="on"
                        >
                          W{{ currentWeekNumber }}
                        </span>
                      </template>
                      <span>Week {{ currentWeekNumber }} of {{ currentYear }}</span>
                    </v-tooltip>
                  </v-chip>

                  <v-btn
                    icon
                    small
                    color="primary"
                    class="ml-1 mr-2"
                    @click="timeShiftRight"
                  >
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <font-awesome-icon
                          style="outline-color: transparent;"
                          :icon="['fas', 'chevron-right']"
                          focusable="false"
                          v-bind="attrs"
                          v-on="on"
                        />
                      </template>
                      <span>Next Week</span>
                    </v-tooltip>
                  </v-btn>
                </template>

                <!-- Date Range Picker (all views) - Only render when currentDateRange is valid -->
                <date-range-picker-button
                  v-if="currentDateRange && currentDateRange.length === 2"
                  :loading="applyingDateFilter"
                  :initial-start-date="currentDateRange[0]"
                  :initial-end-date="currentDateRange[1]"
                  @apply-date-range="onApplyDateRange"
                  @clear-date-range="onClearDateRange"
                />
              </v-col>
            </v-row>
          </div>
        </div>
      </v-col>

      <!-- Jump to Group (contextual row) -->
      <v-col
        v-if="filteredGroups && filteredGroups.length >= 2"
        cols="12"
        class="py-0 px-4"
      >
        <jump-to-group
          :groups="filteredGroups"
          class="mb-1"
          @scroll-to-group="scrollToGroup"
        />
      </v-col>

      <!-- Compact Stacked Time Breakdown -->
      <v-row
        wrap
        class="justify-center mt-3"
      >
        <v-col
          class="pt-0 px-4"
        >
          <!-- Time Entry View -->
          <v-row
            v-if="currentTabId === 'time-entry'"
            class="justify-center"
          >
            <v-col>
              <v-card
                v-if="currentWeek && currentWeek.week"
                class="week-container"
                outlined
              >
                <v-card-actions class="px-6 pt-4">
                  <v-col class="pa-0">
                    <!-- Locked Week Notices -->
                    <v-card
                      v-if="week.length && lockedDates.length === week.length"
                      color="warning"
                      outlined
                      class="mb-3"
                    >
                      <v-card-text>
                        This week is out of range for edits.
                        Message an administrator to request edits to time
                        entries outside the allowed date range.
                      </v-card-text>
                    </v-card>

                    <!-- Partial Locked Week Notice -->
                    <v-card
                      v-else-if="week.length && lockedDates.length"
                      color="warning"
                      outlined
                      class="mb-3"
                    >
                      <v-card-text>
                        Some dates in this week are out of range.
                        Message an administrator to request edits to time
                        entries outside the allowed date range.
                      </v-card-text>
                    </v-card>

                    <!-- CALENDAR VIEW (Option 1) - Day-grouped layout -->
                    <div v-if="weekViewMode === 'calendar'">
                      <!-- Loading State: Skeleton Cards -->
                      <div v-if="loading">
                        <v-card
                          v-for="n in 7"
                          :key="`skeleton-day-${n}`"
                          class="mt-3 py-2"
                          outlined
                        >
                          <v-card-title class="py-2 px-4">
                            <v-skeleton-loader
                              type="heading"
                              max-width="200"
                            />
                          </v-card-title>
                          <v-card-text>
                            <v-row>
                              <v-col
                                v-for="c in 3"
                                :key="`skeleton-card-${c}`"
                                cols="12"
                                sm="6"
                                md="4"
                                lg="3"
                              >
                                <v-skeleton-loader
                                  type="card"
                                  elevation="0"
                                />
                              </v-col>
                            </v-row>
                          </v-card-text>
                        </v-card>
                      </div>

                      <!-- Daily Entries -->
                      <template v-else>
                        <v-card
                          v-for="(weekdate, index) in currentWeek.week"
                          :key="index"
                          :class="`mt-3 py-2 ${setEntryClass(weekdate, index)}`"
                          outlined
                        >
                          <v-container
                            class="pa-0 ma-0"
                          >
                            <!-- WeekDate's Header -->
                            <v-card-title
                              primary-title
                              class="py-2 px-3"
                            >
                              <v-row class="align-center ma-0">
                                <v-col
                                  cols="auto"
                                  class="pa-0"
                                >
                                  <div :class="`subtitle-2 font-weight-bold ${lockedDates.includes(weekdate.date.valueOf()) ? 'coolgray' : 'darkestblue'}--text`">
                                    {{ weekdate.formatted }}
                                  </div>
                                </v-col>

                                <v-spacer />

                                <v-col
                                  cols="auto"
                                  class="pa-0 mr-2"
                                >
                                  <v-chip
                                    :color="lockedDates.includes(weekdate.date.valueOf()) ? 'grey lighten-1' : 'primary'"
                                    :text-color="lockedDates.includes(weekdate.date.valueOf()) ? 'grey darken-2' : 'white'"
                                    small
                                    class="font-weight-bold"
                                  >
                                    <font-awesome-icon
                                      :icon="['fas', 'clock']"
                                      class="mr-2 clock-icon-12px"
                                    />
                                    {{ durationFormatter(weekdate.total) }}
                                  </v-chip>
                                </v-col>

                                <v-col
                                  v-if="enableBulkOperations"
                                  cols="auto"
                                  class="pa-0"
                                >
                                  <Checkbox
                                    :input-value="areAllDayEntriesSelected(weekdate.entries)"
                                    :indeterminate="areSomeDayEntriesSelected(weekdate.entries) && !areAllDayEntriesSelected(weekdate.entries)"
                                    :disabled="lockedDates.includes(weekdate.date.valueOf()) || weekdate.filteredEntries.length === 0"
                                    class="mt-0 mr-1"
                                    @change="toggleDaySelection(weekdate.entries, $event)"
                                  />
                                </v-col>

                                <v-col
                                  cols="auto"
                                  class="pa-0"
                                >
                                  <v-btn
                                    icon
                                    small
                                    color="primary"
                                    :disabled="lockedDates.includes(weekdate.date.valueOf())"
                                    @click="openTimeEntryFormCreate(weekdate.dateValue)"
                                  >
                                    <font-awesome-icon :icon="['fas', 'plus']" />
                                  </v-btn>
                                </v-col>
                              </v-row>
                            </v-card-title>

                            <!-- WeekDate's Entries Cards -->
                            <v-card-text v-if="weekdate.filteredEntries.length">
                              <!-- Grouped View: Show entries grouped by selected property -->
                              <template v-if="hasActiveGrouping">
                                <div
                                  v-for="group in getGroupedEntriesForDay(weekdate.filteredEntries)"
                                  :key="group.groupName || 'ungrouped'"
                                  class="calendar-group-section mb-3"
                                >
                                  <!-- Group Header (only show if actually grouped) -->
                                  <div
                                    v-if="group.isPartOfGroup"
                                    class="calendar-group-header d-flex align-center pa-2 mb-2"
                                    @click="toggleCalendarGroup(weekdate.dateValue, group.groupName)"
                                  >
                                    <font-awesome-icon
                                      :icon="['fas', 'chevron-right']"
                                      size="sm"
                                      class="mr-2 coolgray--text transition-rotate flex-shrink-0"
                                      :class="{ 'rotate-90': !isCalendarGroupCollapsed(weekdate.dateValue, group.groupName) }"
                                    />
                                    <span class="body-2 font-weight-bold text-truncate">{{ group.groupName || 'Unassigned' }}</span>
                                    <v-spacer />
                                    <v-chip
                                      small
                                      outlined
                                      color="primary"
                                      class="ml-2 flex-shrink-0"
                                    >
                                      {{ group.items.length }} {{ group.items.length === 1 ? 'entry' : 'entries' }}
                                    </v-chip>
                                    <v-chip
                                      small
                                      color="primary"
                                      class="ml-1 font-weight-bold flex-shrink-0"
                                    >
                                      {{ durationFormatter(getGroupTotalMinutes(group.items)) }}
                                    </v-chip>
                                  </div>

                                  <!-- Group Entries (collapsible) -->
                                  <v-expand-transition>
                                    <v-row
                                      v-show="!group.isPartOfGroup || !isCalendarGroupCollapsed(weekdate.dateValue, group.groupName)"
                                      class="time-entries-grid"
                                    >
                                      <v-col
                                        v-for="item in group.items"
                                        :key="item.timeEntryId"
                                        cols="12"
                                        sm="6"
                                        md="4"
                                        lg="3"
                                      >
                                        <v-card
                                          class="time-entry-card"
                                          :class="{ 'entry-selected': isEntrySelected(item.timeEntryId) }"
                                          outlined
                                        >
                                          <!-- Card Header with Duration and Time Type -->
                                          <v-card-title class="py-2 px-3 d-flex align-center">
                                            <!-- Duration -->
                                            <div class="d-flex align-center mr-3">
                                              <font-awesome-icon
                                                :icon="['fas', 'clock']"
                                                class="mr-2 coolgray--text clock-icon-12px"
                                              />

                                              <span class="body-2 font-weight-bold">
                                                {{ durationFormatter(item.duration) }}
                                              </span>
                                            </div>

                                            <v-spacer />

                                            <v-chip
                                              :color="getTimeTypeColor(item.timeClassificationName)"
                                              :text-color="getTimeTypeColor(item.timeClassificationName)"
                                              small
                                              outlined
                                              class="time-type-chip-header"
                                            >
                                              {{ item.timeClassificationName }}
                                            </v-chip>
                                          </v-card-title>

                                          <v-divider />

                                          <!-- Card Body with Entry Details -->
                                          <v-card-text class="py-2 px-3">
                                            <!-- Person Name (conditional) -->
                                            <div
                                              v-if="shouldShowPersonFilter"
                                              class="entry-info-row mb-2"
                                            >
                                              <font-awesome-icon
                                                :icon="['fas', 'user']"
                                                size="sm"
                                                class="icon-fixed-width primary--text"
                                              />
                                              <div class="entry-info-content">
                                                <span class="body-2 font-weight-bold">
                                                  {{ item.personName }}
                                                </span>
                                              </div>
                                            </div>

                                            <!-- Client & Billing Code - Icon-based -->
                                            <div class="entry-info-row mb-2">
                                              <font-awesome-icon
                                                :icon="['fas', 'building']"
                                                size="sm"
                                                class="icon-fixed-width primary--text"
                                              />
                                              <div class="entry-info-content">
                                                <div
                                                  class="client-billing-row"
                                                  :title="formatClientWithBilling(item.clientName, item.billingCode, item.clientId)"
                                                >
                                                  <span class="client-name-text body-2 font-weight-medium">
                                                    {{ item.clientName || 'N/A' }}
                                                  </span>
                                                  <span
                                                    v-if="resolveBillingCode(item.billingCode, item.clientId, item.clientName)"
                                                    class="billing-code-badge caption"
                                                  >
                                                    {{ resolveBillingCode(item.billingCode, item.clientId, item.clientName) }}
                                                  </span>
                                                </div>
                                              </div>
                                            </div>

                                            <!-- Project - Icon-based (if exists) -->
                                            <div
                                              v-if="item.projectName"
                                              class="entry-info-row mb-2"
                                            >
                                              <font-awesome-icon
                                                :icon="['fas', 'folder']"
                                                size="sm"
                                                class="icon-fixed-width primary--text"
                                              />
                                              <div class="entry-info-content">
                                                <span class="body-2">{{ item.projectName }}</span>
                                              </div>
                                            </div>

                                            <!-- Notes Display (if exists) -->
                                            <div
                                              v-if="item.description"
                                              class="entry-notes-display mt-2"
                                            >
                                              <div class="body-2 work-notes-text">
                                                {{ item.description }}
                                              </div>
                                            </div>
                                          </v-card-text>

                                          <v-divider />

                                          <!-- Card Actions -->
                                          <v-card-actions class="py-2 px-3 justify-space-between">
                                            <!-- Action Icons Group -->
                                            <div
                                              class="d-flex align-center"
                                              role="group"
                                              aria-label="Time entry actions"
                                            >
                                              <v-tooltip top>
                                                <template v-slot:activator="{ on, attrs }">
                                                  <v-btn
                                                    icon
                                                    :disabled="lockedDates.includes(weekdate.dateValue)"
                                                    class="mr-1 action-btn action-btn-duplicate"
                                                    aria-label="Copy time entry"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                    @click.stop="openTimeEntryFormDuplicate(item, weekdate.dateValue)"
                                                  >
                                                    <font-awesome-icon
                                                      :icon="['fas', 'copy']"
                                                      aria-hidden="true"
                                                    />
                                                  </v-btn>
                                                </template>
                                                <span>Copy entry</span>
                                              </v-tooltip>

                                              <v-tooltip top>
                                                <template v-slot:activator="{ on, attrs }">
                                                  <v-btn
                                                    icon
                                                    :disabled="lockedDates.includes(weekdate.dateValue)"
                                                    class="mr-1 action-btn action-btn-edit"
                                                    aria-label="Edit time entry"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                    @click.stop="openTimeEntryFormUpdate(item)"
                                                  >
                                                    <font-awesome-icon
                                                      :icon="['fas', 'pencil']"
                                                      aria-hidden="true"
                                                    />
                                                  </v-btn>
                                                </template>
                                                <span>Edit entry</span>
                                              </v-tooltip>

                                              <v-tooltip top>
                                                <template v-slot:activator="{ on, attrs }">
                                                  <v-btn
                                                    icon
                                                    :disabled="lockedDates.includes(weekdate.dateValue)"
                                                    class="action-btn action-btn-delete"
                                                    aria-label="Delete time entry"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                    @click.stop="openTimeEntryFormDelete(item)"
                                                  >
                                                    <font-awesome-icon
                                                      :icon="['fas', 'trash']"
                                                      aria-hidden="true"
                                                    />
                                                  </v-btn>
                                                </template>
                                                <span>Delete entry</span>
                                              </v-tooltip>
                                            </div>

                                            <!-- Checkbox on Right -->
                                            <Checkbox
                                              v-if="enableBulkOperations"
                                              :input-value="isEntrySelected(item.timeEntryId)"
                                              :disabled="lockedDates.includes(weekdate.dateValue)"
                                              class="mt-0"
                                              aria-label="Select time entry"
                                              @change="toggleEntrySelection(item.timeEntryId, $event)"
                                            />
                                          </v-card-actions>
                                        </v-card>
                                      </v-col>
                                    </v-row>
                                  </v-expand-transition>
                                </div>
                              </template>

                              <!-- Ungrouped View: Flat list of entries -->
                              <v-row
                                v-else
                                class="time-entries-grid"
                              >
                                <v-col
                                  v-for="item in weekdate.filteredEntries"
                                  :key="item.timeEntryId"
                                  cols="12"
                                  sm="6"
                                  md="4"
                                  lg="3"
                                >
                                  <v-card
                                    class="time-entry-card"
                                    :class="{ 'entry-selected': isEntrySelected(item.timeEntryId) }"
                                    outlined
                                  >
                                    <!-- Card Header with Duration and Time Type -->
                                    <v-card-title class="py-2 px-3 d-flex align-center">
                                      <!-- Duration -->
                                      <div class="d-flex align-center mr-3">
                                        <font-awesome-icon
                                          :icon="['fas', 'clock']"
                                          class="mr-2 coolgray--text clock-icon-12px"
                                        />

                                        <span class="body-2 font-weight-bold">
                                          {{ durationFormatter(item.duration) }}
                                        </span>
                                      </div>

                                      <v-spacer />

                                      <v-chip
                                        :color="getTimeTypeColor(item.timeClassificationName)"
                                        :text-color="getTimeTypeColor(item.timeClassificationName)"
                                        small
                                        outlined
                                        class="time-type-chip-header"
                                      >
                                        {{ item.timeClassificationName }}
                                      </v-chip>
                                    </v-card-title>

                                    <v-divider />

                                    <!-- Card Body with Entry Details -->
                                    <v-card-text class="py-2 px-3">
                                      <!-- Person Name (conditional) -->
                                      <div
                                        v-if="shouldShowPersonFilter"
                                        class="entry-info-row mb-2"
                                      >
                                        <font-awesome-icon
                                          :icon="['fas', 'user']"
                                          size="sm"
                                          class="icon-fixed-width primary--text"
                                        />
                                        <div class="entry-info-content">
                                          <span class="body-2 font-weight-bold">
                                            {{ item.personName }}
                                          </span>
                                        </div>
                                      </div>

                                      <!-- Client & Billing Code - Icon-based -->
                                      <div class="entry-info-row mb-2">
                                        <font-awesome-icon
                                          :icon="['fas', 'building']"
                                          size="sm"
                                          class="icon-fixed-width primary--text"
                                        />
                                        <div class="entry-info-content">
                                          <div
                                            class="client-billing-row"
                                            :title="formatClientWithBilling(item.clientName, item.billingCode, item.clientId)"
                                          >
                                            <span class="client-name-text body-2 font-weight-medium">
                                              {{ item.clientName || 'N/A' }}
                                            </span>
                                            <span
                                              v-if="resolveBillingCode(item.billingCode, item.clientId, item.clientName)"
                                              class="billing-code-badge caption"
                                            >
                                              {{ resolveBillingCode(item.billingCode, item.clientId, item.clientName) }}
                                            </span>
                                          </div>
                                        </div>
                                      </div>

                                      <!-- Project - Icon-based (if exists) -->
                                      <div
                                        v-if="item.projectName"
                                        class="entry-info-row mb-2"
                                      >
                                        <font-awesome-icon
                                          :icon="['fas', 'folder']"
                                          size="sm"
                                          class="icon-fixed-width primary--text"
                                        />
                                        <div class="entry-info-content">
                                          <span class="body-2">{{ item.projectName }}</span>
                                        </div>
                                      </div>

                                      <!-- Notes Display (if exists) -->
                                      <div
                                        v-if="item.description"
                                        class="entry-notes-display mt-2"
                                      >
                                        <div class="body-2 work-notes-text">
                                          {{ item.description }}
                                        </div>
                                      </div>
                                    </v-card-text>

                                    <v-divider />

                                    <!-- Card Actions -->
                                    <v-card-actions class="py-2 px-3 justify-space-between">
                                      <!-- Action Icons Group -->
                                      <div
                                        class="d-flex align-center"
                                        role="group"
                                        aria-label="Time entry actions"
                                      >
                                        <v-tooltip top>
                                          <template v-slot:activator="{ on, attrs }">
                                            <v-btn
                                              icon
                                              :disabled="lockedDates.includes(weekdate.dateValue)"
                                              class="mr-1 action-btn action-btn-duplicate"
                                              aria-label="Copy time entry"
                                              v-bind="attrs"
                                              v-on="on"
                                              @click.stop="openTimeEntryFormDuplicate(item, weekdate.dateValue)"
                                            >
                                              <font-awesome-icon
                                                :icon="['fas', 'copy']"
                                                aria-hidden="true"
                                              />
                                            </v-btn>
                                          </template>
                                          <span>Copy entry</span>
                                        </v-tooltip>

                                        <v-tooltip top>
                                          <template v-slot:activator="{ on, attrs }">
                                            <v-btn
                                              icon
                                              :disabled="lockedDates.includes(weekdate.dateValue)"
                                              class="mr-1 action-btn action-btn-edit"
                                              aria-label="Edit time entry"
                                              v-bind="attrs"
                                              v-on="on"
                                              @click.stop="openTimeEntryFormUpdate(item)"
                                            >
                                              <font-awesome-icon
                                                :icon="['fas', 'pencil']"
                                                aria-hidden="true"
                                              />
                                            </v-btn>
                                          </template>
                                          <span>Edit entry</span>
                                        </v-tooltip>

                                        <v-tooltip top>
                                          <template v-slot:activator="{ on, attrs }">
                                            <v-btn
                                              icon
                                              :disabled="lockedDates.includes(weekdate.dateValue)"
                                              class="action-btn action-btn-delete"
                                              aria-label="Delete time entry"
                                              v-bind="attrs"
                                              v-on="on"
                                              @click.stop="openTimeEntryFormDelete(item)"
                                            >
                                              <font-awesome-icon
                                                :icon="['fas', 'trash']"
                                                aria-hidden="true"
                                              />
                                            </v-btn>
                                          </template>
                                          <span>Delete entry</span>
                                        </v-tooltip>
                                      </div>

                                      <!-- Checkbox on Right -->
                                      <Checkbox
                                        v-if="enableBulkOperations"
                                        :input-value="isEntrySelected(item.timeEntryId)"
                                        :disabled="lockedDates.includes(weekdate.dateValue)"
                                        class="mt-0"
                                        aria-label="Select time entry"
                                        @change="toggleEntrySelection(item.timeEntryId, $event)"
                                      />
                                    </v-card-actions>
                                  </v-card>
                                </v-col>
                              </v-row>
                            </v-card-text>

                            <!-- Empty State Message -->
                            <v-card-text
                              v-else
                              class="text-center py-8 empty-state-clickable"
                              @click="openTimeEntryFormCreate(weekdate.dateValue)"
                            >
                              <v-row class="justify-center">
                                <v-col cols="12">
                                  <font-awesome-icon
                                    :icon="['fas', 'calendar-plus']"
                                    size="3x"
                                    class="coolgray--text mb-3"
                                  />
                                </v-col>
                              </v-row>
                              <v-row class="justify-center">
                                <v-col cols="12">
                                  <div class="text-h6 empty-state-text mb-2">
                                    No time entries for {{ weekdate.formatted }}
                                  </div>
                                  <div class="body-2 empty-state-text mb-4">
                                    Click here or the
                                    <font-awesome-icon
                                      :icon="['fas', 'plus']"
                                      size="sm"
                                    />
                                    button above to add your first entry for this day.
                                  </div>
                                </v-col>
                              </v-row>
                            </v-card-text>
                          </v-container>
                        </v-card>
                      </template>
                    </div>
                    <!-- End of calendar view mode -->

                    <!-- COLUMN CALENDAR VIEW - Week days in columns -->
                    <div v-else-if="weekViewMode === 'columns'">
                      <v-row class="time-entries-columns mt-0 mb-1">
                        <v-col
                          v-for="(weekdate, index) in currentWeek.week"
                          :key="index"
                          class="d-flex flex-column pt-0"
                        >
                          <!-- Day Column Header -->
                          <v-card
                            :class="`day-column-header ${setEntryClass(weekdate, index)}`"
                            outlined
                          >
                            <v-card-title class="py-2 px-2 d-flex flex-column align-center">
                              <!-- Day Name and Date -->
                              <div class="d-flex align-center justify-space-between full-width mb-2">
                                <div :class="`caption font-weight-bold text-uppercase day-column-date ${lockedDates.includes(weekdate.date.valueOf()) ? 'locked' : ''}`">
                                  {{ weekdate.date.format('ddd, MMM D') }}
                                </div>

                                <v-btn
                                  icon
                                  x-small
                                  color="primary"
                                  :disabled="lockedDates.includes(weekdate.date.valueOf())"
                                  @click="openTimeEntryFormCreate(weekdate.dateValue)"
                                >
                                  <font-awesome-icon :icon="['fas', 'plus']" />
                                </v-btn>
                              </div>

                              <!-- Total Hours and Checkbox Row -->
                              <div class="d-flex align-center justify-space-between full-width">
                                <v-chip
                                  :color="lockedDates.includes(weekdate.date.valueOf()) ? 'grey lighten-1' : 'primary'"
                                  :text-color="lockedDates.includes(weekdate.date.valueOf()) ? 'grey darken-2' : 'white'"
                                  small
                                  class="font-weight-bold"
                                >
                                  <font-awesome-icon
                                    :icon="['fas', 'clock']"
                                    class="mr-1 clock-icon-12px"
                                  />
                                  {{ durationFormatter(weekdate.total) }}
                                </v-chip>

                                <Checkbox
                                  v-if="enableBulkOperations"
                                  :input-value="areAllDayEntriesSelected(weekdate.entries)"
                                  :indeterminate="areSomeDayEntriesSelected(weekdate.entries) && !areAllDayEntriesSelected(weekdate.entries)"
                                  :disabled="lockedDates.includes(weekdate.date.valueOf()) || weekdate.filteredEntries.length === 0"
                                  class="mt-0"
                                  @change="toggleDaySelection(weekdate.entries, $event)"
                                />
                              </div>
                            </v-card-title>
                          </v-card>

                          <v-divider />

                          <!-- Day Column Entries -->
                          <div class="day-column-entries flex-grow-1">
                            <!-- Grouped View: Show entries grouped by selected property -->
                            <template v-if="hasActiveGrouping">
                              <div
                                v-for="group in getGroupedEntriesForDay(weekdate.filteredEntries)"
                                :key="group.groupName || 'ungrouped'"
                                class="column-group-section"
                              >
                                <!-- Compact Group Header -->
                                <div
                                  v-if="group.isPartOfGroup"
                                  class="column-group-header d-flex align-center pa-1 mt-2 mb-1"
                                  @click="toggleCalendarGroup(weekdate.dateValue, group.groupName)"
                                >
                                  <font-awesome-icon
                                    :icon="['fas', 'chevron-right']"
                                    size="xs"
                                    class="mr-1 coolgray--text transition-rotate flex-shrink-0"
                                    :class="{ 'rotate-90': !isCalendarGroupCollapsed(weekdate.dateValue, group.groupName) }"
                                  />
                                  <span class="caption font-weight-bold text-truncate">{{ group.groupName || 'Unassigned' }}</span>
                                  <v-spacer />
                                  <span class="caption font-weight-bold primary--text flex-shrink-0">
                                    {{ durationFormatter(getGroupTotalMinutes(group.items)) }}
                                  </span>
                                </div>

                                <!-- Group Entries (collapsible) -->
                                <v-expand-transition>
                                  <div v-show="!group.isPartOfGroup || !isCalendarGroupCollapsed(weekdate.dateValue, group.groupName)">
                                    <v-card
                                      v-for="item in group.items"
                                      :key="item.timeEntryId"
                                      class="time-entry-card-compact mb-2"
                                      :class="{ 'entry-selected': isEntrySelected(item.timeEntryId), 'mt-2': !group.isPartOfGroup }"
                                      outlined
                                    >
                                      <!-- Compact Card Header -->
                                      <v-card-title class="py-1 px-2 d-flex align-center card-header-compact">
                                        <!-- Duration - Left side -->
                                        <div class="d-flex align-center mr-2">
                                          <font-awesome-icon
                                            :icon="['fas', 'clock']"
                                            class="mr-1 primary--text clock-icon-12px"
                                          />

                                          <span class="body-2 font-weight-bold">
                                            {{ durationFormatter(item.duration) }}
                                          </span>
                                        </div>

                                        <v-spacer />

                                        <v-chip
                                          :color="getTimeTypeColor(item.timeClassificationName)"
                                          :text-color="getTimeTypeColor(item.timeClassificationName)"
                                          x-small
                                          outlined
                                          class="time-type-chip-compact"
                                        >
                                          {{ item.timeClassificationName }}
                                        </v-chip>
                                      </v-card-title>

                                      <v-divider />

                                      <!-- Compact Card Body -->
                                      <v-card-text class="py-2 px-2">
                                        <!-- Person Name (conditional, compact) -->
                                        <div
                                          v-if="shouldShowPersonFilter"
                                          class="entry-info-row mb-1 align-center"
                                        >
                                          <font-awesome-icon
                                            :icon="['fas', 'user']"
                                            size="xs"
                                            class="icon-fixed-width coolgray--text"
                                          />
                                          <div class="entry-info-content">
                                            <span class="caption font-weight-bold">
                                              {{ item.personName }}
                                            </span>
                                          </div>
                                        </div>

                                        <!-- Client & Billing Code - Icon-based -->
                                        <div class="entry-info-row mb-1 align-center">
                                          <font-awesome-icon
                                            :icon="['fas', 'building']"
                                            size="xs"
                                            class="icon-fixed-width coolgray--text"
                                          />
                                          <div class="entry-info-content">
                                            <div
                                              class="client-billing-row"
                                              :title="formatClientWithBilling(item.clientName, item.billingCode, item.clientId)"
                                            >
                                              <span class="client-name-text caption font-weight-medium">
                                                {{ item.clientName || 'N/A' }}
                                              </span>
                                              <span
                                                v-if="resolveBillingCode(item.billingCode, item.clientId, item.clientName)"
                                                class="billing-code-badge caption"
                                              >
                                                {{ resolveBillingCode(item.billingCode, item.clientId, item.clientName) }}
                                              </span>
                                            </div>
                                          </div>
                                        </div>

                                        <!-- Project - Icon-based (if exists) -->
                                        <div
                                          v-if="item.projectName"
                                          class="entry-info-row mb-1 align-center"
                                        >
                                          <font-awesome-icon
                                            :icon="['fas', 'folder']"
                                            size="xs"
                                            class="icon-fixed-width coolgray--text"
                                          />
                                          <div class="entry-info-content">
                                            <span class="caption">{{ item.projectName }}</span>
                                          </div>
                                        </div>

                                        <!-- Notes - No Label (if exists) -->
                                        <div
                                          v-if="item.description"
                                          class="entry-info-row mb-1"
                                        >
                                          <font-awesome-icon
                                            :icon="['fas', 'comment']"
                                            size="xs"
                                            class="icon-fixed-width coolgray--text"
                                          />
                                          <div class="entry-info-content">
                                            <span class="caption text--secondary entry-notes-truncate">{{ item.description }}</span>
                                          </div>
                                        </div>
                                      </v-card-text>

                                      <!-- Card Actions -->
                                      <v-card-actions class="py-1 px-2 d-flex justify-space-between align-center">
                                        <!-- Action Buttons on Left -->
                                        <div class="action-buttons d-flex">
                                          <!-- Edit Button -->
                                          <v-tooltip top>
                                            <template v-slot:activator="{ on, attrs }">
                                              <v-btn
                                                icon
                                                x-small
                                                color="primary"
                                                :disabled="lockedDates.includes(weekdate.dateValue)"
                                                v-bind="attrs"
                                                v-on="on"
                                                @click="openTimeEntryFormEdit(item)"
                                              >
                                                <font-awesome-icon :icon="['fas', 'pen']" />
                                              </v-btn>
                                            </template>
                                            <span>Edit</span>
                                          </v-tooltip>

                                          <!-- Copy Button -->
                                          <v-tooltip top>
                                            <template v-slot:activator="{ on, attrs }">
                                              <v-btn
                                                icon
                                                x-small
                                                color="primary"
                                                v-bind="attrs"
                                                v-on="on"
                                                @click="openCopyEntryDialog(item)"
                                              >
                                                <font-awesome-icon :icon="['fas', 'copy']" />
                                              </v-btn>
                                            </template>
                                            <span>Copy</span>
                                          </v-tooltip>

                                          <!-- Delete Button -->
                                          <v-tooltip top>
                                            <template v-slot:activator="{ on, attrs }">
                                              <v-btn
                                                icon
                                                x-small
                                                color="error"
                                                :disabled="lockedDates.includes(weekdate.dateValue)"
                                                v-bind="attrs"
                                                v-on="on"
                                                @click="openDeleteTimeEntryConfirmation(item)"
                                              >
                                                <font-awesome-icon :icon="['fas', 'trash']" />
                                              </v-btn>
                                            </template>
                                            <span>Delete</span>
                                          </v-tooltip>
                                        </div>

                                        <!-- Checkbox on Right -->
                                        <Checkbox
                                          v-if="enableBulkOperations"
                                          :input-value="isEntrySelected(item.timeEntryId)"
                                          :disabled="lockedDates.includes(weekdate.dateValue)"
                                          class="mt-0"
                                          aria-label="Select time entry"
                                          @change="toggleEntrySelection(item.timeEntryId, $event)"
                                        />
                                      </v-card-actions>
                                    </v-card>
                                  </div>
                                </v-expand-transition>
                              </div>
                            </template>

                            <!-- Ungrouped View: Flat list of entries -->
                            <template v-else>
                              <v-card
                                v-for="item in weekdate.filteredEntries"
                                :key="item.timeEntryId"
                                class="time-entry-card-compact mb-2 mt-2"
                                :class="{ 'entry-selected': isEntrySelected(item.timeEntryId) }"
                                outlined
                              >
                                <!-- Compact Card Header -->
                                <v-card-title class="py-1 px-2 d-flex align-center card-header-compact">
                                  <!-- Duration - Left side -->
                                  <div class="d-flex align-center mr-2">
                                    <font-awesome-icon
                                      :icon="['fas', 'clock']"
                                      class="mr-1 primary--text clock-icon-12px"
                                    />

                                    <span class="body-2 font-weight-bold">
                                      {{ durationFormatter(item.duration) }}
                                    </span>
                                  </div>

                                  <v-spacer />

                                  <v-chip
                                    :color="getTimeTypeColor(item.timeClassificationName)"
                                    :text-color="getTimeTypeColor(item.timeClassificationName)"
                                    x-small
                                    outlined
                                    class="time-type-chip-compact"
                                  >
                                    {{ item.timeClassificationName }}
                                  </v-chip>
                                </v-card-title>

                                <v-divider />

                                <!-- Compact Card Body -->
                                <v-card-text class="py-2 px-2">
                                  <!-- Person Name (conditional, compact) -->
                                  <div
                                    v-if="shouldShowPersonFilter"
                                    class="entry-info-row mb-1 align-center"
                                  >
                                    <font-awesome-icon
                                      :icon="['fas', 'user']"
                                      size="xs"
                                      class="icon-fixed-width coolgray--text"
                                    />
                                    <div class="entry-info-content">
                                      <span class="caption font-weight-bold">
                                        {{ item.personName }}
                                      </span>
                                    </div>
                                  </div>

                                  <!-- Client & Billing Code - Icon-based -->
                                  <div class="entry-info-row mb-1 align-center">
                                    <font-awesome-icon
                                      :icon="['fas', 'building']"
                                      size="xs"
                                      class="icon-fixed-width coolgray--text"
                                    />
                                    <div class="entry-info-content">
                                      <div
                                        class="client-billing-row"
                                        :title="formatClientWithBilling(item.clientName, item.billingCode, item.clientId)"
                                      >
                                        <span class="client-name-text caption font-weight-medium">
                                          {{ item.clientName || 'N/A' }}
                                        </span>
                                        <span
                                          v-if="resolveBillingCode(item.billingCode, item.clientId, item.clientName)"
                                          class="billing-code-badge caption"
                                        >
                                          {{ resolveBillingCode(item.billingCode, item.clientId, item.clientName) }}
                                        </span>
                                      </div>
                                    </div>
                                  </div>

                                  <!-- Project - Icon-based (if exists) -->
                                  <div
                                    v-if="item.projectName"
                                    class="entry-info-row mb-1 align-center"
                                  >
                                    <font-awesome-icon
                                      :icon="['fas', 'folder']"
                                      size="xs"
                                      class="icon-fixed-width coolgray--text"
                                    />
                                    <div class="entry-info-content">
                                      <span class="caption">{{ item.projectName }}</span>
                                    </div>
                                  </div>

                                  <!-- Notes - No Label (if exists) -->
                                  <div
                                    v-if="item.description"
                                    class="entry-notes-display mt-2"
                                  >
                                    <div class="caption work-notes-text">
                                      {{ item.description }}
                                    </div>
                                  </div>
                                </v-card-text>

                                <v-divider />

                                <!-- Compact Card Actions -->
                                <v-card-actions class="py-2 px-2 justify-space-between">
                                  <!-- Action Icons Group -->
                                  <div
                                    class="d-flex align-center"
                                    role="group"
                                    aria-label="Time entry actions"
                                  >
                                    <v-tooltip top>
                                      <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                          icon
                                          small
                                          :disabled="lockedDates.includes(weekdate.dateValue)"
                                          class="mr-1 action-btn action-btn-duplicate"
                                          aria-label="Copy time entry"
                                          v-bind="attrs"
                                          v-on="on"
                                          @click="openTimeEntryFormDuplicate(item, weekdate.dateValue)"
                                        >
                                          <font-awesome-icon
                                            :icon="['fas', 'copy']"
                                            aria-hidden="true"
                                          />
                                        </v-btn>
                                      </template>
                                      <span>Copy</span>
                                    </v-tooltip>

                                    <v-tooltip top>
                                      <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                          icon
                                          small
                                          :disabled="lockedDates.includes(weekdate.dateValue)"
                                          class="mr-1 action-btn action-btn-edit"
                                          aria-label="Edit time entry"
                                          v-bind="attrs"
                                          v-on="on"
                                          @click="openTimeEntryFormUpdate(item)"
                                        >
                                          <font-awesome-icon
                                            :icon="['fas', 'pencil']"
                                            aria-hidden="true"
                                          />
                                        </v-btn>
                                      </template>
                                      <span>Edit</span>
                                    </v-tooltip>

                                    <v-tooltip top>
                                      <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                          icon
                                          small
                                          :disabled="lockedDates.includes(weekdate.dateValue)"
                                          class="action-btn action-btn-delete"
                                          aria-label="Delete time entry"
                                          v-bind="attrs"
                                          v-on="on"
                                          @click="openTimeEntryFormDelete(item)"
                                        >
                                          <font-awesome-icon
                                            :icon="['fas', 'trash']"
                                            aria-hidden="true"
                                          />
                                        </v-btn>
                                      </template>
                                      <span>Delete</span>
                                    </v-tooltip>
                                  </div>

                                  <!-- Checkbox on Right -->
                                  <Checkbox
                                    v-if="enableBulkOperations"
                                    :input-value="isEntrySelected(item.timeEntryId)"
                                    :disabled="lockedDates.includes(weekdate.dateValue)"
                                    class="mt-0"
                                    aria-label="Select time entry"
                                    @change="toggleEntrySelection(item.timeEntryId, $event)"
                                  />
                                </v-card-actions>
                              </v-card>
                            </template>

                            <!-- Empty State for Day Column -->
                            <v-card
                              v-if="weekdate.filteredEntries.length === 0"
                              class="mt-2 empty-state-clickable"
                              outlined
                              hover
                              @click="openTimeEntryFormCreate(weekdate.dateValue)"
                            >
                              <v-card-text class="text-center py-4">
                                <font-awesome-icon
                                  :icon="['fas', 'calendar-plus']"
                                  size="2x"
                                  class="coolgray--text mb-2"
                                />
                                <div class="caption coolgray--text">
                                  No entries<br>
                                  <span class="caption font-weight-light">Click to add</span>
                                </div>
                              </v-card-text>
                            </v-card>
                          </div>
                        </v-col>
                      </v-row>
                    </div>
                  <!-- End of column calendar view -->
                  </v-col>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>

          <!-- List Views (MTD, Billable, Projects) -->
          <v-row
            v-else
            class="body-2 mt-0"
          >
            <v-col>
              <!-- Card View -->
              <v-row v-if="listViewMode === 'cards'">
                <!-- Loading State: Skeleton Cards -->
                <template v-if="loading">
                  <v-col
                    v-for="n in 8"
                    :key="`skeleton-entry-${n}`"
                    cols="12"
                    sm="6"
                    md="4"
                    lg="3"
                  >
                    <v-skeleton-loader
                      type="card"
                      elevation="0"
                    />
                  </v-col>
                </template>

                <!-- Actual Entry Cards -->
                <template v-else>
                  <v-col
                    v-for="entry in progressivelyRenderedTimes"
                    :key="entry.timeEntryId"
                    cols="12"
                    sm="6"
                    md="4"
                    lg="3"
                  >
                    <v-card
                      class="time-entry-card"
                      :class="{ 'entry-selected': isEntrySelected(entry.timeEntryId) }"
                      outlined
                    >
                      <!-- Card Header with Date and Time Type -->
                      <v-card-title class="py-2 px-3 d-flex align-center">
                        <!-- Date -->
                        <div class="d-flex align-center mr-3">
                          <font-awesome-icon
                            :icon="['fas', 'calendar-day']"
                            class="mr-2 primary--text"
                            size="sm"
                          />

                          <span class="body-2 font-weight-bold">
                            {{ entry.timeEntryDateFormatted || formatDate(entry.timeEntryDate) }}
                          </span>
                        </div>

                        <v-spacer />

                        <v-chip
                          :color="getTimeTypeColor(entry.timeClassificationName)"
                          :text-color="getTimeTypeColor(entry.timeClassificationName)"
                          small
                          outlined
                          class="time-type-chip-header"
                        >
                          {{ entry.timeClassificationName }}
                        </v-chip>
                      </v-card-title>

                      <v-divider />

                      <!-- Card Body with Entry Details -->
                      <v-card-text class="py-2 px-3">
                        <!-- Duration -->
                        <div class="entry-info-row mb-2">
                          <font-awesome-icon
                            :icon="['fas', 'clock']"
                            size="sm"
                            class="icon-fixed-width primary--text"
                          />
                          <div class="entry-info-content">
                            <span class="body-2 font-weight-bold">
                              {{ durationFormatter(entry.duration) }}
                            </span>
                          </div>
                        </div>

                        <!-- Person Name (conditional) -->
                        <div
                          v-if="shouldShowPersonFilter"
                          class="entry-info-row mb-2"
                        >
                          <font-awesome-icon
                            :icon="['fas', 'user']"
                            size="sm"
                            class="icon-fixed-width primary--text"
                          />
                          <div class="entry-info-content">
                            <span class="body-2 font-weight-bold">
                              {{ entry.personName }}
                            </span>
                          </div>
                        </div>

                        <!-- Client & Billing Code - Icon-based -->
                        <div class="entry-info-row mb-2">
                          <font-awesome-icon
                            :icon="['fas', 'building']"
                            size="sm"
                            class="icon-fixed-width primary--text"
                          />
                          <div class="entry-info-content">
                            <div
                              class="client-billing-row"
                              :title="formatClientWithBilling(entry.clientName, entry.billingCode, entry.clientId)"
                            >
                              <span class="client-name-text body-2 font-weight-medium">
                                {{ entry.clientName || 'N/A' }}
                              </span>
                              <span
                                v-if="resolveBillingCode(entry.billingCode, entry.clientId, entry.clientName)"
                                class="billing-code-badge caption"
                              >
                                {{ resolveBillingCode(entry.billingCode, entry.clientId, entry.clientName) }}
                              </span>
                            </div>
                          </div>
                        </div>

                        <!-- Project - Icon-based (if exists) -->
                        <div
                          v-if="entry.projectName"
                          class="entry-info-row mb-2"
                        >
                          <font-awesome-icon
                            :icon="['fas', 'folder']"
                            size="sm"
                            class="icon-fixed-width primary--text"
                          />
                          <div class="entry-info-content">
                            <span class="body-2">{{ entry.projectName }}</span>
                          </div>
                        </div>

                        <!-- Notes Display (if exists) -->
                        <div
                          v-if="entry.description || entry.workNotes"
                          class="entry-notes-display mt-2"
                        >
                          <div class="body-2 work-notes-text">
                            {{ entry.description || entry.workNotes }}
                          </div>
                        </div>
                      </v-card-text>

                      <v-divider />

                      <!-- Card Actions -->
                      <v-card-actions class="py-2 px-3 justify-space-between">
                        <!-- Action Icons Group -->
                        <div
                          class="d-flex align-center"
                          role="group"
                          aria-label="Time entry actions"
                        >
                          <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                icon
                                class="mr-1 action-btn action-btn-duplicate"
                                aria-label="Copy time entry"
                                v-bind="attrs"
                                v-on="on"
                                @click.stop="openTimeEntryFormDuplicate(entry, entry.date)"
                              >
                                <font-awesome-icon
                                  :icon="['fas', 'copy']"
                                  aria-hidden="true"
                                />
                              </v-btn>
                            </template>
                            <span>Copy entry</span>
                          </v-tooltip>

                          <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                icon
                                class="mr-1 action-btn action-btn-edit"
                                aria-label="Edit time entry"
                                v-bind="attrs"
                                v-on="on"
                                @click.stop="openTimeEntryFormUpdate(entry)"
                              >
                                <font-awesome-icon
                                  :icon="['fas', 'pencil']"
                                  aria-hidden="true"
                                />
                              </v-btn>
                            </template>
                            <span>Edit entry</span>
                          </v-tooltip>

                          <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                icon
                                class="action-btn action-btn-delete"
                                aria-label="Delete time entry"
                                v-bind="attrs"
                                v-on="on"
                                @click.stop="openTimeEntryFormDelete(entry)"
                              >
                                <font-awesome-icon
                                  :icon="['fas', 'trash']"
                                  aria-hidden="true"
                                />
                              </v-btn>
                            </template>
                            <span>Delete entry</span>
                          </v-tooltip>
                        </div>

                        <!-- Checkbox on Right -->
                        <Checkbox
                          v-if="enableBulkOperations"
                          :input-value="isEntrySelected(entry.timeEntryId)"
                          class="mt-0"
                          aria-label="Select time entry"
                          @change="toggleEntrySelection(entry.timeEntryId, $event)"
                        />
                      </v-card-actions>
                    </v-card>
                  </v-col>

                  <!-- Progressive Loading Indicator -->
                  <v-col
                    v-if="hasMoreItemsToRender"
                    cols="12"
                    class="text-center py-4"
                  >
                    <v-progress-circular
                      indeterminate
                      color="primary"
                      size="24"
                      class="mr-2"
                    />
                    <span class="body-2 coolgray--text">
                      Loading {{ filteredTimes.length - renderedItemCount }} more entries...
                    </span>
                  </v-col>
                </template>

                <!-- Empty State -->
                <v-col
                  v-if="filteredTimes.length === 0"
                  cols="12"
                >
                  <v-card
                    outlined
                    class="text-center pa-8"
                  >
                    <font-awesome-icon
                      :icon="['fas', 'inbox']"
                      size="3x"
                      class="coolgray--text mb-3"
                    />
                    <div class="text-h6 coolgray--text">
                      No time entries found
                    </div>
                    <div class="body-2 coolgray--text mt-2">
                      {{ currentTabId === 'time-entry' ? 'Try adjusting your filters or date range' : 'Create entries in the Time Entry tab, then return here to review totals.' }}
                    </div>
                    <v-btn
                      v-if="currentTabId !== 'time-entry'"
                      color="primary"
                      outlined
                      class="text-none mt-4"
                      @click="setCurrentTabId('time-entry')"
                    >
                      <font-awesome-icon
                        :icon="['fas', 'calendar-week']"
                        size="sm"
                        class="mr-2"
                      />
                      Go to Time Entry
                    </v-btn>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Table View -->
              <!-- Loading State: Skeleton Table -->
              <v-row v-if="loading && listViewMode === 'table'">
                <v-col>
                  <v-skeleton-loader
                    type="table-heading, table-thead, table-tbody, table-tfoot"
                    elevation="0"
                  />
                </v-col>
              </v-row>

              <!-- Actual Data Table -->
              <data-table
                v-else-if="listViewMode === 'table'"
                ref="table"
                :actions="actions"
                :data-groups="currentDataGroups"
                :initial-items-per-page="10"
                :hide-toolbar-buttons="true"
              />
            </v-col>
          </v-row>

          <time-modal
            :time-shift="timeShift"
            :clients="clients"
            :persons="personsArray"
            :projects="projectsArray"
            :services="servicesArray"
            :time-types="timeTypesArray"
            :time-entries="times"
            :week="week"
          />

          <!-- Bulk Operation Confirmation Dialog -->
          <v-dialog
            v-if="enableBulkOperations"
            v-model="bulkOperationDialog"
            max-width="500"
          >
            <v-card>
              <v-card-title class="headline">
                {{ bulkOperationType === 'delete' ? 'Delete Multiple Entries' : 'Copy Entries' }}
              </v-card-title>

              <v-card-text>
                <div v-if="bulkOperationType === 'delete'">
                  <p class="mb-2">
                    Are you sure you want to delete {{ selectedEntries.length }} time {{ selectedEntries.length === 1 ? 'entry' : 'entries' }}?
                  </p>
                  <v-alert
                    type="warning"
                    text
                    dense
                  >
                    This action cannot be undone.
                  </v-alert>
                </div>

                <div v-else-if="bulkOperationType === 'copy'">
                  <p class="mb-4">
                    Copy {{ selectedEntries.length }} time {{ selectedEntries.length === 1 ? 'entry' : 'entries' }} to a new date:
                  </p>

                  <v-menu
                    ref="copyDateMenu"
                    v-model="copyToDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <div
                        v-bind="attrs"
                        style="cursor: pointer"
                        v-on="on"
                      >
                        <v-text-field
                          v-model="copyToDate"
                          label="Copy To Date"
                          readonly
                          outlined
                        >
                          <template v-slot:prepend-inner>
                            <font-awesome-icon
                              :icon="['fas', 'calendar']"
                              class="mr-2"
                            />
                          </template>
                        </v-text-field>
                      </div>
                    </template>

                    <v-date-picker
                      v-model="copyToDate"
                      no-title
                      scrollable
                      @input="copyToDateMenu = false"
                    />
                  </v-menu>
                </div>
              </v-card-text>

              <v-card-actions>
                <v-spacer />
                <v-btn
                  text
                  @click="bulkOperationDialog = false"
                >
                  Cancel
                </v-btn>
                <v-btn
                  :color="bulkOperationType === 'delete' ? 'error' : 'primary'"
                  @click="confirmBulkOperation"
                >
                  {{ bulkOperationType === 'delete' ? 'Delete' : 'Copy' }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <!-- Keyboard Shortcuts Help Dialog -->
          <v-dialog
            v-model="showKeyboardShortcuts"
            max-width="500"
          >
            <v-card>
              <v-card-title class="headline primary white--text">
                <font-awesome-icon
                  :icon="['fas', 'keyboard']"
                  class="mr-2"
                />
                Keyboard Shortcuts
              </v-card-title>

              <v-card-text class="pt-4">
                <v-list dense>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-bold">
                        <v-chip
                          small
                          outlined
                          class="mr-2"
                        >
                          ←
                        </v-chip>
                        Previous Week
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-bold">
                        <v-chip
                          small
                          outlined
                          class="mr-2"
                        >
                          →
                        </v-chip>
                        Next Week
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-bold">
                        <v-chip
                          small
                          outlined
                          class="mr-2"
                        >
                          T
                        </v-chip>
                        Go to Today / Current Week
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-bold">
                        <v-chip
                          small
                          outlined
                          class="mr-2"
                        >
                          N
                        </v-chip>
                        New Entry (Today)
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-bold">
                        <v-chip
                          small
                          outlined
                          class="mr-2"
                        >
                          ESC
                        </v-chip>
                        Clear Selection
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>

                <v-divider class="my-3" />

                <p class="caption coolgray--text mb-0">
                  Shortcuts work in the Time Entry tab when not typing in an input field.
                </p>
              </v-card-text>

              <v-card-actions>
                <v-spacer />
                <v-btn
                  color="primary"
                  text
                  @click="showKeyboardShortcuts = false"
                >
                  Close
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <!-- Time View Help Dialog -->
          <v-dialog
            v-model="showTimeHelp"
            max-width="600"
            scrollable
            eager
            transition="none"
          >
            <v-card>
              <v-card-title class="headline primary white--text">
                <font-awesome-icon
                  :icon="['fas', 'circle-question']"
                  class="mr-2"
                />
                Time View Guide
              </v-card-title>

              <v-card-text class="pt-4">
                <!-- Quick Start Section -->
                <div class="subtitle-1 font-weight-bold mb-2">
                  <font-awesome-icon
                    :icon="['fas', 'rocket']"
                    class="mr-1 primary--text"
                    size="sm"
                  />
                  Quick Start
                </div>

                <ol class="body-2 pl-4 mb-2">
                  <li class="mb-1">
                    Go to the <strong>Time Entry</strong> tab to create entries for each day.
                  </li>
                  <li class="mb-1">
                    Click <strong>+ Add Entry</strong> or the <v-chip
                      x-small
                      outlined
                      class="mx-1"
                    >
                      N
                    </v-chip> key to open the entry form.
                  </li>
                  <li class="mb-1">
                    Select a client, time type, and duration. Notes are required for Billable and Project types.
                  </li>
                  <li class="mb-1">
                    Use <strong>MTD</strong> for current month progress, <strong>Last Month</strong> for prior month reconciliation, and <strong>Billable</strong> to track client-billable hours.
                  </li>
                  <li>Switch between <strong>Card</strong> and <strong>Table</strong> views using the layout dropdown.</li>
                </ol>

                <v-divider class="my-3" />

                <!-- Layouts Section -->
                <div class="subtitle-1 font-weight-bold mb-2">
                  <font-awesome-icon
                    :icon="['fas', 'table-columns']"
                    class="mr-1 primary--text"
                    size="sm"
                  />
                  Layouts
                </div>

                <v-list
                  dense
                  class="mb-2"
                >
                  <v-list-item>
                    <v-list-item-icon class="mr-3 mt-3">
                      <font-awesome-icon
                        :icon="['fas', 'calendar-week']"
                        size="sm"
                      />
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-bold">
                        Horizontal Layout
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-wrap">
                        Days as side-by-side columns. Best for wide screens and comparing days at a glance.
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item>
                    <v-list-item-icon class="mr-3 mt-3">
                      <font-awesome-icon
                        :icon="['fas', 'columns']"
                        size="sm"
                      />
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-bold">
                        Vertical Layout
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-wrap">
                        Days stacked top to bottom. Best for mobile and focused day-by-day entry.
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item>
                    <v-list-item-icon class="mr-3 mt-3">
                      <font-awesome-icon
                        :icon="['fas', 'th-large']"
                        size="sm"
                      />
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-bold">
                        Card View
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-wrap">
                        Summary cards for MTD, Last Month, Billable, and Projects tabs. Quick visual overview.
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item>
                    <v-list-item-icon class="mr-3 mt-3">
                      <font-awesome-icon
                        :icon="['fas', 'table']"
                        size="sm"
                      />
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-bold">
                        Table View
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-wrap">
                        Sortable data table with grouping. Best for detailed review, filtering, and export.
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>

                <v-divider class="my-3" />

                <!-- Tabs Section -->
                <div class="subtitle-1 font-weight-bold mb-2">
                  <font-awesome-icon
                    :icon="['fas', 'layer-group']"
                    class="mr-1 primary--text"
                    size="sm"
                  />
                  Tabs
                </div>

                <v-list
                  dense
                  class="mb-2"
                >
                  <v-list-item
                    v-for="tab in timeHelpTabs"
                    :key="tab.id"
                  >
                    <v-list-item-icon class="mr-3 mt-3">
                      <font-awesome-icon
                        :icon="tab.icon"
                        size="sm"
                        class="primary--text"
                      />
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-bold">
                        {{ tab.label }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-wrap">
                        {{ tab.description }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>

                <v-divider class="my-3" />

                <!-- Keyboard shortcut link -->
                <p class="caption coolgray--text mb-0">
                  <font-awesome-icon
                    :icon="['fas', 'keyboard']"
                    size="sm"
                    class="mr-1"
                  />
                  Press <v-chip
                    x-small
                    outlined
                    class="mx-1"
                  >
                    ?
                  </v-chip> to view keyboard shortcuts.
                </p>
              </v-card-text>

              <v-card-actions>
                <v-btn
                  text
                  class="text-none"
                  @click="showTimeHelp = false; $router.push('/docs/guides#time')"
                >
                  <font-awesome-icon
                    :icon="['fas', 'book']"
                    size="sm"
                    class="mr-1"
                  />
                  Full Guide
                </v-btn>
                <v-spacer />
                <v-btn
                  color="primary"
                  text
                  @click="showTimeHelp = false"
                >
                  Close
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-col>
      </v-row>

      <!-- Floating Action Button for Add Entry (mobile/tablet only, all tabs) -->
      <v-btn
        v-if="!enableBulkOperations || selectedEntries.length === 0"
        fab
        fixed
        bottom
        right
        color="primary"
        class="mb-4 mr-4 d-md-none"
        @click="openTimeEntryFormCreate(today)"
      >
        <font-awesome-icon
          :icon="['fas', 'plus']"
          size="lg"
        />
      </v-btn>

      <!-- Save Filter Dialog -->
      <save-filter-dialog
        v-model="saveFilterDialog"
        view-name="time"
        :current-filters="currentFilterState"
      />

      <!-- Rename Filter Dialog -->
      <rename-filter-dialog
        v-model="renameFilterDialog"
        :filter="filterToRename"
      />
    </v-row>
  </v-col>
</template>

<script>
/**
 * Time Component - Tab System Documentation
 *
 * This component uses a flexible tab system that supports different date ranges
 * and filtering for each tab view.
 *
 * 📚 For detailed documentation on adding new tabs, see:
 *    - Full Guide: docs/time-component-tabs.md
 *    - Quick Reference: docs/time-tabs-quick-reference.md
 *
 * Key Features:
 * - Smart data fetching (only refetches when date ranges differ)
 * - Client-side filtering for instant tab switching
 * - Flexible date range configuration per tab
 * - URL-based tab and week state management (bookmarkable URLs)
 * - Automatic week navigation data fetching
 * - URL cleanup: timeShift parameters removed when switching away from Time Entry tab
 * - Automatic removal of unrecognized query parameters to maintain clean URLs
 * - Local summary calculations that update reactively when tabs or date ranges change
 */

import { createNamespacedHelpers } from 'vuex'
import _ from 'underscore'

// @note Lodash is loaded with --legacy-peer-deps because it conflicts with upstream dependencies.
// eslint-disable-next-line import/no-extraneous-dependencies
import { debounce } from 'lodash-es'

import dayjs from 'dayjs'
import dates from 'src/utils/dates'
import format from 'src/utils/format'
import groupUtil from 'src/utils/group'
import Checkbox from '@/tools/components/Checkbox.vue'
import themeClasses from '@/mixins/themeClasses'
import durations from '../store/utils/durations'

import columnUtils from '@/tools/store/utils/columns'
import mixin from '../components/mixin'
import api from '@/store/api.js'

const SAFE_TIME_TAB_KEYS = Object.freeze(['time-entry', 'mtd', 'last-month', 'billable', 'projects'])
const SAFE_GROUP_BY_PROPERTIES = Object.freeze(['personName', 'clientName', 'timeClassificationName', 'projectName', 'time_entry_date'])

const getLastMonthDateRangeFormatted = () => {
  const start = dates.getStartOfLastMonth()
  const end = dates.getStartOfCurrentMonth().subtract(1, 'day')

  return [dates.getFormattedDate(start), dates.getFormattedDate(end)]
}

const { mapState: mapLayoutState } = createNamespacedHelpers('layout')
const { mapGetters: mapSavedFiltersGetters } = createNamespacedHelpers('savedFilters')
const {
  mapGetters: mapToolsGetters,
  mapState: mapToolsState,
} = createNamespacedHelpers('tools')

export default {
  name: 'TimePage',
  components: {
    Checkbox,
    ExportXlsx: () => import('@/tools/components/export/ExportXlsx'),
    TimeFilterPanel: () => import('src/internal/components/TimeFilterPanel.vue'),
    ToolsToolbar: () => import('@/tools/components/filter/ToolsToolbar.vue'),
    JumpToGroup: () => import('@/tools/components/filter/JumpToGroup.vue'),
    SearchField: () => import('@/tools/components/filter/SearchField.vue'),
    DateRangePickerButton: () => import('@/tools/components/DateRangePickerButton'),
    RenameFilterDialog: () => import('@/tools/components/filter/RenameFilterDialog'),
    SaveFilterDialog: () => import('@/tools/components/filter/SaveFilterDialog'),
    TimeModal: () => import('src/internal/components/TimeModal'),
  },
  mixins: [mixin, themeClasses],
  /**
   * OPTIMIZATION: Data prefetching
   * Start loading critical data before component is created/mounted
   * This reduces perceived load time by starting API calls as early as possible
   */
  beforeRouteEnter(to, from, next) {
    // Start prefetching data in parallel
    // We can't access 'this' yet, so we'll pass the data to the component via next()
    const prefetchPromises = [
      api.getProjects(),
      api.getServices(),
    ]

    // Continue to component and store prefetched data
    Promise.allSettled(prefetchPromises).then((results) => {
      next((vm) => {
        // Store projects if successfully fetched
        if (results[0].status === 'fulfilled' && results[0].value) {
          const { content } = results[0].value
          if (Array.isArray(content)) {
            vm.$store.commit('tools/projectsResponse', content)
          }
        }

        // Store services if successfully fetched
        if (results[1].status === 'fulfilled' && results[1].value) {
          const { content } = results[1].value
          if (Array.isArray(content)) {
            vm.$store.commit('tools/servicesResponse', content)
          }
        }
      })
    })
  },
  data() {
    return {
      actions: [
        {
          event: 'internal/openTimeEntryFormUpdate',
          name: 'Edit',
        },
        {
          event: 'internal/openTimeEntryFormDelete',
          name: 'Delete',
        },
      ],
      lockedClass: '', // 'offwhite'
      applyingDateFilter: false,
      loading: true, // Start with loading=true for initial data fetch
      isRefreshing: false,
      loadingProjects: false,
      loadingServices: false,
      error: null,

      // Component-level data (not from Vuex)
      clients: [],
      persons: [],
      timeTypes: [],
      loadingClients: false,
      loadingPersons: false,
      loadingTimeTypes: false,

      // Component-level time entries data - separated by tab to prevent cross-contamination
      timesByTab: {
        'time-entry': [],
        mtd: [],
        'last-month': [],
        billable: [],
        projects: [],
      },
      tableSearch: '', // Search filter for the table

      // Drawers
      previewFilters: null, // Filters being previewed (not yet applied)

      // Quick filters (applied filters)
      selectedPersonFilter: [], // Array of person IDs - no longer initialized to current user
      selectedTimeTypeFilter: [],
      selectedClientFilter: [],
      selectedProjectFilter: [],
      filterMenuOpen: false,

      // Grouping state (synced from store via watcher)
      localGroupByProperties: [],

      // Calendar view: track collapsed groups per day (key: dayValue:groupName)
      collapsedCalendarGroups: {},

      // Filter options cache (PERFORMANCE: memoization to avoid recalculating on every render)
      cachedPersonFilterItems: null,
      cachedClientFilterItems: null,
      cachedTimeTypeFilterItems: null,
      cachedProjectFilterItems: null,
      lastTimesHash: null, // Hash to detect when times data actually changes

      // PERFORMANCE: Progressive rendering for large lists
      renderedItemCount: 50, // Start with 50 items, progressively load more
      renderBatchSize: 50, // Items to add per batch
      isProgressivelyRendering: false, // Flag to track rendering state
      progressiveRenderRAF: null, // requestAnimationFrame ID for cleanup

      // Saved filters
      saveFilterDialog: false,
      renameFilterDialog: false,
      filterToRename: null,
      // Separate date ranges for each view type
      dateRanges: (() => {
        try {
          const now = dates.now()
          const fallbackDate = dates.getFormattedDate(now)

          return {
            'time-entry': (() => {
              // Current week range for time entry view (weekly calendar)
              try {
                const week = dates.getWeek(0) // Current week

                if (!week || !Array.isArray(week) || week.length < 7 || !week[0] || !week[6]) {
                  return [fallbackDate, fallbackDate]
                }

                const startOfWeek = week[0] // Monday
                const endOfWeek = week[6] // Sunday

                return [dates.getFormattedDate(startOfWeek), dates.getFormattedDate(endOfWeek)]
              } catch (err) {
                return [fallbackDate, fallbackDate]
              }
            })(),
            mtd: (() => {
              try {
                const mtd = dates.mtdFormatted()

                if (!mtd || !Array.isArray(mtd) || mtd.length < 2) {
                  return [fallbackDate, fallbackDate]
                }

                return mtd
              } catch (err) {
                return [fallbackDate, fallbackDate]
              }
            })(),
            billable: (() => {
              try {
                const mtd = dates.mtdFormatted()

                if (!mtd || !Array.isArray(mtd) || mtd.length < 2) {
                  return [fallbackDate, fallbackDate]
                }

                return mtd
              } catch (err) {
                return [fallbackDate, fallbackDate]
              }
            })(),
            'last-month': (() => {
              try {
                const lastMonth = getLastMonthDateRangeFormatted()

                if (!lastMonth || !Array.isArray(lastMonth) || lastMonth.length < 2) {
                  return [fallbackDate, fallbackDate]
                }

                return lastMonth
              } catch (err) {
                return [fallbackDate, fallbackDate]
              }
            })(),
            projects: (() => {
              try {
                const mtd = dates.mtdFormatted()

                if (!mtd || !Array.isArray(mtd) || mtd.length < 2) {
                  return [fallbackDate, fallbackDate]
                }

                return mtd
              } catch (err) {
                return [fallbackDate, fallbackDate]
              }
            })(),
          }
        } catch (error) {
          // Ultimate fallback if dates utility completely fails
          const now = new Date()
          const formatted = now.toISOString().split('T')[0]

          return {
            'time-entry': [formatted, formatted],
            mtd: [formatted, formatted],
            'last-month': [formatted, formatted],
            billable: [formatted, formatted],
            projects: [formatted, formatted],
          }
        }
      })(),
      activeTab: null, // Will be set based on currentTabId computed property
      activeTabMap: {
        0: 'time-entry',
        1: 'mtd',
        2: 'last-month',
        3: 'billable',
        4: 'projects',
      },

      tabs: [
        {
          id: 'time-entry',
          label: 'Time Entry',
          icon: ['fas', 'calendar-week'],
        },
        {
          id: 'mtd',
          label: 'Month-To-Date',
          icon: ['fas', 'calendar-days'],
        },
        {
          id: 'last-month',
          label: 'Last Month',
          icon: ['fas', 'calendar-minus'],
        },
        {
          id: 'billable',
          label: 'Billable',
          icon: ['fas', 'dollar-sign'],
        },
        {
          id: 'projects',
          label: 'Projects',
          icon: ['fas', 'folder-open'],
        },
      ],

      // Tab counts cache (PERFORMANCE: debounced recalculation)
      tabCounts: {
        'time-entry': 0,
        mtd: 0,
        'last-month': 0,
        billable: 0,
        projects: 0,
      },

      // Per-tab loading states for progressive loading
      tabLoadingStates: {
        'time-entry': false,
        mtd: false,
        'last-month': false,
        billable: false,
        projects: false,
      },

      customTimeShiftText: [null, -1, 0, 1], // Values that do not need "from Current Week" text

      // Feature flags
      enableBulkOperations: false, // DISABLED: Bulk selection feature not ready

      // Bulk operations
      selectedEntries: [], // Array of selected entry IDs
      bulkOperationDialog: false,
      bulkOperationType: null, // 'delete' or 'copy'
      copyToDate: null,
      copyToDateMenu: false,

      // Entry templates
      entryTemplates: [],
      templateDialog: false,
      templateName: '',
      selectedTemplate: null,

      // Keyboard shortcuts help
      showKeyboardShortcuts: false,

      // Time view help dialog
      showTimeHelp: false,

      // Column picker dialog
      columnPickerDialog: false,

      // View mode toggle: 'calendar' (list view) or 'columns' (week columns)
      weekViewMode: 'columns', // Default to column calendar view

      // List view mode toggle for MTD/Billable/Projects: 'cards' or 'table'
      listViewMode: 'cards', // Default to card view for better mobile UX

      // Pending groupByProperty from URL - applied after store initialization
      pendingGroupByProperty: null,
    }
  },
  computed: {
    ...mapLayoutState({
      navCollapsed: (state) => state.navCollapsed,
    }),
    ...mapSavedFiltersGetters(['activeFilter', 'filtersByView']),

    ...mapToolsState({
      projectsResponse: (state) => state.projectsResponse,
      servicesResponse: (state) => state.servicesResponse,
      requiredColumns: (state) => state.requiredColumns,
      sortingByKeys: (state) => state.sortingByKeys || [],
      sortingByDirections: (state) => state.sortingByDirections || [],
    }),

    ...mapToolsGetters([
      'showSummaries', // Still need for summary card display
      'reportKey', // For column picker
      'tableColumns', // For column picker
      'currentColumns', // For sort toolbar
      'currentGroupByProperties', // For grouping
      'currentViewId', // For view initialization check
    ]),

    currentTabId() {
      return this.showTimeEntryList || 'time-entry'
    },

    /**
     * Check if the view is ready to render
     * Prevents template errors by ensuring component initialization is complete
     * Must check BOTH loading flag AND currentViewId to prevent premature rendering
     * Vue 2 will try to render as soon as currentViewId is set (mid-creation), so we
     * guard with loading flag which only becomes false after created() completes
     */
    isViewReady() {
      // View is ready when BOTH conditions are met:
      // 1. loading is complete (created() hook finished)
      // 2. viewId is set (store initialization complete)
      return !this.loading && !!this.currentViewId
    },

    /**
     * Generate a simple hash of times data to detect changes
     * PERFORMANCE: Only recalculates when times array reference changes
     * Updated to work with timesByTab structure
     */
    timesDataHash() {
      const { currentTabId } = this
      const times = this.timesByTab[currentTabId] || []

      if (!Array.isArray(times) || times.length === 0) {
        return `${currentTabId}-empty`
      }
      // Create hash from tab, array length, and first/last entry IDs
      const first = times[0]?.timeEntryId || 0
      const last = times[times.length - 1]?.timeEntryId || 0

      return `${currentTabId}-${times.length}-${first}-${last}`
    },

    showTimeEntryList: {
      get() {
        // Recover safely from invalid or tampered query-string tab values.
        return this.getSafeTabKey(this.$route.query.tab) || 'time-entry'
      },
      set(value) {
        const validValues = ['time-entry', 'mtd', 'last-month', 'billable', 'projects']

        if (!validValues.includes(value)) {
          return false
        }

        // Get the current value to avoid unnecessary route updates.
        const currentQuery = this.$route.query || {}
        const cleanedQuery = this.cleanQueryParameters(currentQuery)

        // When switching tabs, check if the new tab has a stored date range
        // If it does, update URL with those dates; otherwise, clear date params
        const currentRange = this.getDateRangeForView(value)
        if (currentRange) {
          const [start, end] = currentRange
          cleanedQuery.startDate = start
          cleanedQuery.endDate = end
        } else {
          // No stored date range for this tab - clear date params to use defaults
          delete cleanedQuery.startDate
          delete cleanedQuery.endDate
        }

        // For time-entry view, also clear week parameter unless it's stored
        if (value === 'time-entry') {
          delete cleanedQuery.weekOffset
        }

        // Update route's query string.
        cleanedQuery.tab = value

        this.$router.push({ query: cleanedQuery })

        return true
      },
    },
    tabIndex() {
      // Convert report string to tab index for v-tabs
      const tabMap = {
        'time-entry': 0,
        mtd: 1,
        'last-month': 2,
        billable: 3,
        projects: 4,
      }
      return tabMap[this.currentTabId] || 0
    },

    /**
     * Tabs with computed counts for each tab
     * Shows total count per tab (filters only affect active tab's data table)
     * PERFORMANCE: Uses cached counts updated via debounced method
     */
    tabsWithCounts() {
      if (!this.isViewReady) {
        return []
      }

      // Guard: Return empty array if tabs not initialized
      if (!Array.isArray(this.tabs) || !this.tabCounts) {
        return []
      }

      const result = this.tabs.map((tab) => ({
        ...tab,
        count: this.tabCounts[tab.id] || 0,
      }))

      return result
    },

    /**
     * Tab descriptions for the Time View Help dialog
     */
    timeHelpTabs() {
      const descriptions = {
        'time-entry': 'Create and edit your daily time entries. Use this tab to log hours for each day of the week.',
        mtd: 'Review month-to-date totals across all your entries. Use this tab to track progress toward monthly targets.',
        'last-month': 'Review totals for the full previous month. Use this tab for retrospective checks and monthly reconciliation.',
        billable: 'View billable hours for the current period. Use this tab to verify client-billable time before invoicing.',
        projects: 'See time aggregated by project. Use this tab to check project-level effort and budgets.',
      }

      return this.tabs.map((tab) => ({
        ...tab,
        description: descriptions[tab.id],
      }))
    },

    /**
     * @function times
     * @desc Get the current tab's time entries data
     * This computed property ensures each tab works with its own isolated dataset.
     * @return {Array} Array of time entries for the current tab
     */
    times() {
      // Guard: Ensure we have the necessary data structures
      if (!this.currentTabId || !this.timesByTab) {
        return []
      }

      const tabTimes = this.timesByTab[this.currentTabId]
      return Array.isArray(tabTimes) ? tabTimes : []
    },

    /**
     * @function clientsById
     * @desc Generate a map of clients by their ID for the current tab's filter options.
     * @return {Object} Object mapping client IDs to client objects
     */
    clientsById() {
      const clientMap = {}

      // Guard: Don't process until view is ready
      if (!this.isViewReady) {
        return clientMap
      }

      // Guard: Ensure times array exists
      if (!Array.isArray(this.times)) {
        return clientMap
      }

      // If no clients, return empty map.
      if (!Array.isArray(this.clients) || this.clients.length === 0) {
        return clientMap
      }

      // Loop through all clients to build the map.
      this.clients.forEach((client) => {
        // Set the client on the clientMap.
        this.setClientMap(client, clientMap)

        // Safety check: ensure the client object exists in the map
        if (!clientMap[client.clientId]) {
          return
        }

        // Find all time entries for this client in the current tab's times.
        const entries = this.times.filter((t) => t.clientId === client.clientId)
        const hasEntries = Array.isArray(entries) && entries.length > 0
        const totalActionDuration = hasEntries
          ? durations.sumDurations(entries.map((e) => e.actionDuration))
          : 0

        // Update the clientMap with entries info and summaries for this client.
        clientMap[client.clientId].entries = entries
        clientMap[client.clientId].hasEntries = hasEntries
        clientMap[client.clientId].totalActionDuration = totalActionDuration
      })

      return clientMap
    },

    /**
     * @function clientsArray
     * @desc Get an array of clients for easier iteration.
     * @return {Array} Array of client objects
     */
    clientsArray() {
      // Convert clientsById map to an array for easier iteration.
      return Object.values(this.clientsById)
    },

    /**
     * @function clientsWithEntries
     * @desc Get an array of clients who have time entries in the current tab.
     * @return {Array} Array of client objects with time entries
     */
    clientsWithEntries() {
      // Filter clientsArray by the hasEntries flag set in clientsById.
      return this.clientsArray.filter((client) => client && client.hasEntries)
    },

    /**
     * @function personsById
     * @desc Generate a map of persons by their ID for the current tab's filter options.
     * @return {Object} Object mapping person IDs to person objects
     */
    personsById() {
      const personMap = {}

      try {
        // Guard: Don't process until view is ready
        if (!this.isViewReady) {
          return personMap
        }

        // Guard: Ensure times array exists
        if (!Array.isArray(this.times)) {
          this.$log.error(['[personsById] this.times is not an array:', typeof this.times, this.times])

          return personMap
        }

        // If no persons, return empty map.
        if (!Array.isArray(this.persons) || this.persons.length === 0) {
          return personMap
        }

        // Loop through all persons to build the map.
        this.persons.forEach((person) => {
          // Set the person on the personMap.
          this.setPersonMap(person, personMap)

          // Safety check: ensure the person object exists in the map
          if (!personMap[person.personId]) {
            return
          }

          // Find all time entries for this person in the current tab's times.
          const entries = this.times.filter((t) => t.personId === person.personId)
          const hasEntries = Array.isArray(entries) && entries.length > 0
          const totalActionDuration = hasEntries
            ? durations.sumDurations(entries.map((e) => e.actionDuration))
            : 0

          // Update the personMap with entries info and summaries for this person.
          personMap[person.personId].entries = entries
          personMap[person.personId].hasEntries = hasEntries
          personMap[person.personId].totalActionDuration = totalActionDuration
        })

        return personMap
      } catch (error) {
        this.$log.error(['[personsById] ERROR:', error.message, error.stack])

        return personMap
      }
    },

    /**
     * @function personsArray
     * @desc Get an array of persons for easier iteration.
     * @return {Array} Array of person objects
     */
    personsArray() {
      // Convert personsById map to an array for easier iteration.
      return Object.values(this.personsById)
    },

    /**
     * @function personsWithEntries
     * @desc Get an array of persons who have time entries in the current tab.
     * @return {Array} Array of person objects with time entries
     */
    personsWithEntries() {
      // Guard: Don't process until view is ready
      if (!this.isViewReady) {
        return []
      }

      // Filter personsArray by the hasEntries flag set in personsById.
      return this.personsArray.filter((person) => person && person.hasEntries)
    },

    /**
     * @function projectsById
     * @desc Generate a map of projects by their ID for the current tab's filter options.
     * @return {Object} Object mapping project IDs to project objects
     */
    projectsById() {
      const projectMap = {}

      // Guard: Don't process until view is ready
      if (!this.isViewReady) {
        return projectMap
      }

      // Guard: Ensure times array exists
      if (!Array.isArray(this.times)) {
        return projectMap
      }

      // If no projects, return empty map.
      if (!Array.isArray(this.projectsResponse) || this.projectsResponse.length === 0) {
        return projectMap
      }

      // Loop through all projects to build the map.
      this.projectsResponse.forEach((project) => {
        // Set the project on the projectMap.
        this.setProjectMap(project, projectMap)

        // Safety check: ensure the project object exists in the map
        if (!projectMap[project.projectId]) {
          return
        }

        // Find all time entries for this project in the current tab's times.
        const entries = this.times.filter((t) => t.projectId === project.projectId)
        const hasEntries = Array.isArray(entries) && entries.length > 0
        const totalActionDuration = hasEntries
          ? durations.sumDurations(entries.map((e) => e.actionDuration))
          : 0

        // Update the projectMap with entries info and summaries for this project.
        projectMap[project.projectId].entries = entries
        projectMap[project.projectId].hasEntries = hasEntries
        projectMap[project.projectId].totalActionDuration = totalActionDuration
      })

      return projectMap
    },

    /**
     * @function projectsArray
     * @desc Get an array of projects for easier iteration.
     * @return {Array} Array of project objects
     */
    projectsArray() {
      // Convert projectsById map to an array for easier iteration.
      return Object.values(this.projectsById)
    },

    /**
     * @function projectsWithEntries
     * @desc Get an array of projects who have time entries in the current tab.
     * @return {Array} Array of project objects with time entries
     */
    projectsWithEntries() {
      // Filter projectsArray by the hasEntries flag set in projectsById.
      return this.projectsArray.filter((project) => project && project.hasEntries)
    },

    servicesById() {
      const serviceMap = {}

      // Guard: Don't process until view is ready
      if (!this.isViewReady) {
        return serviceMap
      }

      // Guard: Ensure times array exists
      if (!Array.isArray(this.times)) {
        return serviceMap
      }

      // If no services, return empty map.
      if (!Array.isArray(this.servicesResponse) || this.servicesResponse.length === 0) {
        return serviceMap
      }

      // Loop through all services to build the map.
      this.servicesResponse.forEach((service) => {
        // Set the service on the serviceMap.
        this.setServiceMap(service, serviceMap)

        // Safety check: ensure the service object exists in the map
        if (!serviceMap[service.serviceId]) {
          return
        }

        // Find all time entries for this service in the current tab's times.
        const entries = this.times.filter((t) => t.serviceId === service.serviceId)
        const hasEntries = Array.isArray(entries) && entries.length > 0
        const totalActionDuration = hasEntries
          ? durations.sumDurations(entries.map((e) => e.actionDuration))
          : 0

        // Update the serviceMap with entries info and summaries for this service.
        serviceMap[service.serviceId].entries = entries
        serviceMap[service.serviceId].hasEntries = hasEntries
        serviceMap[service.serviceId].totalActionDuration = totalActionDuration
      })

      return serviceMap
    },

    servicesArray() {
      // Convert servicesById map to an array for easier iteration.
      return Object.values(this.servicesById)
    },

    servicesWithEntries() {
      // Filter servicesArray by the hasEntries flag set in servicesById.
      return this.servicesArray.filter((service) => service && service.hasEntries)
    },

    /**
     * @function timeTypesById
     * @desc Generate a map of time types by their ID for the current tab's filter options.
     * @return {Object} Object mapping time type IDs to time type objects
     */
    timeTypesById() {
      const timeTypeMap = {}

      // Guard: Don't process until view is ready
      if (!this.isViewReady) {
        return timeTypeMap
      }

      // Guard: Ensure times array exists
      if (!Array.isArray(this.times)) {
        return timeTypeMap
      }

      // If no time types, return empty map.
      if (!Array.isArray(this.timeTypes) || this.timeTypes.length === 0) {
        return timeTypeMap
      }

      // Loop through all time types to build the map.
      this.timeTypes.forEach((timeType) => {
        // Set the time type on the timeTypeMap.
        this.setTimeTypeMap(timeType, timeTypeMap)

        // Get ID using both naming conventions (API returns timeClassificationId)
        const timeTypeId = timeType.timeClassificationId || timeType.timeTypeId

        // Safety check: ensure the time type object exists in the map
        if (!timeTypeMap[timeTypeId]) {
          return
        }

        // Find all time entries for this time type in the current tab's times.
        // Time entries use timeTypeId field
        const entries = this.times.filter((t) => t.timeTypeId === timeTypeId)
        const hasEntries = Array.isArray(entries) && entries.length > 0
        const totalActionDuration = hasEntries
          ? durations.sumDurations(entries.map((e) => e.actionDuration))
          : 0

        // Update the timeTypeMap with entries info and summaries for this time type.
        timeTypeMap[timeTypeId].entries = entries
        timeTypeMap[timeTypeId].hasEntries = hasEntries
        timeTypeMap[timeTypeId].totalActionDuration = totalActionDuration
      })

      return timeTypeMap
    },

    /**
     * @function timeTypesArray
     * @desc Get an array of time types for easier iteration.
     * @return {Array} Array of time type objects
     */
    timeTypesArray() {
      // Convert timeTypesById map to an array for easier iteration.
      return Object.values(this.timeTypesById)
    },

    /**
     * @function timeTypesWithEntries
     * @desc Get an array of time types who have time entries in the current tab.
     * @return {Array} Array of time type objects with time entries
     */
    timeTypesWithEntries() {
      // Filter timeTypesArray by the hasEntries flag set in timeTypesById.
      return this.timeTypesArray.filter((timeType) => timeType && timeType.hasEntries)
    },

    // Current required columns for the column picker
    currentRequiredColumns() {
      // Guard: Return empty array if requiredColumns or reportKey not available
      if (!this.requiredColumns || !this.reportKey) {
        return []
      }
      return this.requiredColumns[this.reportKey] || []
    },

    /**
     * Default date ranges for each view - easily configurable
     * Change these values to adjust default date ranges across all views
     */
    defaultDateRanges() {
      try {
        const now = dates.now()
        const fallbackDate = dates.getFormattedDate(now)

        return {
          'time-entry': (() => {
            // Default: Current week (Monday to Sunday)
            try {
              const week = dates.getWeek(0)
              if (!week || !Array.isArray(week) || week.length < 7 || !week[0] || !week[6]) {
                return [fallbackDate, fallbackDate]
              }
              return [dates.getFormattedDate(week[0]), dates.getFormattedDate(week[6])]
            } catch (err) {
              return [fallbackDate, fallbackDate]
            }
          })(),
          mtd: (() => {
            // Default: Month-to-date
            try {
              const mtd = dates.mtdFormatted()
              return mtd && Array.isArray(mtd) && mtd.length === 2 ? mtd : [fallbackDate, fallbackDate]
            } catch (err) {
              return [fallbackDate, fallbackDate]
            }
          })(),
          billable: (() => {
            // Default: Month-to-date
            try {
              const mtd = dates.mtdFormatted()
              return mtd && Array.isArray(mtd) && mtd.length === 2 ? mtd : [fallbackDate, fallbackDate]
            } catch (err) {
              return [fallbackDate, fallbackDate]
            }
          })(),
          'last-month': (() => {
            // Default: Full previous month
            try {
              const lastMonth = getLastMonthDateRangeFormatted()
              return lastMonth && Array.isArray(lastMonth) && lastMonth.length === 2
                ? lastMonth
                : [fallbackDate, fallbackDate]
            } catch (err) {
              return [fallbackDate, fallbackDate]
            }
          })(),
          projects: (() => {
            // Default: Month-to-date
            try {
              const mtd = dates.mtdFormatted()
              return mtd && Array.isArray(mtd) && mtd.length === 2 ? mtd : [fallbackDate, fallbackDate]
            } catch (err) {
              return [fallbackDate, fallbackDate]
            }
          })(),
        }
      } catch (error) {
        // Ultimate fallback
        const now = new Date()
        const formatted = now.toISOString().split('T')[0]
        return {
          'time-entry': [formatted, formatted],
          mtd: [formatted, formatted],
          'last-month': [formatted, formatted],
          billable: [formatted, formatted],
          projects: [formatted, formatted],
        }
      }
    },

    currentDateRange() {
      try {
        // PRIORITY 1: Check URL parameters for custom date range (works for all views)
        const { startDate, endDate } = this.$route.query

        // If both dates provided, use them
        if (startDate && endDate) {
          return [startDate, endDate]
        }

        // If only startDate provided, auto-complete the week (Monday to Sunday)
        if (startDate && !endDate) {
          const start = dates.get(startDate)
          // Find the Monday of this week
          const dayOfWeek = start.day() // 0=Sunday, 1=Monday, ..., 6=Saturday
          const daysFromMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek // Adjust for Sunday being 0
          const monday = start.add(daysFromMonday, 'day')
          const sunday = monday.add(6, 'days')
          return [dates.getFormattedDate(monday), dates.getFormattedDate(sunday)]
        }

        // PRIORITY 2: For time-entry view, check timeShift navigation
        if (this.currentTabId === 'time-entry') {
          // If there's a stored custom date range for time-entry, use it
          if (this.dateRanges && this.dateRanges['time-entry'] && this.hasCustomTimeEntryRange) {
            return this.dateRanges['time-entry']
          }

          // Otherwise, use the current timeShift to calculate the week range
          const timeShiftValue = this.timeShift !== null ? this.timeShift : 0
          const week = dates.getWeek(timeShiftValue)
          if (!week || !week[0] || !week[6]) {
            // Fallback if getWeek fails
            return this.defaultDateRanges['time-entry'] || [dates.getFormattedDate(dates.now()), dates.getFormattedDate(dates.now())]
          }
          const startOfWeek = week[0] // Monday
          const endOfWeek = week[6] // Sunday
          return [dates.getFormattedDate(startOfWeek), dates.getFormattedDate(endOfWeek)]
        }

        // PRIORITY 3: For other views, check stored date range
        if (this.dateRanges && this.dateRanges[this.currentTabId]) {
          const dateRange = this.dateRanges[this.currentTabId]
          if (dateRange && Array.isArray(dateRange) && dateRange.length === 2) {
            return dateRange
          }
        }

        // PRIORITY 4: Fall back to default date range for this view
        return this.defaultDateRanges[this.currentTabId] || this.defaultDateRanges.mtd || [dates.getFormattedDate(dates.now()), dates.getFormattedDate(dates.now())]
      } catch (error) {
        // Ultimate fallback - return current date
        const now = dates.now()
        return [dates.getFormattedDate(now), dates.getFormattedDate(now)]
      }
    },

    hasCustomTimeEntryRange() {
      try {
        // Get custom date range from URL parameters.
        const { startDate, endDate } = this.$route.query

        // If both startDate and endDate are present in URL, return true.
        if (startDate && endDate) {
          return true
        }

        // If no dateRanges or no stored range, return false.
        if (!this.dateRanges || !this.dateRanges['time-entry']) {
          return false
        }

        // Get the stored date range for the time-entry view.
        const storedRange = this.dateRanges['time-entry']

        // Ensure storedRange is valid
        if (!Array.isArray(storedRange) || storedRange.length < 2) {
          return false
        }

        // Get timeShift. Default is current week's timeShift (0).
        const timeShiftValue = this.timeShift !== null ? this.timeShift : 0

        // Calculate the timeShift week's date range (Monday to Sunday).
        const timeShiftWeek = dates.getWeek(timeShiftValue)

        // Ensure timeShiftWeek is valid
        if (!timeShiftWeek || !timeShiftWeek[0] || !timeShiftWeek[6]) {
          return false
        }

        // Format dates to 'YYYY-MM-DD' for comparison.
        const timeShiftWeekStart = dates.getFormattedDate(timeShiftWeek[0])
        const timeShiftWeekEnd = dates.getFormattedDate(timeShiftWeek[6])

        // Compare stored range with timeShift's week range.
        // If startDate differs from timeShiftWeekStart, return true.
        // If endDate differs from timeShiftWeekEnd, return true.
        return !(storedRange[0] === timeShiftWeekStart && storedRange[1] === timeShiftWeekEnd)
      } catch (error) {
        // Fallback - assume no custom range
        return false
      }
    },

    today() {
      return dates.now()
    },
    timeShift: {
      get() {
        // PRIORITY 1: Check if we have custom date range parameters first
        const { startDate, endDate, weekNumber, year, weekOffset } = this.$route.query

        // If tab is 'time-entry' and custom date range provided, return null
        if (this.currentTabId === 'time-entry' && startDate && endDate) {
          // Return null to indicate this is a custom range, not a standard week shift
          return null
        }

        // PRIORITY 2: Check for absolute week number + year (bookmarkable)
        if (weekNumber !== undefined && year !== undefined) {
          const parsedWeekNumber = parseInt(weekNumber, 10)
          const parsedYear = parseInt(year, 10)

          if (!Number.isNaN(parsedWeekNumber) && !Number.isNaN(parsedYear)) {
            // Calculate the offset from current week
            const now = dates.now()
            const currentWeekNumber = now.week()
            const currentYear = now.year()

            // Handle year boundaries
            let weeksOffset = 0
            if (parsedYear === currentYear) {
              weeksOffset = parsedWeekNumber - currentWeekNumber
            } else {
              // Approximate: 52 weeks per year (simplified)
              const yearDiff = parsedYear - currentYear
              weeksOffset = (yearDiff * 52) + (parsedWeekNumber - currentWeekNumber)
            }

            return weeksOffset
          }
        }

        // PRIORITY 3: Check for relative week offset (backward compatible)
        if (weekOffset !== undefined) {
          const parsedShift = parseInt(weekOffset, 10)
          // Validate the parsed value is a valid number
          if (!Number.isNaN(parsedShift)) {
            return parsedShift
          }
        }

        // PRIORITY 4: Fallback to store value
        return this.$store.state.internal?.timeShift || 0
      },
      set(value) {
        // Ensure the value is a valid integer
        const timeShiftValue = Math.floor(value)

        // Update the store (check if module is registered)
        if (this.$store.hasModule && this.$store.hasModule('internal')) {
          this.$store.commit('internal/timeShift', timeShiftValue)
        }

        // Clean and update the URL query string
        const cleanedQuery = this.cleanQueryParameters(this.$route.query)

        // Clear any custom date range parameters when using timeShift
        delete cleanedQuery.startDate
        delete cleanedQuery.endDate

        // Calculate the absolute week number and year for the target week
        const targetWeek = dates.getWeek(timeShiftValue)
        if (targetWeek && targetWeek[0]) {
          const weekNumber = targetWeek[0].week()
          const year = targetWeek[0].year()

          // Use absolute week number/year for bookmarkable URLs
          cleanedQuery.weekNumber = weekNumber.toString()
          cleanedQuery.year = year.toString()

          // Remove old relative week parameter
          delete cleanedQuery.weekOffset
        } else if (timeShiftValue === 0) {
          // Fallback: current week, remove all week parameters
          delete cleanedQuery.weekOffset
          delete cleanedQuery.weekNumber
          delete cleanedQuery.year
        } else {
          // Fallback: use relative offset
          cleanedQuery.weekOffset = timeShiftValue.toString()
          delete cleanedQuery.weekNumber
          delete cleanedQuery.year
        }

        // Only update route if query actually changed
        const currentWeekNumber = this.$route.query.weekNumber
        const currentYear = this.$route.query.year
        const currentWeekOffset = this.$route.query.weekOffset
        const hadCustomRange = this.$route.query.startDate || this.$route.query.endDate

        const weekChanged = currentWeekNumber !== cleanedQuery.weekNumber
          || currentYear !== cleanedQuery.year
          || currentWeekOffset !== cleanedQuery.weekOffset

        if (weekChanged || hadCustomRange) {
          this.$router.push({ query: cleanedQuery })
        }

        return true
      },
    },
    timeEntryHeaders() {
      return [
        {
          text: '',
          value: 'select',
          class: 'select-column',
          sortable: false,
          width: '50px',
        },
        {
          text: 'Billing Code',
          value: 'billingCode',
          class: 'org-code font-weight-medium',
          sortable: true,
        },
        {
          text: 'Organization',
          value: 'clientName',
          class: 'org-name font-weight-medium',
          sortable: true,
        },
        {
          text: 'Project',
          value: 'projectName',
          class: 'project-name font-weight-medium',
          sortable: true,
        },
        {
          text: 'Time Type',
          value: 'timeClassificationName',
          class: 'time-type font-weight-medium',
          sortable: true,
        },
        {
          text: 'HH:MM',
          value: 'duration',
          class: 'entry-duration font-weight-medium',
          sortable: true,
        },
        {
          text: 'Notes',
          value: 'description',
          class: 'work-notes font-weight-medium',
          sortable: true,
        },
        {
          text: 'Actions',
          value: 'actions',
          class: 'entry-actions font-weight-medium',
          sortable: false,
        },
      ]
    },
    // Quick filter options
    personFilterOptions() {
      return this.personsArray.toSorted((a, b) => a.text.localeCompare(b.text))
    },
    timeTypeFilterOptions() {
      return this.timeTypesArray.toSorted((a, b) => a.text.localeCompare(b.text))
    },
    clientFilterOptions() {
      return this.clientsArray.toSorted((a, b) => a.text.localeCompare(b.text))
    },
    projectFilterOptions() {
      return this.projectsArray.toSorted((a, b) => a.text.localeCompare(b.text))
    },
    shouldShowPersonFilter() {
      // Only show person filter if there are multiple persons with accessible time entries
      return this.personsWithEntries?.length > 1
    },
    hasActiveFilters() {
      return !!(this.selectedPersonFilter?.length > 0 || this.selectedTimeTypeFilter?.length > 0 || this.selectedClientFilter?.length > 0 || this.selectedProjectFilter?.length > 0)
    },
    activeFilterCount() {
      let count = 0
      if (this.selectedPersonFilter?.length > 0) count += 1
      if (this.selectedTimeTypeFilter?.length > 0) count += 1
      if (this.selectedClientFilter?.length > 0) count += 1
      if (this.selectedProjectFilter?.length > 0) count += 1

      return count
    },
    /**
     * Current filter state for saving
     * Returns an object with all active filter values
     */
    currentFilterState() {
      return {
        selectedPersonFilter: [...this.selectedPersonFilter],
        selectedTimeTypeFilter: this.selectedTimeTypeFilter,
        selectedClientFilter: this.selectedClientFilter,
        selectedProjectFilter: this.selectedProjectFilter,
        tableSearch: this.tableSearch,
        dateRange: this.currentDateRange,
        // Include tab state for context
        activeTab: this.currentTabId,
      }
    },

    /**
     * Check if current filter state is different from active saved filter
     * Used to show/hide "Save Filter" button
     */
    hasUnsavedFilterChanges() {
      // If no filters are active, don't show save button
      if (!this.hasActiveFilters) return false

      // If no active saved filter, show save button (filters are new/unsaved)
      if (!this.activeFilter) return true

      // Compare current state with active saved filter
      const saved = this.activeFilter.filters || {}
      const current = this.currentFilterState

      // Compare person filter (array)
      const personMatch = JSON.stringify(saved.selectedPersonFilter || []) === JSON.stringify(current.selectedPersonFilter || [])

      // Compare other filters (now all arrays)
      const timeTypeMatch = JSON.stringify(saved.selectedTimeTypeFilter || []) === JSON.stringify(current.selectedTimeTypeFilter || [])
      const clientMatch = JSON.stringify(saved.selectedClientFilter || []) === JSON.stringify(current.selectedClientFilter || [])
      const projectMatch = JSON.stringify(saved.selectedProjectFilter || []) === JSON.stringify(current.selectedProjectFilter || [])
      const searchMatch = saved.tableSearch === current.tableSearch

      // If any filter doesn't match, there are unsaved changes
      return !personMatch || !timeTypeMatch || !clientMatch || !projectMatch || !searchMatch
    },

    week() {
      const week = dates.getWeek(this.timeShift)
      return week || []
    },
    currentWeekNumber() {
      const { week } = this
      if (!week || !week[0]) return ''
      return week[0].week()
    },
    currentYear() {
      const { week } = this
      if (!week || !week[0]) return ''
      return week[0].year()
    },
    timeShiftText() {
      const { timeShift } = this
      const { weekNumber, year } = this.$route.query

      // Handle custom date range case
      if (timeShift === null) {
        return 'Custom Range'
      }

      // If we have absolute week number/year in URL, show it
      if (weekNumber && year) {
        const now = dates.now()
        const currentWk = now.week()
        const currentYr = now.year()

        const wkNum = parseInt(weekNumber, 10)
        const yr = parseInt(year, 10)

        if (wkNum === currentWk && yr === currentYr) {
          return 'Current Week'
        }

        return `Week ${wkNum}, ${yr}`
      }

      // Relative week text
      if (timeShift === 0) {
        return 'Current Week'
      }
      if (timeShift === 1) {
        return 'Next Week'
      }
      if (timeShift === -1) {
        return 'Last Week'
      }
      if (timeShift > 1) {
        return `+${timeShift}`
      }
      return `${timeShift}` // Already has negative sign for negative values
    },
    getWeekDateRange() {
      const { week } = this
      const [startDate,,,,,, endDate] = week
      const { $M: startMonth } = startDate || {}
      const { $M: endMonth } = endDate || {}
      const sameMonth = startMonth === endMonth

      return sameMonth
        ? this.sameMonthText(startDate, endDate)
        : this.crossMonthText(startDate, endDate)
    },
    lockedDates() {
      const { week } = this
      // Get getters dynamically because store is imported dynamically.
      const maxDate = this.$store.getters['internal/maxCalendarDate']
      const minDate = this.$store.getters['internal/minCalendarDate']
      const seed = []

      return week.reduce((locked, weekdate) => {
        const valid = dates.areDatesInRange(weekdate, minDate, maxDate)

        if (!valid) locked.push(weekdate.valueOf())

        return locked
      }, seed)
    },
    currentWeek() {
      // Guard: Don't process until view is ready
      if (!this.isViewReady) {
        return { week: [], total: 0 }
      }

      const { week } = this

      if (!week || !Array.isArray(week) || week.length === 0) {
        return { week: [], total: 0 }
      }

      // Prevent pollution during nav.
      const seedWeek = week.map((date) => ({ date, entries: [], filteredEntries: [], formatted: date.format('dddd, MMMM DD'), total: 0 }))
      const currentWeek = { week: seedWeek, total: 0 }

      currentWeek.week = seedWeek.map((day) => {
        const results = this.getEntriesForDay(day.date)
        const totalMinutes = this.getTotal(results)

        currentWeek.total += totalMinutes

        day.dateValue = day.date.valueOf() // Example: 1759291200000
        day.entries = results
        day.filteredEntries = this.getFilteredEntries(results) // 🚀 Pre-compute filtered entries once
        day.total += totalMinutes

        return day
      })

      return currentWeek
    },

    // Get filtered times based on the active tab
    // 📝 To add filtering for new tabs, see docs/time-tabs-quick-reference.md
    filteredTimes() {
      // Guard: Don't process until view is ready
      if (!this.isViewReady) {
        return []
      }

      if (!Array.isArray(this.times)) {
        return []
      }

      let filtered = []

      switch (this.currentTabId) {
        case 'billable':
          // Filter for billable entries only
          filtered = this.times.filter((entry) => entry.timeClassificationName === 'BILLABLE')
          break

        case 'projects':
          // Filter for entries that have a project assigned (projectId exists and is not -1)
          filtered = this.times.filter((entry) => (entry?.projectId && entry.projectId !== -1) || entry.timeClassificationName === 'PROJECT')
          break

        default:
          filtered = this.times
      }

      // Apply search and filter controls (person, time type, client, project, search text)
      return this.getFilteredEntries(filtered)
    },

    /**
     * PERFORMANCE: Progressively rendered subset of filteredTimes
     * Returns only the first N items based on renderedItemCount
     * This allows the UI to remain responsive while rendering large lists
     */
    progressivelyRenderedTimes() {
      const allItems = this.filteredTimes
      if (this.renderedItemCount >= allItems.length) {
        return allItems
      }
      return allItems.slice(0, this.renderedItemCount)
    },

    /**
     * Check if there are more items to render progressively
     */
    hasMoreItemsToRender() {
      return this.renderedItemCount < this.filteredTimes.length
    },

    // Data groups for the DataTable based on the active tab
    currentDataGroups() {
      // Guard: Don't process until view is ready
      if (!this.isViewReady) {
        return []
      }

      const filteredItems = this.filteredTimes
      const groupByProperties = this.currentGroupByProperties || []

      // Use standardized groupUtil - handles empty grouping, sorting, multi-property grouping
      return groupUtil.byProperty(filteredItems, groupByProperties)
    },

    /**
     * Whether grouping is currently active for calendar view
     */
    hasActiveGrouping() {
      return this.currentGroupByProperties && this.currentGroupByProperties.length > 0
    },

    // Transform currentDataGroups to format expected by ToolsDrawer Jump tab
    filteredGroups() {
      const groups = this.currentDataGroups || []
      // Filter out ungrouped "all items" group
      return groups
        .filter((group) => group.isPartOfGroup)
        .map((group) => ({
          groupName: group.groupName,
          labels: [group.groupName],
          items: group.items,
        }))
    },

    // Local summary calculations based on filtered time entries
    localTimeSummaries() {
      const entries = this.filteredTimes || []

      // Initialize counters
      let billableTotal = 0
      let nonBillableTotal = 0
      let projectTotal = 0
      let adxTotal = 0
      let adxPgTotal = 0
      let grandTotal = 0

      // Calculate totals based on filtered entries
      entries.forEach((entry) => {
        const duration = entry.duration || 0
        grandTotal += duration

        // Categorize by time classification
        const timeClass = entry.timeClassificationName || ''

        if (timeClass === 'BILLABLE') {
          billableTotal += duration
        } else if (timeClass === 'NON-BILLABLE') {
          nonBillableTotal += duration
        } else if (timeClass === 'PROJECT') {
          projectTotal += duration
        } else if (timeClass === 'ADX') {
          adxTotal += duration
        } else if (timeClass === 'ADX-PG') {
          adxPgTotal += duration
        }
      })

      // Return summary objects matching the expected format
      return {
        BILLABLE: {
          id: 'BILLABLE',
          title: 'Billable',
          subtitle: this.getSummarySubtitle(),
          total: billableTotal,
          format: 'hhmm',
          styles: {
            background: 'primary',
            text: 'white',
            value: 'white',
          },
        },
        'NON-BILLABLE': {
          id: 'NON-BILLABLE',
          title: 'Non-Billable',
          subtitle: this.getSummarySubtitle(),
          total: nonBillableTotal,
          format: 'hhmm',
          styles: {
            background: 'primary',
            text: 'white',
            value: 'white',
          },
        },
        PROJECT: {
          id: 'PROJECT',
          title: 'Project',
          subtitle: this.getSummarySubtitle(),
          total: projectTotal,
          format: 'hhmm',
          styles: {
            background: 'primary',
            text: 'white',
            value: 'white',
          },
        },
        ADX: {
          id: 'ADX',
          title: 'AdX',
          subtitle: this.getSummarySubtitle(),
          total: adxTotal,
          format: 'hhmm',
          styles: {
            background: 'primary',
            text: 'white',
            value: 'white',
          },
        },
        'ADX-PG': {
          id: 'ADX-PG',
          title: 'AdX-PG',
          subtitle: this.getSummarySubtitle(),
          total: adxPgTotal,
          format: 'hhmm',
          styles: {
            background: 'primary',
            text: 'white',
            value: 'white',
          },
        },
        TOTAL: {
          id: 'TOTAL',
          title: 'Total',
          subtitle: this.getSummarySubtitle(),
          total: grandTotal,
          format: 'hhmm',
          styles: {
            background: 'white',
            text: 'darkestblue',
            value: 'darkestblue',
          },
        },
      }
    },

    // Progress segments for stacked bar visualization
    progressSegments() {
      const summaries = this.localTimeSummaries
      const total = summaries.TOTAL.total || 1 // Avoid division by zero

      // Define segments with colors - only include non-zero values
      const segments = [
        {
          id: 'BILLABLE',
          label: 'Billable',
          value: summaries.BILLABLE.total,
          colorClass: 'segment-billable',
          percentage: (summaries.BILLABLE.total / total) * 100,
        },
        {
          id: 'PROJECT',
          label: 'Project',
          value: summaries.PROJECT.total,
          colorClass: 'segment-project',
          percentage: (summaries.PROJECT.total / total) * 100,
        },
        {
          id: 'NON-BILLABLE',
          label: 'Non-Billable',
          value: summaries['NON-BILLABLE'].total,
          colorClass: 'segment-nonbillable',
          percentage: (summaries['NON-BILLABLE'].total / total) * 100,
        },
        {
          id: 'ADX',
          label: 'AdX',
          value: summaries.ADX.total,
          colorClass: 'segment-adx',
          percentage: (summaries.ADX.total / total) * 100,
        },
        {
          id: 'ADX-PG',
          label: 'AdX-PG',
          value: summaries['ADX-PG'].total,
          colorClass: 'segment-adxpg',
          percentage: (summaries['ADX-PG'].total / total) * 100,
        },
      ]

      // Filter out segments with zero values
      return segments.filter((segment) => segment.value > 0)
    },

    // Filter Drawer Support
    appliedFilters() {
      // Guard against undefined currentDateRange
      const dateRange = Array.isArray(this.currentDateRange) ? this.currentDateRange : []

      return {
        startDate: dateRange[0] || null,
        endDate: dateRange[1] || null,
        selectedPersonFilter: this.selectedPersonFilter,
        selectedClientFilter: this.selectedClientFilter,
        selectedTimeTypeFilter: this.selectedTimeTypeFilter,
        selectedProjectFilter: this.selectedProjectFilter,
      }
    },

    // Filter item lists for FilterDrawer
    // PERFORMANCE: Returns cached values, actual calculation happens in watcher
    personFilterItems() {
      return this.cachedPersonFilterItems || []
    },

    clientFilterItems() {
      return this.cachedClientFilterItems || []
    },

    timeTypeFilterItems() {
      return this.cachedTimeTypeFilterItems || []
    },

    projectFilterItems() {
      return this.cachedProjectFilterItems || []
    },

    activeFilterName() {
      return this.activeFilter?.name || ''
    },

    previewCount() {
      if (!this.previewFilters) {
        return this.filteredTimes.length
      }

      // Calculate preview count based on preview filters
      const previewFiltered = this.getFilteredEntriesWithFilters(this.times || [], this.previewFilters)

      return previewFiltered.length
    },

    // Current tab context for FilterDrawer
    currentTabContext() {
      const currentTab = this.tabs.find((tab) => tab.id === this.currentTabId)

      return currentTab ? currentTab.label : ''
    },

    /**
     * Show export bar when there's data to export
     */
    showExportBar() {
      return this.filteredTimes && this.filteredTimes.length > 0
    },

    /**
     * Export options for the dropdown menu
     */
    exportOptions() {
      const { showExportBar } = this

      return [
        {
          active: showExportBar,
          key: 'csv',
          text: 'CSV',
          callback: this.exportAsCsv,
        },
        {
          active: showExportBar,
          key: 'xlsx',
          text: 'XLSX',
          callback: this.exportAsXlsx,
        },
      ]
    },

    /**
     * Export columns configuration for time entries
     * Defines which columns to export and how to format them
     */
    exportColumns() {
      return [
        {
          key: 'timeEntryDateFormatted',
          columnName: 'Date',
          formatAs: 'text',
          columnValue: (item) => item.timeEntryDateFormatted || '',
        },
        {
          key: 'personName',
          columnName: 'Person',
          formatAs: 'text',
          columnValue: (item) => item.personName || '',
        },
        {
          key: 'billingCode',
          columnName: 'Billing Code',
          formatAs: 'text',
          columnValue: (item) => this.resolveBillingCode(item.billingCode, item.clientId, item.clientName) || '',
        },
        {
          key: 'clientName',
          columnName: 'Client Name',
          formatAs: 'text',
          columnValue: (item) => item.clientName || '',
        },
        {
          key: 'projectName',
          columnName: 'Project',
          formatAs: 'text',
          columnValue: (item) => item.projectName || '',
        },
        {
          key: 'projectCode',
          columnName: 'Project Code',
          formatAs: 'text',
          columnValue: (item) => this.resolveProjectCode(item.projectCode, item.projectId, item.projectName) || '',
        },
        {
          key: 'timeClassificationName',
          columnName: 'Time Type',
          formatAs: 'text',
          columnValue: (item) => item.timeClassificationName || '',
        },
        {
          key: 'duration',
          columnName: 'Duration (min)',
          formatAs: 'number',
          columnValue: (item) => item.duration || 0,
        },
        {
          key: 'hhmm',
          columnName: 'HH:MM',
          formatAs: 'text',
          columnValue: (item) => item.hhmm || '',
        },
        {
          key: 'description',
          columnName: 'Notes',
          formatAs: 'text',
          columnValue: (item) => item.description || '',
        },
      ]
    },

    /**
     * Export groups formatted for the tools/exportAs action
     * Groups the filtered times based on current grouping or returns single ungrouped list
     */
    exportGroups() {
      return this.currentDataGroups.map((group) => ({
        ...group,
        items: group.items || [],
        rows: group.items || [],
      }))
    },

    /**
     * Export report name using current tab and date range
     */
    exportReportName() {
      const tabLabel = this.tabs.find((t) => t.id === this.currentTabId)?.label || 'Time'
      const dateRange = this.currentDateRange
      const startDate = dateRange && dateRange[0] ? dateRange[0] : ''
      const endDate = dateRange && dateRange[1] ? dateRange[1] : ''

      return `time_${tabLabel.replace(/\s+/g, '_').toLowerCase()}_${startDate}_to_${endDate}`
    },
  },
  watch: {
    renameFilterDialog(isOpen) {
      // Reset filter to rename when dialog closes
      if (!isOpen) {
        this.filterToRename = null
      }
    },

    /**
     * PERFORMANCE: Recalculate filter options only when times data actually changes
     * This prevents expensive recalculations on every reactive property change
     * Note: Tab counts are now updated directly in fetchTabData(), not here
     * CRITICAL: No immediate:true - let created() handle initial load completely first
     */
    timesDataHash: {
      handler(newHash, oldHash) {
        // Guard: Don't run until view is ready
        if (!this.isViewReady) {
          return
        }

        if (newHash !== oldHash) {
          // Recalculate all filter options when data changes
          this.recalculateFilterOptions()
          // Tab counts are updated individually in fetchTabData() - no need to recalculate all tabs
        }
      },
      // No immediate:true - prevents race condition during initial load
    },

    /**
     * PERFORMANCE: Reset progressive rendering when filtered data changes
     * This ensures fast initial render then progressively loads more items
     */
    filteredTimes: {
      handler() {
        // Reset to initial batch and start progressive rendering
        this.resetProgressiveRendering()
      },
    },

    /**
     * Sync grouping from store to local state
     * This enables proper reactivity when grouping changes via ToolsDrawer
     */
    currentGroupByProperties: {
      immediate: true,
      handler(newGrouping) {
        if (newGrouping && Array.isArray(newGrouping)) {
          this.localGroupByProperties = [...newGrouping]
        }
      },
    },

    currentTabId: {
      async handler(newValue, oldValue) {
        // Keep activeTab in sync with currentTabId
        this.activeTab = this.tabIndex

        // IMPORTANT: Only run logic if this is a tab change (oldValue exists)
        // On initial load (oldValue is undefined), skip to avoid duplicate fetch
        // The created() hook handles initial data loading
        if (!oldValue) {
          return
        }

        // Clear filters when switching between tabs to prevent filter carryover
        // For tabs that show org-wide data, clear person filter completely
        this.clearAllFilters(true) // true = clear person filter completely

        // Clean up URL parameters when switching tabs
        const currentQuery = this.$route.query
        const cleanedQuery = this.cleanQueryParameters(currentQuery)

        // When switching tabs, remove ALL date-related parameters for a clean slate
        // Each tab will use its own default date range
        delete cleanedQuery.weekOffset
        delete cleanedQuery.weekNumber
        delete cleanedQuery.year
        delete cleanedQuery.startDate
        delete cleanedQuery.endDate

        // Check if the cleaned query is different from current query
        const currentQueryString = JSON.stringify(currentQuery)
        const cleanedQueryString = JSON.stringify(cleanedQuery)

        if (currentQueryString !== cleanedQueryString) {
          this.$router.push({ query: cleanedQuery })
        }

        // Apply the appropriate date range for the new view if it hasn't been customized
        this.applyDefaultDateRangeForView(newValue)

        // Check if we need to re-fetch data due to date range change
        if (this.shouldRefetchForTabChange(oldValue, newValue)) {
          await this.fetchTimeData()
        }
      },
      immediate: true,
    },
    timeShift: {
      async handler(newValue, oldValue) {
        // Only fetch new data if we're in time-entry view and timeShift actually changed
        if (this.currentTabId === 'time-entry' && newValue !== oldValue && oldValue !== undefined) {
          // Skip background fetches - only refresh the Time Entry tab
          // Other tabs (MTD, Billable, Projects) have their own date ranges
          await this.fetchTimeData({ skipBackgroundFetch: true })
        }
      },
    },
    '$route.query.weekOffset': {
      immediate: true,
      handler(newWeekOffset, oldWeekOffset) {
        // Sync store when URL changes (browser back/forward, direct URL access)
        if (newWeekOffset !== oldWeekOffset) {
          const parsedShift = newWeekOffset ? parseInt(newWeekOffset, 10) : 0
          if (!Number.isNaN(parsedShift)) {
            // Update store to match URL (don't trigger URL update again)
            // Check if internal module is registered before committing
            if (this.$store.hasModule && this.$store.hasModule('internal')) {
              this.$store.commit('internal/timeShift', parsedShift)
            }
          }
        }
      },
    },
    '$route.query.weekNumber': {
      async handler(newWeekNumber, oldWeekNumber) {
        // Sync store and fetch data when weekNumber changes (absolute week reference)
        if (newWeekNumber !== oldWeekNumber && newWeekNumber !== undefined) {
          const { year } = this.$route.query
          if (year !== undefined) {
            const parsedWeekNumber = parseInt(newWeekNumber, 10)
            const parsedYear = parseInt(year, 10)

            if (!Number.isNaN(parsedWeekNumber) && !Number.isNaN(parsedYear)) {
              // Calculate offset from current week
              const now = dates.now()
              const currentWeekNumber = now.week()
              const currentYear = now.year()

              let weeksOffset = 0
              if (parsedYear === currentYear) {
                weeksOffset = parsedWeekNumber - currentWeekNumber
              } else {
                const yearDiff = parsedYear - currentYear
                weeksOffset = (yearDiff * 52) + (parsedWeekNumber - currentWeekNumber)
              }

              // Update store to match URL
              if (this.$store.hasModule && this.$store.hasModule('internal')) {
                this.$store.commit('internal/timeShift', weeksOffset)
              }

              // Fetch new data for time-entry view
              // Skip background fetch - only refresh Time Entry tab
              if (this.currentTabId === 'time-entry' && oldWeekNumber !== undefined) {
                await this.fetchTimeData({ skipBackgroundFetch: true })
              }
            }
          }
        }
      },
    },
    '$route.query.year': {
      async handler(newYear, oldYear) {
        // Sync store and fetch data when year changes (absolute week reference)
        if (newYear !== oldYear && newYear !== undefined) {
          const { weekNumber } = this.$route.query
          if (weekNumber !== undefined) {
            const parsedWeekNumber = parseInt(weekNumber, 10)
            const parsedYear = parseInt(newYear, 10)

            if (!Number.isNaN(parsedWeekNumber) && !Number.isNaN(parsedYear)) {
              // Calculate offset from current week
              const now = dates.now()
              const currentWeekNumber = now.week()
              const currentYear = now.year()

              let weeksOffset = 0
              if (parsedYear === currentYear) {
                weeksOffset = parsedWeekNumber - currentWeekNumber
              } else {
                const yearDiff = parsedYear - currentYear
                weeksOffset = (yearDiff * 52) + (parsedWeekNumber - currentWeekNumber)
              }

              // Update store to match URL
              if (this.$store.hasModule && this.$store.hasModule('internal')) {
                this.$store.commit('internal/timeShift', weeksOffset)
              }

              // Fetch new data for time-entry view
              // Skip background fetch - only refresh Time Entry tab
              if (this.currentTabId === 'time-entry' && oldYear !== undefined) {
                await this.fetchTimeData({ skipBackgroundFetch: true })
              }
            }
          }
        }
      },
    },
    '$route.query': {
      async handler(newQuery, oldQuery) {
        // Watch for changes in startDate/endDate parameters
        const oldStartDate = oldQuery?.startDate
        const oldEndDate = oldQuery?.endDate
        const newStartDate = newQuery?.startDate
        const newEndDate = newQuery?.endDate

        // If startDate or endDate changed, refetch data for time-entry view
        if (this.currentTabId === 'time-entry'
            && (oldStartDate !== newStartDate || oldEndDate !== newEndDate)) {
          await this.fetchTimeData()
        }
      },
      deep: true,
    },
    // Watch for time modal closing to refresh data only if submission was successful
    '$store.state.internal.isTimeModalVisible': {
      async handler(isVisible, wasVisible) {
        // When modal closes (was visible, now not visible), check if a submission occurred
        if (wasVisible && !isVisible) {
          const wasSubmitted = this.$store.state.internal.timeEntrySubmitted

          if (wasSubmitted) {
            // Reset the flag and refetch data
            // Skip background fetch - only refresh current tab
            this.$store.commit('internal/timeEntrySubmitted', false)
            await this.fetchTimeData({ skipBackgroundFetch: true })
          }
        }
      },
    },

    /**
     * Sync weekViewMode to URL when it changes (user toggles layout mode)
     * Only applies to time-entry tab (calendar vs columns)
     */
    weekViewMode(newMode, oldMode) {
      if (this.currentTabId !== 'time-entry') return
      if (newMode === oldMode) return

      // Update URL to reflect layout mode change
      const currentQuery = { ...this.$route.query }
      const cleanedQuery = this.cleanQueryParameters(currentQuery)

      // Add layout param if not default, remove if default
      if (newMode !== 'columns') {
        cleanedQuery.layout = newMode
      } else {
        delete cleanedQuery.layout
      }

      // Only push if query actually changed
      if (JSON.stringify(cleanedQuery) !== JSON.stringify(this.$route.query)) {
        this.$router.push({ query: cleanedQuery })
      }
    },

    /**
     * Sync listViewMode to URL when it changes (user toggles cards vs table)
     * Applies to MTD, Billable, Projects tabs
     */
    listViewMode(newMode, oldMode) {
      if (this.currentTabId === 'time-entry') return
      if (newMode === oldMode) return

      // Update URL to reflect layout mode change
      const currentQuery = { ...this.$route.query }
      const cleanedQuery = this.cleanQueryParameters(currentQuery)

      // Add layout param if not default, remove if default
      if (newMode !== 'cards') {
        cleanedQuery.layout = newMode
      } else {
        delete cleanedQuery.layout
      }

      // Only push if query actually changed
      if (JSON.stringify(cleanedQuery) !== JSON.stringify(this.$route.query)) {
        this.$router.push({ query: cleanedQuery })
      }
    },

    /**
     * Sync localGroupByProperties to URL when grouping changes
     * Supports single grouping property in URL for now
     */
    localGroupByProperties: {
      deep: true,
      handler(newGrouping, oldGrouping) {
        // Skip if arrays are the same
        if (JSON.stringify(newGrouping) === JSON.stringify(oldGrouping)) return

        // Update URL to reflect grouping change
        const currentQuery = { ...this.$route.query }
        const cleanedQuery = this.cleanQueryParameters(currentQuery)

        // Add groupByProperty if set, remove if empty
        if (newGrouping && newGrouping.length > 0) {
          // URL only supports single grouping property for now
          // eslint-disable-next-line prefer-destructuring
          cleanedQuery.groupByProperty = newGrouping[0]
        } else {
          delete cleanedQuery.groupByProperty
        }

        // Only push if query actually changed
        if (JSON.stringify(cleanedQuery) !== JSON.stringify(this.$route.query)) {
          this.$router.push({ query: cleanedQuery })
        }
      },
    },

    /**
     * Sync layout param from URL when route changes (browser back/forward)
     */
    '$route.query.layout': {
      handler(newLayout) {
        if (!newLayout) {
          // Reset to defaults
          if (this.currentTabId === 'time-entry') {
            this.weekViewMode = 'columns'
          } else {
            this.listViewMode = 'cards'
          }
          return
        }

        const validLayoutModes = {
          'time-entry': ['calendar', 'columns'],
          other: ['cards', 'table'],
        }

        const isTimeEntryTab = this.currentTabId === 'time-entry'
        const validModes = isTimeEntryTab ? validLayoutModes['time-entry'] : validLayoutModes.other

        if (validModes.includes(newLayout)) {
          if (isTimeEntryTab) {
            this.weekViewMode = newLayout
          } else {
            this.listViewMode = newLayout
          }
        }
      },
    },

    /**
     * Sync groupByProperty from URL when route changes (browser back/forward)
     */
    '$route.query.groupByProperty': {
      handler(newGroupByProperty) {
        const validGroupProperties = ['personName', 'clientName', 'timeClassificationName', 'projectName', 'time_entry_date']
        const timeReportKey = 'time'

        if (!newGroupByProperty) {
          // Clear grouping
          this.$store.commit('tools/grouping', { groupByProperties: [], reportKey: timeReportKey })
          this.localGroupByProperties = []
          return
        }

        if (validGroupProperties.includes(newGroupByProperty)) {
          this.$store.commit('tools/grouping', { groupByProperties: [newGroupByProperty], reportKey: timeReportKey })
          this.localGroupByProperties = [newGroupByProperty]
        }
      },
    },
  },
  async created() {
    this.$log.info(['[Time.vue] created() - START'])

    // Self-managed view: Set reportName in tools store
    // This is normally done by SearchBar, but we bypass that for self-managed views
    // NOTE: SearchBar's configureStore already handles internal module registration
    const reportName = this.$route.params.reportName || 'time'
    const reportKey = 'time'
    const routeName = 'internal'

    this.$store.commit('tools/reportName', reportName)

    // CRITICAL: Wait for SearchBar's configureStore to complete before accessing modules
    // SearchBar calls configureStore asynchronously, but Time.vue created() runs immediately
    // Use the existing isStoreRegistered action to check if internal module is ready
    const maxRetries = 50 // 5 seconds max wait

    let retries = 0
    let isRegistered = await this.$store.dispatch('isStoreRegistered', 'internal', { root: true })

    while (!isRegistered && retries < maxRetries) {
      this.$log.info(['[Time.vue] created() - Waiting for internal module... retry', retries])

      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => { setTimeout(resolve, 100) })

      retries += 1

      // eslint-disable-next-line no-await-in-loop
      isRegistered = await this.$store.dispatch('isStoreRegistered', 'internal', { root: true })
    }

    if (!isRegistered) {
      this.$log.info(['[Time.vue] created() - Module not registered, registering internal module directly'])

      try {
        await this.$store.dispatch('registerModule', 'internal', { root: true })
        isRegistered = await this.$store.dispatch('isStoreRegistered', 'internal', { root: true })
      } catch (err) {
        this.$log.error(['[Time.vue] created() - Failed to register internal module:', err])
      }
    }

    if (!isRegistered) {
      this.$log.error(['[Time.vue] created() - TIMEOUT: internal module not registered after 5 seconds'])

      return
    }

    await this.$store.dispatch('tools/setColumns', { reportKey, reportName, routeName }, { root: true })

    // Commit items mutation with empty report to initialize storage
    // This mimics what successHandler does before committing viewId
    // eslint-disable-next-line no-console
    this.$log.info(['[Time.vue] created() - Committing items'])

    this.$store.commit('tools/items', {
      reportKey,
      reportName,
      moduleName: 'internal',
      endpointName: 'time',
      items: [],
    })

    // Increment viewId to trigger re-render
    this.$log.info(['[Time.vue] created() - Committing viewId for:', reportKey])

    this.$store.commit('tools/viewId', reportKey)

    // Initialize activeTab based on currentTabId
    this.activeTab = this.tabIndex

    // Initialize date ranges from URL parameters or defaults
    this.initializeDateRangesFromUrl()

    // Initialize timeShift from URL query parameters on first load
    // Priority: weekNumber/year → weekOffset → store default
    const weekNumber = typeof this.$route.query.weekNumber === 'string' ? this.$route.query.weekNumber : undefined
    const year = typeof this.$route.query.year === 'string' ? this.$route.query.year : undefined
    const weekOffset = typeof this.$route.query.weekOffset === 'string' ? this.$route.query.weekOffset : undefined

    // Check if internal module is registered before committing
    const hasInternalModule = this.$store.hasModule && this.$store.hasModule('internal')

    this.$log.info(['[Time.vue] created() - Initializing timeShift from URL parameters'])

    if (weekNumber !== undefined && year !== undefined) {
      // Absolute week reference - calculate offset and sync to store
      const parsedWeekNumber = parseInt(weekNumber, 10)
      const parsedYear = parseInt(year, 10)

      if (!Number.isNaN(parsedWeekNumber) && !Number.isNaN(parsedYear)) {
        const now = dates.now()
        const currentWeekNumber = now.week()
        const currentYear = now.year()

        let weeksOffset = 0

        if (parsedYear === currentYear) {
          weeksOffset = parsedWeekNumber - currentWeekNumber
        } else {
          const yearDiff = parsedYear - currentYear
          weeksOffset = (yearDiff * 52) + (parsedWeekNumber - currentWeekNumber)
        }

        if (hasInternalModule) {
          this.$store.commit('internal/timeShift', weeksOffset)
        }
      }
    } else if (weekOffset !== undefined) {
      // Relative week offset - sync to store
      const parsedShift = parseInt(weekOffset, 10)

      if (!Number.isNaN(parsedShift) && hasInternalModule) {
        this.$store.commit('internal/timeShift', parsedShift)
      }
    }

    this.$log.info(['[Time.vue] created() - timeShift initialized to:', this.timeShift])

    // Initialize layout mode from URL query parameter
    // For time-entry tab: 'calendar' or 'columns' (default: 'columns')
    // For other tabs: 'cards' or 'table' (default: 'cards')
    const layout = typeof this.$route.query.layout === 'string' ? this.$route.query.layout : undefined
    const groupByProperty = typeof this.$route.query.groupByProperty === 'string'
      ? this.$route.query.groupByProperty
      : undefined

    if (layout) {
      const isTimeEntryTab = this.currentTabId === 'time-entry'
      const validModes = isTimeEntryTab ? ['calendar', 'columns'] : ['cards', 'table']

      if (validModes.includes(layout)) {
        if (isTimeEntryTab) {
          this.weekViewMode = layout
        } else {
          this.listViewMode = layout
        }
        this.$log.info(['[Time.vue] created() - layout mode initialized from URL:', layout])
      } else {
        this.$log.warn(['[Time.vue] created() - invalid layout mode in URL:', layout, '- valid modes:', validModes])
      }
    }

    // Initialize groupByProperty from URL query parameter
    // Valid values: 'personName', 'clientName', 'timeClassificationName', 'projectName', 'time_entry_date'
    if (groupByProperty) {
      if (SAFE_GROUP_BY_PROPERTIES.includes(groupByProperty)) {
        // Will be applied after initializeToolsStoreOptions sets up the store
        this.pendingGroupByProperty = groupByProperty
        this.$log.info(['[Time.vue] created() - groupByProperty pending from URL:', groupByProperty])
      } else {
        this.$log.warn(['[Time.vue] created() - invalid groupByProperty in URL:', groupByProperty, '- valid options:', SAFE_GROUP_BY_PROPERTIES])
      }
    }

    // Load saved templates from localStorage (synchronous, fast)
    this.loadTemplates()

    // Initialize tools store for GroupToolbar and SortToolbar support
    this.initializeToolsStoreOptions()

    // Apply pending groupByProperty from URL after store is initialized
    if (this.pendingGroupByProperty) {
      const timeReportKey = 'time'
      this.$store.commit('tools/grouping', { groupByProperties: [this.pendingGroupByProperty], reportKey: timeReportKey })
      this.localGroupByProperties = [this.pendingGroupByProperty]
      this.$log.info(['[Time.vue] created() - groupByProperty applied:', this.pendingGroupByProperty])
      this.pendingGroupByProperty = null
    }

    // CRITICAL FIX: Load persons FIRST before fetchTimeData()
    // fetchTimeData() calls getAccessiblePersons(), which requires persons data
    // to properly filter by manager relationships. If persons haven't loaded,
    // getAccessiblePersons() falls back to just the current user, missing delegates.
    // See: https://github.com/adops-prod/iadops-next/issues/NNN
    this.$log.info(['[Time.vue] created() - Loading persons BEFORE time data (critical for manager filtering)'])
    await this.getPersons()

    // OPTIMIZATION: Load time data + non-critical background data in parallel
    // Time entries are critical path; load after persons so getAccessiblePersons() works
    // Load clients/projects/services/timeTypes in background (for filter dropdowns)
    await Promise.all([
      this.fetchTimeData(), // Critical - now safe, persons data is available
      this.getClients(), // Background - for filter dropdowns
      this.getProjects(), // Background - for dropdowns
      this.getServices(), // Background - for filters
      this.getTimeTypes(), // Background - for time type filter
    ])

    // CRITICAL: Set loading = false only AFTER all initialization complete
    // This ensures isViewReady returns true only when safe to render
    this.loading = false

    // CRITICAL: Recalculate filter options AFTER all data has loaded AND loading=false
    // The timesDataHash watcher may fire during Promise.all when fetchTimeData
    // completes but getPersons/getClients are still in flight, causing empty
    // filter dropdowns. This explicit call ensures proper filter population.
    // Must be AFTER loading=false because recalculateFilterOptions checks isViewReady.
    this.recalculateFilterOptions()

    // Check for action query parameter to auto-open modals
    // This enables deep-linking to the new entry form: /time?action=create
    this.handleActionQueryParameter()
  },
  mounted() {
    // Load saved filters for this view
    this.$store.dispatch('savedFilters/loadFilters', 'time', { root: true })

    // Add keyboard shortcuts
    window.addEventListener('keydown', this.handleKeyboardShortcut)
  },
  beforeDestroy() {
    // Remove keyboard shortcuts
    window.removeEventListener('keydown', this.handleKeyboardShortcut)

    // Cancel any pending progressive rendering
    if (this.progressiveRenderRAF) {
      cancelAnimationFrame(this.progressiveRenderRAF)
    }
  },
  methods: {
    /**
     * Fetch clients for filter dropdowns and time entry forms
     * Component-level data management (self-managed view pattern)
     */
    async getClients() {
      if (this.clients.length > 0) return // Already loaded

      this.loadingClients = true
      try {
        const response = await api.getClients()
        if (response?.content) {
          this.clients = response.content
        }
      } catch (error) {
        // Silent fail - filters will show empty, not critical
      } finally {
        this.loadingClients = false
      }
    },

    /**
     * Fetch persons for filter dropdowns and time entry forms
     * Component-level data management (self-managed view pattern)
     */
    async getPersons() {
      if (this.persons.length > 0) return // Already loaded

      this.loadingPersons = true
      try {
        const response = await api.getPersons()
        if (response?.content) {
          this.persons = response.content
        }
      } catch (error) {
        // Silent fail - filters will show empty, not critical
      } finally {
        this.loadingPersons = false
      }
    },

    setClientMap(client, clientMap) {
      if (client.clientId && !clientMap[client.clientId]) {
        clientMap[client.clientId] = {
          // Identifiers
          clientId: client.clientId,
          clientName: client.clientName || '',

          // Form values
          text: client.clientName || '',
          value: client.clientId,
        }
      }

      return clientMap
    },
    setPersonMap(person, personMap) {
      if (person.personId && !personMap[person.personId]) {
        // API returns personNameFirst/personNameLast (not personFirstName/personLastName)
        const firstName = person.personNameFirst || ''
        const lastName = person.personNameLast || ''
        const fullName = `${firstName} ${lastName}`.trim()

        personMap[person.personId] = {
          // Identifiers
          personId: person.personId,
          managerPersonId: person.managerPersonId || null,
          // Display info - construct from first/last name
          displayName: fullName,

          // Database fields for first and last name.
          firstName,
          lastName,

          // Form values
          text: fullName,
          value: person.personId,
        }
      }

      return personMap
    },
    setProjectMap(project, projectMap) {
      if (project.projectId && !projectMap[project.projectId]) {
        projectMap[project.projectId] = {
          // Identifiers
          projectId: project.projectId,
          projectName: project.projectName || '',
          projectCode: project.projectCode || '',

          // Form values
          text: project.projectName || '',
          value: project.projectId,
        }
      }

      return projectMap
    },
    setServiceMap(service, servicesMap) {
      if (service.serviceId && !servicesMap[service.serviceId]) {
        servicesMap[service.serviceId] = {
          // Identifiers
          serviceId: service.serviceId,
          serviceName: service.serviceName || '',

          // Form values
          text: service.serviceName || '',
          value: service.serviceId,
        }
      }

      return servicesMap
    },
    setTimeTypeMap(timeType, timeTypeMap) {
      // API returns timeClassificationId/Name, support both naming conventions
      const id = timeType.timeClassificationId || timeType.timeTypeId
      const name = timeType.timeClassificationName || timeType.timeTypeName || ''

      if (id && !timeTypeMap[id]) {
        timeTypeMap[id] = {
          // Time type identifiers (both naming conventions for compatibility)
          timeTypeId: id,
          timeTypeName: name,
          timeClassificationId: id,
          timeClassificationName: name,

          // Form values
          text: name,
          value: id,
        }
      }

      return timeTypeMap
    },

    /**
     * Initialize date ranges from URL query parameters or use defaults
     * Called during component creation to restore date range state from URL
     */
    initializeDateRangesFromUrl() {
      this.$log.info(['[Time.vue] initializeDateRangesFromUrl() - START'])

      const { startDate, endDate, weekNumber, year, weekOffset } = this.$route.query
      const currentView = this.getSafeTabKey(this.currentTabId) || 'time-entry'

      // PRIORITY 1: If URL has explicit startDate/endDate parameters, use them
      if (startDate && endDate) {
        this.setDateRangeForView(currentView, [startDate, endDate])
      } else if (weekNumber !== undefined && year !== undefined) {
        // PRIORITY 2: If URL has weekNumber/year, calculate the week dates
        const parsedWeekNumber = parseInt(weekNumber, 10)
        const parsedYear = parseInt(year, 10)

        if (!Number.isNaN(parsedWeekNumber) && !Number.isNaN(parsedYear)) {
          // Calculate offset from current week
          const now = dates.now()
          const currentWeekNumber = now.week()
          const currentYear = now.year()

          let weeksOffset = 0
          if (parsedYear === currentYear) {
            weeksOffset = parsedWeekNumber - currentWeekNumber
          } else {
            const yearDiff = parsedYear - currentYear
            weeksOffset = (yearDiff * 52) + (parsedWeekNumber - currentWeekNumber)
          }

          // Calculate the week dates using the offset
          const targetWeek = dates.getWeek(weeksOffset)
          if (targetWeek && targetWeek[0] && targetWeek[6]) {
            const weekStart = dates.getFormattedDate(targetWeek[0])
            const weekEnd = dates.getFormattedDate(targetWeek[6])
            this.setDateRangeForView(currentView, [weekStart, weekEnd])
          } else {
            // Fallback to defaults
            this.initializeDefaultDateRanges()
          }
        } else {
          // Invalid weekNumber/year, use defaults
          this.initializeDefaultDateRanges()
        }
      } else if (weekOffset !== undefined) {
        // PRIORITY 3: If URL has relative week offset, calculate the week dates
        const parsedShift = parseInt(weekOffset, 10)

        if (!Number.isNaN(parsedShift)) {
          const targetWeek = dates.getWeek(parsedShift)
          if (targetWeek && targetWeek[0] && targetWeek[6]) {
            const weekStart = dates.getFormattedDate(targetWeek[0])
            const weekEnd = dates.getFormattedDate(targetWeek[6])
            this.setDateRangeForView(currentView, [weekStart, weekEnd])
          } else {
            // Fallback to defaults
            this.initializeDefaultDateRanges()
          }
        } else {
          // Invalid weekOffset, use defaults
          this.initializeDefaultDateRanges()
        }
      } else {
        // No URL parameters, initialize with defaults
        this.initializeDefaultDateRanges()
      }
    },

    /**
     * Initialize all views with default date ranges
     */
    initializeDefaultDateRanges() {
      this.$log.info(['[Time.vue] initializeDefaultDateRanges() - START'])

      if (!this.dateRanges['time-entry']) {
        this.setDateRangeForView('time-entry', this.defaultDateRanges['time-entry'])
      }
      if (!this.dateRanges.mtd) {
        this.setDateRangeForView('mtd', this.defaultDateRanges.mtd)
      }
      if (!this.dateRanges['last-month']) {
        this.setDateRangeForView('last-month', this.defaultDateRanges['last-month'])
      }
      if (!this.dateRanges.billable) {
        this.setDateRangeForView('billable', this.defaultDateRanges.billable)
      }
      if (!this.dateRanges.projects) {
        this.setDateRangeForView('projects', this.defaultDateRanges.projects)
      }
    },

    /**
     * Export table data by calling the DataTable's export methods
     * @param {String} exportFormat - Export format ('csv' or 'xlsx')
     */
    exportTableData(exportFormat) {
      const { table } = this.$refs
      if (table) {
        if (exportFormat === 'csv') {
          table.exportAsCsv()
        } else if (exportFormat === 'xlsx') {
          table.exportAsXlsx()
        }
      }
    },

    /**
     * Export time entries as CSV file
     * Uses the tools/exportAs action with CSV format
     */
    exportAsCsv() {
      const {
        exportColumns,
        exportGroups,
        exportReportName: exportName,
      } = this
      const exportFormat = 'csv'

      return this.$store.dispatch('tools/exportAs', { exportColumns, exportFormat, exportGroups, exportName })
    },

    /**
     * Export time entries as XLSX file
     * Uses the tools/exportAs action with XLSX format
     */
    exportAsXlsx() {
      const {
        exportColumns,
        exportGroups,
        exportReportName: exportName,
      } = this
      const exportFormat = 'xlsx'

      return this.$store.dispatch('tools/exportAs', { exportColumns, exportFormat, exportGroups, exportName })
    },

    /**
     * PERFORMANCE: Recalculate all filter options when times data changes
     * Separated from computed properties to avoid side effects
     */
    recalculateFilterOptions() {
      // Guard: Don't run until view is ready
      if (!this.isViewReady) {
        return
      }

      this.cachedPersonFilterItems = this.calculatePersonFilterItems()
      this.cachedClientFilterItems = this.calculateClientFilterItems()
      this.cachedTimeTypeFilterItems = this.calculateTimeTypeFilterItems()
      this.cachedProjectFilterItems = this.calculateProjectFilterItems()
    },

    /**
     * Calculate person filter items from times data
     * @returns {Array} Person filter options
     */
    calculatePersonFilterItems() {
      // Resort persons with entries in case times data changed.
      return this.personsArray.sort((a, b) => a.text.localeCompare(b.text))
    },

    /**
     * @function calculateClientFilterItems
     * @desc Calculate client filter items from times data
     * @returns {Array} Client filter options
     */
    calculateClientFilterItems() {
      // Resort clients with entries in case times data changed.
      return this.clientsArray.sort((a, b) => a.text.localeCompare(b.text))
    },

    /**
     * @function calculateTimeTypeFilterItems
     * @desc Calculate time type filter items from times data
     * @returns {Array} Time type filter options
     */
    calculateTimeTypeFilterItems() {
      // Resort time types with entries in case times data changed.
      return this.timeTypesArray.sort((a, b) => a.text.localeCompare(b.text))
    },

    /**
     * @function calculateProjectFilterItems
     * @desc Calculate project filter items from times data
     * @returns {Array} Project filter options
     */
    calculateProjectFilterItems() {
      // Resort projects with entries in case times data changed.
      return this.projectsArray.sort((a, b) => a.text.localeCompare(b.text))
    },

    /**
     * Recalculate tab counts (debounced for performance)
     * PERFORMANCE: Only runs when times data changes, with 150ms debounce
     */
    recalculateTabCounts: debounce(function recalculateTabCounts() {
      this.tabs.forEach((tab) => {
        let count = 0

        // Get count based on tab type (no person/client/project/time-type filters applied)
        // Only date range filtering is applied to maintain consistent tab counts
        switch (tab.id) {
          case 'time-entry': {
            // For time-entry, count entries in the current week/date range
            const weekEntries = this.filteredTimesForTab('time-entry')
            count = weekEntries.length
            break
          }

          case 'mtd': {
            // For MTD, count all entries in the date range
            const mtdEntries = this.filteredTimesForTab('mtd')
            count = mtdEntries.length
            break
          }

          case 'last-month': {
            // For Last Month, count all entries in the date range
            const lastMonthEntries = this.filteredTimesForTab('last-month')
            count = lastMonthEntries.length
            break
          }

          case 'billable': {
            // For billable, count only billable entries in the date range
            const dateFilteredEntries = this.filteredTimesForTab('billable')
            count = dateFilteredEntries.filter((entry) => entry.timeClassificationName === 'BILLABLE').length
            break
          }

          case 'projects': {
            // For projects, count entries with projects assigned in the date range
            const dateFilteredEntries = this.filteredTimesForTab('projects')
            count = dateFilteredEntries.filter((entry) => (entry?.projectId && entry.projectId !== -1) || entry.timeClassificationName === 'PROJECT').length
            break
          }

          default:
            count = 0
        }

        this.$set(this.tabCounts, tab.id, count)
      })
    }, 150),

    /**
     * Initialize grouping and sorting options in tools store
     * This enables GroupToolbar and SortToolbar to work with Time view
     */
    initializeToolsStoreOptions() {
      const reportKey = 'time' // Use a consistent key for Time view

      // Define grouping options for Time view tables
      const groupingOptions = [
        { text: 'Person', value: 'personName' },
        { text: 'Client', value: 'clientName' },
        { text: 'Time Type', value: 'timeClassificationName' },
        { text: 'Project', value: 'projectName' },
        { text: 'Date', value: 'time_entry_date' },
      ]

      // Set grouping options in store
      this.$store.commit('tools/groupingOptions', { groupingOptions, reportKey })

      // Initialize empty grouping state if not already set
      if (!this.$store.state.tools.groupByProperties[reportKey]) {
        this.$store.commit('tools/grouping', { groupByProperties: [], reportKey })
      }

      // Get columns from timeEntryHeaders and format for tools store
      // Filter out non-sortable columns (select, actions)
      const columns = this.timeEntryHeaders
        .filter((header) => header.sortable)
        .map((header) => ({
          text: header.text,
          value: header.value,
          sortable: true,
          visible: true,
          class: header.class,
        }))

      // Set columns in store for SortToolbar to use
      const existingTimeColumns = this.$store.state.tools.columns[reportKey]

      if (!columnUtils.hasCompatibleColumnSchema(existingTimeColumns, columns)) {
        const storedPrefs = columnUtils.loadColumnPreferences(reportKey)
        const mergedColumns = columnUtils.applyColumnPreferences(columns, storedPrefs)
        this.$store.commit('tools/columns', { [reportKey]: mergedColumns })
      }

      // Initialize default sorting by date (descending) if not set or if current sort key is invalid for this view
      const currentSortKeys = this.$store.state.tools.sortingByKeys || []
      const validKeys = columns.map((c) => c.value)
      const hasValidSortKey = currentSortKeys.length > 0 && currentSortKeys.every((key) => validKeys.includes(key))

      if (!hasValidSortKey) {
        this.$store.commit('tools/sortingByKeys', ['time_entry_date'])
        this.$store.commit('tools/sortingByDirections', ['desc'])
      }
    },

    /**
     * Apply sorting to an array of time entries based on sortingByKeys and sortingByDirections.
     * Uses the underscore extension methods for case-insensitive sorting.
     * @param {Array} items - Array of time entries to sort
     * @returns {Array} Sorted array
     */
    applySorting(items) {
      const { sortingByKeys, sortingByDirections } = this

      if (!sortingByKeys || sortingByKeys.length === 0) {
        return items
      }

      // Apply sorting in reverse order (last key first) to maintain proper multi-level sort
      const sortedItems = sortingByKeys.reduceRight((sorted, key, index) => {
        const direction = sortingByDirections[index] || 'asc'
        const sortDsc = direction === 'desc'

        const valueGetter = (item) => {
          const value = item[key]
          // Handle different value types
          if (typeof value === 'boolean') {
            return value ? 1 : 0
          }
          if (typeof value === 'string') {
            return value.toLowerCase()
          }
          // Handle date values
          if (key.toLowerCase().includes('date') && value) {
            return dates.getUnixDate(value)
          }
          return value ?? ''
        }

        const sorter = sortDsc ? _.sortDscByCaseInsensitive : _.sortAscByCaseInsensitive
        return sorter(sorted, valueGetter)
      }, items)

      return sortedItems
    },

    openTimeHelpDialog() {
      this.showTimeHelp = true
    },

    /**
     * Handle keyboard shortcuts for navigation and quick actions
     * @param {KeyboardEvent} event - Keyboard event
     */
    handleKeyboardShortcut(event) {
      // Only handle shortcuts in time-entry view
      if (this.currentTabId !== 'time-entry') return

      // Don't trigger shortcuts if user is typing in an input field
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') return

      // Arrow keys for week navigation
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        this.timeShiftLeft()
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        this.timeShiftRight()
      } else if (event.key === 'n' && !event.ctrlKey && !event.metaKey) {
        // 'n' key for new entry (today)
        event.preventDefault()
        this.openTimeEntryFormCreate(this.today)
      } else if (event.key === 't' && !event.ctrlKey && !event.metaKey) {
        // 't' key for go to today
        event.preventDefault()
        this.timeShift = 0
      } else if (event.key === 'Escape' && this.selectedEntries.length > 0) {
        // Escape to clear selection
        event.preventDefault()
        this.clearSelection()
      }
    },

    /**
     * Handle action query parameter for deep-linking to modals
     * Supported actions:
     * - 'create': Opens the new time entry form
     * - 'new': Alias for 'create'
     *
     * Usage: /time?action=create or /time?action=new
     */
    handleActionQueryParameter() {
      const { action } = this.$route.query

      if (!action) return

      // Handle supported actions
      if (action === 'create' || action === 'new') {
        // Open the new entry form for today
        this.openTimeEntryFormCreate(this.today)

        // Clean up the URL by removing the action parameter
        // This prevents re-opening on page refresh
        const { action: _action, ...remainingQuery } = this.$route.query
        const cleanedQuery = this.cleanQueryParameters(remainingQuery)

        this.$router.replace({
          path: this.$route.path,
          query: cleanedQuery,
        }).catch(() => {})
      }
    },

    /**
     * Helper method to get filtered times for a specific tab
     * Used for calculating tab counts
     * Filters by date range only (not by person/client/project/time-type)
     * @param {String} tabId - The tab identifier
     * @returns {Array} Filtered time entries for the tab
     */
    filteredTimesForTab(tabId) {
      // Guard: Don't process until view is ready
      if (!this.isViewReady) {
        return []
      }

      // Get the specific tab's data instead of the current tab's data
      const allTimes = this.timesByTab[tabId] || []

      // Get the date range for the tab
      let dateRange

      if (tabId === 'time-entry') {
        // For time-entry, use the current date range (respects custom ranges and URL params)
        dateRange = this.currentDateRange
      } else {
        // For other tabs, use their configured date range
        dateRange = this.dateRanges[tabId] || this.dateRanges.mtd
      }

      // Filter entries by date range only
      const [startDate, endDate] = dateRange
      const start = dates.get(startDate)
      const end = dates.get(endDate)

      return allTimes.filter((entry) => {
        // Use timeEntryDate (dayjs object) from transformed entries
        if (!entry.timeEntryDate) return false
        const entryDate = entry.timeEntryDate
        return entryDate.isSameOrAfter(start, 'day') && entryDate.isSameOrBefore(end, 'day')
      })
    },

    // Template management methods
    loadTemplates() {
      const stored = localStorage.getItem('timeEntryTemplates')

      if (stored) {
        try {
          this.entryTemplates = JSON.parse(stored)
        } catch (error) {
          this.entryTemplates = []
        }
      }
    },

    saveTemplate(entry) {
      if (!this.templateName || !this.templateName.trim()) {
        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: 'Please enter a template name',
          status: 'error',
        }, { root: true })
        return
      }

      const template = {
        id: Date.now(),
        name: this.templateName.trim(),
        clientId: entry.client_id,
        clientName: entry.clientName,
        projectId: entry.project_id,
        projectName: entry.projectName,
        timeClassificationId: entry.time_classification_id,
        timeClassificationName: entry.timeClassificationName,
        duration: entry.duration,
        description: entry.description,
      }

      this.entryTemplates.push(template)

      localStorage.setItem('timeEntryTemplates', JSON.stringify(this.entryTemplates))

      this.$store.commit('tools/snackbar', true, { root: true })
      this.$store.commit('tools/snack', {
        message: `Template "${template.name}" saved successfully`,
        status: 'success',
      }, { root: true })

      this.templateName = ''
      this.templateDialog = false
    },

    deleteTemplate(templateId) {
      this.entryTemplates = this.entryTemplates.filter((t) => t.id !== templateId)
      localStorage.setItem('timeEntryTemplates', JSON.stringify(this.entryTemplates))

      this.$store.commit('tools/snackbar', true, { root: true })
      this.$store.commit('tools/snack', {
        message: 'Template deleted',
        status: 'success',
      }, { root: true })
    },

    applyTemplate(template) {
      const entry = {
        time_entry_date: dates.getFormattedDate(dates.now()),
        timeEntryDate: dates.now(),
        client_id: template.clientId,
        clientName: template.clientName,
        project_id: template.projectId,
        projectName: template.projectName,
        time_classification_id: template.timeClassificationId,
        timeClassificationName: template.timeClassificationName,
        duration: template.duration,
        description: template.description,
      }

      this.openTimeEntryFormCreate(entry)
    },

    /**
     * Get color for time type chip based on classification
     * @param {String} timeType - Time classification name
     * @returns {String} Color hex code for the chip
     */
    getTimeTypeColor(timeType) {
      const colorMap = {
        BILLABLE: '#055289', // darkestblue
        'NON-BILLABLE': '#0094ff', // brightblue
        PROJECT: '#007dd6', // blue
        ADX: '#647b8d', // gray
        'ADX-PG': '#5e6769', // coolgray
      }
      return colorMap[timeType] || '#9e9e9e' // vuetifygray
    },

    /**
     * Get text color for time type chip
     * @returns {String} Text color for the chip
     */
    getTimeTypeTextColor() {
      // All colors in the monochromatic scheme look best with white text
      return 'white'
    },

    lookupClientBillingCode(clientId, clientName) {
      const { clients } = this

      if (!Array.isArray(clients) || clients.length === 0) {
        return ''
      }

      const byId = clients.find((client) => client && client.clientId === clientId)
      if (byId && byId.billingCode) {
        return byId.billingCode
      }

      const byName = clients.find((client) => client && client.clientName === clientName)
      if (byName && byName.billingCode) {
        return byName.billingCode
      }

      return ''
    },

    resolveBillingCode(billingCode, clientId = null, clientName = null) {
      return billingCode || this.lookupClientBillingCode(clientId, clientName)
    },

    lookupProjectCode(projectId, projectName) {
      const byId = this.projectsById[projectId]
      if (byId && byId.projectCode) {
        return byId.projectCode
      }

      const byName = this.projectsArray.find((project) => project && project.projectName === projectName)
      if (byName && byName.projectCode) {
        return byName.projectCode
      }

      return ''
    },

    resolveProjectCode(projectCode, projectId = null, projectName = null) {
      return projectCode || this.lookupProjectCode(projectId, projectName)
    },

    formatClientWithBilling(clientName, billingCode, clientId = null) {
      const safeClient = clientName || 'N/A'
      const safeBilling = this.resolveBillingCode(billingCode, clientId, clientName)

      return safeBilling ? `${safeClient} • ${safeBilling}` : safeClient
    },

    /**
     * Filter entries based on search term and quick filters
     * OPTIMIZED: Single-pass filtering instead of 7 sequential passes
     * @param {Array} entries - Array of time entries to filter
     * @returns {Array} Filtered entries
     */
    getFilteredEntries(entries) {
      // Guard: Return empty array if entries is undefined or not an array
      if (!entries || !Array.isArray(entries)) {
        return []
      }

      // Pre-check which filters are active to avoid redundant checks in the loop
      const hasPersonFilter = this.selectedPersonFilter && this.selectedPersonFilter.length > 0
      const hasTypeFilter = this.selectedTimeTypeFilter && this.selectedTimeTypeFilter.length > 0
      const hasClientFilter = this.selectedClientFilter && this.selectedClientFilter.length > 0
      const hasProjectFilter = this.selectedProjectFilter && this.selectedProjectFilter.length > 0
      const hasSearchFilter = this.tableSearch && this.tableSearch.trim() !== ''

      // If no filters are active, apply sorting only if active
      if (!hasPersonFilter && !hasTypeFilter && !hasClientFilter && !hasProjectFilter && !hasSearchFilter) {
        if (this.sortingByKeys && this.sortingByKeys.length > 0) {
          return this.applySorting([...entries])
        }
        return entries
      }

      // Prepare search term once (outside the loop)
      const searchTerm = hasSearchFilter ? this.tableSearch.toLowerCase().trim() : null

      // Single-pass filter: check all conditions in one iteration
      const filteredEntries = entries.filter((entry) => {
        // Person filter check
        if (hasPersonFilter && !this.selectedPersonFilter.includes(entry.personId)) {
          return false
        }

        // Time type filter check
        if (hasTypeFilter && !this.selectedTimeTypeFilter.includes(entry.timeClassificationName)) {
          return false
        }

        // Client filter check
        if (hasClientFilter && !this.selectedClientFilter.includes(entry.clientName)) {
          return false
        }

        // Project filter check
        if (hasProjectFilter && !this.selectedProjectFilter.includes(entry.projectName)) {
          return false
        }

        // Search filter check
        if (hasSearchFilter) {
          // Search across multiple fields
          const searchableFields = [
            entry.billingCode,
            entry.clientName,
            entry.personName,
            entry.projectName,
            entry.timeClassificationName,
            entry.description,
          ]

          if (!searchableFields.some((field) => field && field.toString().toLowerCase().includes(searchTerm))) {
            return false
          }
        }

        // Entry passed all active filters
        return true
      })

      // Apply sorting to filtered entries
      if (this.sortingByKeys && this.sortingByKeys.length > 0) {
        return this.applySorting([...filteredEntries])
      }

      return filteredEntries
    },

    /**
     * Clear all active quick filters
     * @param {Boolean} clearPersonFilter - If true, clears person filter completely. If false, resets to current user.
     */
    clearAllFilters(clearPersonFilter = false) {
      // Reset person filter - either clear completely or reset to current user
      if (clearPersonFilter) {
        this.selectedPersonFilter = []
      } else {
        // When manually clearing (user clicks "Clear All"), also clear completely
        this.selectedPersonFilter = []
      }
      this.selectedTimeTypeFilter = []
      this.selectedClientFilter = []
      this.selectedProjectFilter = []
      this.tableSearch = ''
      this.filterMenuOpen = false
    },

    /**
     * PERFORMANCE: Reset progressive rendering and start loading items in batches
     * Uses requestAnimationFrame to avoid blocking the main thread
     */
    resetProgressiveRendering() {
      // Cancel any pending render
      if (this.progressiveRenderRAF) {
        cancelAnimationFrame(this.progressiveRenderRAF)
        this.progressiveRenderRAF = null
      }

      // Reset to initial batch
      this.renderedItemCount = this.renderBatchSize
      this.isProgressivelyRendering = true

      // Start rendering more items
      this.renderNextBatch()
    },

    /**
     * PERFORMANCE: Render the next batch of items using requestAnimationFrame
     * This ensures smooth UI by yielding to the browser between batches
     */
    renderNextBatch() {
      // Check if we need to render more
      if (this.renderedItemCount >= this.filteredTimes.length) {
        this.isProgressivelyRendering = false
        return
      }

      // Use requestAnimationFrame to yield to browser for smooth scrolling
      this.progressiveRenderRAF = requestAnimationFrame(() => {
        // Add next batch
        this.renderedItemCount = Math.min(
          this.renderedItemCount + this.renderBatchSize,
          this.filteredTimes.length,
        )

        // Continue rendering if more items remain
        if (this.renderedItemCount < this.filteredTimes.length) {
          // Small delay between batches to keep UI responsive
          setTimeout(() => this.renderNextBatch(), 16) // ~1 frame at 60fps
        } else {
          this.isProgressivelyRendering = false
        }
      })
    },

    /**
     * Clear the active saved filter
     * Clears all filters and deactivates the saved filter
     */
    async clearSavedFilter() {
      // Clear the active filter in the store
      await this.$store.dispatch('savedFilters/clearActiveFilter', { root: true })

      // Clear all quick filters
      this.clearAllFilters(true)
    },

    // Filter Drawer Methods
    handleFilterPreview(previewFilters) {
      // Store preview filters for live preview count
      this.previewFilters = previewFilters
    },

    // Handle ToolsDrawer Apply button
    handleToolsDrawerApplyFilters() {
      const panel = this.$refs.timeFilterPanel
      if (panel && panel.getFilters) {
        const filters = panel.getFilters()
        this.handleFilterApply(filters)
      }
    },

    // Handle ToolsDrawer Reset button
    handleToolsDrawerResetFilters() {
      const panel = this.$refs.timeFilterPanel
      if (panel && panel.resetFilters) {
        panel.resetFilters()
      }
      // Also clear the applied filters
      this.clearAllFilters(true)
      this.previewFilters = null
    },

    /**
     * Handle grouping changes from ToolsDrawer
     * ToolsDrawer already commits to store, so we sync local state
     */
    onGroupingChange() {
      // Grouping is already applied via store dispatch in ToolsDrawer
      // Local state is synced via watcher on currentGroupByProperties
      this.$log.info(['[Time.vue] onGroupingChange() - Grouping changed'])
    },

    /**
     * Handle sorting changes from ToolsDrawer
     * ToolsDrawer already commits to store, so we just need to trigger reactivity
     */
    onSortingChange() {
      // Sorting is already applied via store commit in ToolsDrawer
      // filteredTimes computed will re-evaluate automatically
      this.$log.info(['[Time.vue] onSortingChange() - Sorting changed'])
    },

    /**
     * Scroll to a specific group header
     * Used by ToolsDrawer's Jump tab
     * @param {String} groupName - The name of the group to scroll to
     */
    scrollToGroup(groupName) {
      this.$log.info(['[Time.vue] scrollToGroup() - Scrolling to group:', groupName])

      // Find the group header element
      const groupId = `group-${groupName.replace(/\s+/g, '-').toLowerCase()}`
      const element = document.getElementById(groupId)

      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      } else {
        // Try finding by data attribute as fallback
        const groupHeader = document.querySelector(`[data-group-name="${groupName}"]`)
        if (groupHeader) {
          groupHeader.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          })
        }
      }
    },

    handleFilterApply(filters) {
      // Apply the date range if it changed
      if (filters.startDate && filters.endDate) {
        const currentRange = this.currentDateRange || []
        const filtersChanged = filters.startDate !== currentRange[0] || filters.endDate !== currentRange[1]

        if (filtersChanged) {
          // Sync with toolbar date picker by calling onApplyDateRange
          this.onApplyDateRange([filters.startDate, filters.endDate])
        }
      }

      // Apply the filters
      this.selectedPersonFilter = filters.selectedPersonFilter || []
      this.selectedClientFilter = filters.selectedClientFilter || []
      this.selectedTimeTypeFilter = filters.selectedTimeTypeFilter || []
      this.selectedProjectFilter = filters.selectedProjectFilter || []

      // Clear preview
      this.previewFilters = null
    },

    handleFilterCancel() {
      // Clear preview filters
      this.previewFilters = null
    },

    clearActiveFilter() {
      this.clearSavedFilter()
    },

    getFilteredEntriesWithFilters(entries, filters) {
      // Guard: Return empty array if entries is undefined or not an array
      if (!entries || !Array.isArray(entries)) {
        return []
      }

      // Helper method to filter entries with a given set of filters
      // Used for preview count calculation
      let filtered = entries

      if (filters.selectedPersonFilter && filters.selectedPersonFilter.length > 0) {
        filtered = filtered.filter((entry) => filters.selectedPersonFilter.includes(entry.personId))
      }

      if (filters.selectedTimeTypeFilter && filters.selectedTimeTypeFilter.length > 0) {
        filtered = filtered.filter((entry) => filters.selectedTimeTypeFilter.includes(entry.timeClassificationName))
      }

      if (filters.selectedClientFilter && filters.selectedClientFilter.length > 0) {
        filtered = filtered.filter((entry) => filters.selectedClientFilter.includes(entry.clientName))
      }

      if (filters.selectedProjectFilter && filters.selectedProjectFilter.length > 0) {
        filtered = filtered.filter((entry) => filters.selectedProjectFilter.includes(entry.projectName))
      }

      return filtered
    },

    /**
     * Apply a saved filter to the current view
     * @param {Object} filter - The saved filter object
     */
    async applySavedFilter(filter) {
      if (!filter || !filter.filters) {
        return
      }

      const { filters } = filter

      // Apply all filter values
      this.selectedPersonFilter = filters.selectedPersonFilter || []
      this.selectedTimeTypeFilter = filters.selectedTimeTypeFilter || []
      this.selectedClientFilter = filters.selectedClientFilter || []
      this.selectedProjectFilter = filters.selectedProjectFilter || []
      this.tableSearch = filters.tableSearch || ''

      // Apply date range if different from current
      if (filters.dateRange && filters.dateRange.length === 2) {
        const [savedStart, savedEnd] = filters.dateRange
        const [currentStart, currentEnd] = this.currentDateRange

        if (savedStart !== currentStart || savedEnd !== currentEnd) {
          await this.onApplyDateRange(filters.dateRange)
        }
      }

      // Switch to the tab if needed
      if (filters.activeTab && filters.activeTab !== this.currentTabId) {
        this.setCurrentTabId(filters.activeTab)
      }

      // Close filter menu
      this.filterMenuOpen = false
    },

    /**
     * Open rename dialog for a saved filter
     * @param {Object} filter - The filter to rename
     */
    openRenameFilterDialog(filter) {
      this.filterToRename = filter
      this.renameFilterDialog = true
    },

    /**
     * Update saved filter with current filter state
     * @param {Object} filter - The filter to update
     */
    async updateFilterWithCurrent(filter) {
      try {
        await this.$store.dispatch('savedFilters/updateFilter', {
          id: filter.id,
          updates: {
            filters: this.currentFilterState,
          },
        }, { root: true })
      } catch (error) {
        // Error handled by store action
      }
    },

    /**
     * Duplicate a saved filter
     * @param {Object} filter - The filter to duplicate
     */
    async duplicateFilter(filter) {
      try {
        await this.$store.dispatch('savedFilters/saveFilter', {
          name: `${filter.name} (Copy)`,
          view: filter.view,
          filters: filter.filters,
        }, { root: true })
      } catch (error) {
        // Error handled by store action
      }
    },

    /**
     * Clean URL query parameters by keeping only recognized parameters
     * @param {Object} currentQuery - Current route query object
     * @returns {Object} Cleaned query object with only valid parameters
     */
    cleanQueryParameters(currentQuery) {
      // Define the whitelist of recognized query parameters for the Time component
      // layout: 'calendar' | 'columns' (for time-entry) or 'cards' | 'table' (for other tabs)
      // groupByProperty: 'personName' | 'clientName' | 'timeClassificationName' | 'projectName' | 'time_entry_date'
      const validParameters = ['tab', 'weekOffset', 'weekNumber', 'year', 'startDate', 'endDate', 'action', 'layout', 'groupByProperty']

      const cleanedQuery = {}

      // Only keep parameters that are in our whitelist
      validParameters.forEach((param) => {
        if (currentQuery[param] !== undefined) {
          cleanedQuery[param] = currentQuery[param]
        }
      })

      return cleanedQuery
    },

    /**
     * Fetch time data for a specific tab
     * @param {String} tabId - The tab to fetch data for ('time-entry', 'mtd', 'billable', 'projects')
     * @param {Object} options - Optional configuration
     * @param {Boolean} options.showNotification - Whether to show success/error notifications (default: true)
     * @returns {Promise<Object>} Result object with success status and data
     */
    async fetchTabData(tabId, { showNotification = true } = {}) {
      this.$log.info(['[Time.vue] fetchTabData() - START for tab:', tabId])

      // Set loading state for this tab
      this.$set(this.tabLoadingStates, tabId, true)

      try {
        // Initialize response variable to hold API response
        let response

        // Get accessible persons
        const hasAccessToOrgTimeEntries = this.$store.getters['auth/hasAccessToOrgTimeEntries']
        const accessiblePersons = await this.getAccessiblePersons()

        // Get the date range for this specific tab
        let dateRange

        if (tabId === 'time-entry') {
          dateRange = this.currentDateRange
        } else {
          dateRange = this.dateRanges[tabId] || this.dateRanges.mtd
        }

        this.$log.info(['[Time.vue] fetchTabData() - Date range for tab', tabId, ':', dateRange[0], 'to', dateRange[1]])

        const startDate = dates.get(dateRange[0])
        const endDate = dates.get(dateRange[1])

        // Fetch data based on user permissions
        if (hasAccessToOrgTimeEntries) {
          // Different API calls based on tab type
          if (tabId === 'time-entry' || tabId === 'mtd' || tabId === 'last-month') {
            response = await api.viewTimeEntriesByDateRange([startDate, endDate])
          } else if (tabId === 'projects') {
            response = await api.viewTimeEntriesByDateRange([startDate, endDate], { timeClassificationName: 'PROJECT' })
          } else if (tabId === 'billable') {
            response = await api.viewTimeEntriesByDateRange([startDate, endDate], { timeClassificationName: 'BILLABLE' })
          } else {
            response = await api.viewTimeEntriesByDateRange([startDate, endDate])
          }
        } else {
          // Fetch for accessible persons
          const personIds = accessiblePersons.map((person) => {
            const isNumber = typeof person.personId === 'number'

            return (isNumber && person.personId) || null
          }).join(',')

          if (personIds) {
            response = await api.viewTimeEntriesByPersonId(personIds, [startDate, endDate])
          } else {
            const { personId } = this.$store.state.auth.user

            response = await api.viewTimeEntriesByPersonId(personId, [startDate, endDate])
          }
        }

        if (response.error) {
          throw new Error(response.error)
        }

        this.$log.info(['[Time.vue] fetchTabData() - Fetched', response.content.length, 'entries for tab:', tabId])

        // Transform and store the data in this tab's array
        const transformedData = this.transformTimeEntries(response.content || [])

        // Update timesByTab with the transformed time entries.
        this.$set(this.timesByTab, tabId, transformedData)

        // Update tab count with tab-specific filtering logic
        // Use filteredTimesForTab() to get date-filtered entries, then apply tab-specific criteria
        const dateFilteredEntries = this.filteredTimesForTab(tabId)

        let count = 0

        switch (tabId) {
          case 'time-entry':
          case 'mtd':
          case 'last-month':
            count = dateFilteredEntries.length
            break

          case 'billable':
            count = dateFilteredEntries.length
            break

          case 'projects':
            count = dateFilteredEntries.length
            break

          default:
            count = dateFilteredEntries.length
        }

        // Update the tab count.
        this.$set(this.tabCounts, tabId, count)

        // Show success notification if requested.
        if (showNotification) {
          const uniquePersonIds = new Set(transformedData.map((entry) => entry.personId).filter(Boolean))
          const personCount = uniquePersonIds.size

          this.$store.commit('tools/snackbar', true, { root: true })
          this.$store.commit('tools/snack', {
            message: `Loaded ${transformedData.length} time entries (${dateRange[0]} to ${dateRange[1]}) for ${personCount} ${personCount === 1 ? 'person' : 'persons'}`,
            status: 'success',
          }, { root: true })
        }

        return { success: true, data: transformedData }
      } catch (error) {
        // Show error notification if requested
        if (showNotification) {
          this.$store.commit('tools/snackbar', true, { root: true })
          this.$store.commit('tools/snack', {
            message: `Error loading ${tabId} data: ${error.message}`,
            status: 'error',
          }, { root: true })
        }

        return { success: false, error: error.message }
      } finally {
        this.$set(this.tabLoadingStates, tabId, false)

        this.$log.info(`[Time.vue] fetchTabData() - Loading state cleared for tab: ${tabId}`)

        // NOTE: Main loading state (this.loading) is ONLY controlled by created() hook
        // Do NOT set loading = false here to avoid race condition with template rendering
      }
    },

    /**
     * Fetch data for tabs other than the current one in the background
     * This enables smooth tab switching and accurate tab counts
     * @param {String} currentTabId - The current active tab to exclude
     */
    async fetchOtherTabsInBackground(currentTabId) {
      const allTabs = ['time-entry', 'mtd', 'last-month', 'billable', 'projects']
      const otherTabs = allTabs.filter((tab) => tab !== currentTabId)

      // Fetch all other tabs in parallel without waiting (fire and forget)
      // Use allSettled to prevent one failure from affecting others
      Promise.allSettled(
        otherTabs.map((tabId) => this.fetchTabData(tabId, {
          showNotification: false, // Silent background loading
        })),
      ).then(() => {
        // Optional: Could trigger a final recalculation here if needed
        // For now, counts update as each tab loads
      })
    },

    async fetchTimeData({ skipBackgroundFetch = false } = {}) {
      // NOTE: Do NOT set loading = true here if called from created()
      // Only set loading for subsequent fetches (user-initiated refreshes)
      const isInitialLoad = this.loading === true

      if (!isInitialLoad) {
        this.loading = true
      }

      this.error = null

      try {
        // Get current active tab
        const currentActiveTab = this.activeTabMap[this.activeTab] || 'time-entry'

        // PHASE 1: Fetch current tab's data (blocking - user waits)
        const result = await this.fetchTabData(currentActiveTab, {
          showNotification: true,
        })

        if (!result.success) {
          this.$log.error(['[Time.vue] fetchTimeData() - ERROR: Failed to fetch data for current tab:', currentActiveTab, result.error])

          this.error = result.error

          return
        }

        this.$log.info(['[Time.vue] fetchTimeData() - Successfully fetched data for current tab:', currentActiveTab])

        // PHASE 2: Fetch other tabs in background (non-blocking - progressive loading)
        // Skip background fetches if this is a refresh of just the current tab
        // (e.g., week shifter in Time Entry tab shouldn't refetch MTD/Billable/Projects)
        if (!skipBackgroundFetch) {
          this.$log.info(['[Time.vue] fetchTimeData() - Fetching other tabs in background:', currentActiveTab])

          this.fetchOtherTabsInBackground(currentActiveTab)
        }
      } catch (error) {
        this.$log.error(['[Time.vue] fetchTimeData() - ERROR:', error.message, error.stack])

        this.error = error.message

        // Show error message
        this.$store.commit('tools/snackbar', true, { root: true })

        this.$store.commit('tools/snack', {
          message: `Error loading time entries: ${error.message}`,
          status: 'error',
        }, { root: true })
      } finally {
        // Only clear loading if this was a user-initiated refresh (not initial load)
        // Initial load's loading state is controlled by created() hook
        if (!isInitialLoad) {
          this.$log.info(['[Time.vue] fetchTimeData() - Setting loading = false (user refresh)'])

          this.loading = false
        } else {
          this.$log.info(['[Time.vue] fetchTimeData() - Initial load complete, loading state managed by created() hook'])
        }
      }
    },

    /**
     * Refresh all data (called by Refresh button)
     */
    async refreshData() {
      this.isRefreshing = true
      try {
        await this.fetchTimeData()
      } finally {
        this.isRefreshing = false
      }
    },

    async getAccessiblePersons() {
      // Replicate the delegates getter logic from internal store
      const currentUser = this.$store.getters['auth/currentUser']
      const hasAccessToOrgTimeEntries = this.$store.getters['auth/hasAccessToOrgTimeEntries']

      if (!currentUser) {
        return []
      }

      const { personId: currentPersonId } = currentUser
      const { personsArray } = this

      if (!personsArray.length) {
        // Fallback to just current user if no persons data
        return [{ ...currentUser }]
      }

      return personsArray.reduce((delegates, person) => {
        const isSelf = person.personId === currentPersonId
        const isManager = person.managerPersonId === currentPersonId

        if (isSelf || isManager || hasAccessToOrgTimeEntries) {
          delegates.push({ ...person })
        }

        return delegates
      }, [])
    },

    /**
     * @function transformTimeEntries
     * @desc Transform raw time entries from API to expected format
     * @param {Array|Object} rawEntries - Raw time entries from API
     * @returns {Array} Transformed time entries
     */
    transformTimeEntries(rawEntries) {
      // Defensive check for rawEntries
      if (!rawEntries) {
        return []
      }

      // Handle case where rawEntries might be an object with a content property
      let entries = rawEntries

      if (rawEntries.content && Array.isArray(rawEntries.content)) {
        entries = rawEntries.content
      }

      // Final safety check - ensure we have an array
      if (!Array.isArray(entries)) {
        // If it's still not an array, log what we got and return empty array
        this.$store.commit('tools/snackbar', true, { root: true })

        this.$store.commit('tools/snack', {
          message: `Warning: Expected array but got ${typeof entries}`,
          status: 'warning',
        }, { root: true })
        return []
      }

      // Transform the raw API response to match expected format
      // This follows the same pattern as tools/transformTimeItems
      return entries.map((entry) => {
        const safeEntry = entry && typeof entry === 'object' ? entry : {}

        // Build a new object using explicit fields instead of spreading untrusted input keys.
        const transformedEntry = {
          assignment: safeEntry.assignment || null,
          billableActionDuration: safeEntry.billableActionDuration || 0,
          billingCode: safeEntry.billingCode || this.lookupClientBillingCode(safeEntry.clientId, safeEntry.clientName),
          client: safeEntry.client || null,
          clientId: safeEntry.clientId || null,
          clientName: safeEntry.clientName || null,
          description: safeEntry.workNote || safeEntry.description || '',
          duration: safeEntry.billableActionDuration || safeEntry.duration || 0,
          notes: safeEntry.notes || null,
          personCode: safeEntry.personCode || null,
          personEmail: safeEntry.personEmail || null,
          personId: safeEntry.personId || null,
          personName: safeEntry.personName || null,
          projectId: safeEntry.projectId || null,
          projectCode: this.resolveProjectCode(safeEntry.projectCode, safeEntry.projectId, safeEntry.projectName),
          projectName: safeEntry.projectName || null,
          serviceCode: safeEntry.serviceCode || null,
          serviceId: safeEntry.serviceId || null,
          serviceName: safeEntry.serviceName || null,
          status: safeEntry.status || null,
          teamCode: safeEntry.teamCode || null,
          teamId: safeEntry.teamId || null,
          timeClassificationId: safeEntry.timeClassificationId || null,
          timeClassificationName: safeEntry.timeClassificationName || null,
          timeEntryId: safeEntry.timeEntryId || null,

          // Parse date string as local date-only (no time/timezone interpretation)
          // API returns dates like "2025-10-13T00:00:00" which should be treated as local midnight
          // Extract just the date part (YYYY-MM-DD) to avoid any timezone issues
          timeEntryDate: safeEntry.timeEntryDate
            ? dayjs(safeEntry.timeEntryDate.split('T')[0]).startOf('day')
            : null,

          // Map API response fields to expected internal fields
          time_entry_date: safeEntry.timeEntryDate,
          person_id: safeEntry.personId,
          client_id: safeEntry.clientId,
          time_classification_id: safeEntry.timeClassificationId,
          person_email: safeEntry.personEmail || null,
          project_id: safeEntry.projectId,
          service_id: safeEntry.serviceId,
        }

        // Add the formatted fields that DataTable expects
        transformedEntry.hhmm = format.hhmm(transformedEntry.duration)
        transformedEntry.timeEntryDateFormatted = transformedEntry.timeEntryDate
          ? format.date(transformedEntry.timeEntryDate, 'MM/DD/YYYY')
          : ''

        return transformedEntry
      })
    },

    setCurrentTabId(tabId) {
      if (tabId !== this.showTimeEntryList) {
        this.showTimeEntryList = tabId
      }
    },

    getSummarySubtitle() {
      const { currentTabId } = this

      if (currentTabId === 'time-entry') {
        // For custom timeShift text, show that text directly.
        if (this.customTimeShiftText.includes(this.timeShift)) {
          return this.timeShiftText
        }

        // For other timeShift values, show the week range.
        return this.getWeekDateRange
      }

      const tabsWithDateRanges = ['mtd', 'last-month', 'billable', 'projects']

      if (tabsWithDateRanges.includes(currentTabId)) {
        return this.getWeekDateRange
      }

      // Default to empty subtitle.
      return ''
    },

    getDefaultDateRangeForView(view) {
      // Define default date ranges for each view type
      // 📅 To add new date ranges, see docs/time-tabs-quick-reference.md
      const dateRangeMap = {
        'time-entry': (() => {
          // Current week range for time entry view (weekly calendar)
          const week = dates.getWeek(0) // Current week
          const startOfWeek = week[0] // Monday
          const endOfWeek = week[6] // Sunday
          return [dates.getFormattedDate(startOfWeek), dates.getFormattedDate(endOfWeek)]
        })(),
        mtd: dates.mtdFormatted(),
        'last-month': getLastMonthDateRangeFormatted(),
        billable: dates.mtdFormatted(),
        projects: dates.mtdFormatted(),
        // Future examples:
        // 'last-month': dates.lastMonthFormatted(),
        // 'ytd': dates.ytdFormatted(),
        // 'custom-range': dates.customRangeFormatted(),
      }

      return dateRangeMap[view] || dates.mtdFormatted()
    },

    applyDefaultDateRangeForView(view) {
      const safeView = this.getSafeTabKey(view)
      if (!safeView) return

      // Only apply default if the user hasn't set a custom date range for this view
      if (safeView === 'time-entry' && !this.dateRanges['time-entry']) {
        this.$set(this.dateRanges, 'time-entry', this.getDefaultDateRangeForView('time-entry'))
      }

      if (safeView === 'mtd' && !this.dateRanges.mtd) {
        this.$set(this.dateRanges, 'mtd', this.getDefaultDateRangeForView('mtd'))
      }

      if (safeView === 'last-month' && !this.dateRanges['last-month']) {
        this.$set(this.dateRanges, 'last-month', this.getDefaultDateRangeForView('last-month'))
      }

      if (safeView === 'billable' && !this.dateRanges.billable) {
        this.$set(this.dateRanges, 'billable', this.getDefaultDateRangeForView('billable'))
      }

      if (safeView === 'projects' && !this.dateRanges.projects) {
        this.$set(this.dateRanges, 'projects', this.getDefaultDateRangeForView('projects'))
      }
    },

    shouldRefetchForTabChange(oldTab, newTab) {
      const safeTab = this.getSafeTabKey(newTab)
      if (!safeTab) return true

      // PERFORMANCE: Skip refetch if data already exists for this tab
      // Background fetching populates all tabs during initial load
      let existingData = null
      switch (safeTab) {
        case 'time-entry':
          existingData = this.timesByTab['time-entry']
          break
        case 'mtd':
          existingData = this.timesByTab.mtd
          break
        case 'last-month':
          existingData = this.timesByTab['last-month']
          break
        case 'billable':
          existingData = this.timesByTab.billable
          break
        case 'projects':
          existingData = this.timesByTab.projects
          break
        default:
          existingData = null
          break
      }
      if (existingData && existingData.length > 0) {
        return false
      }

      // No data for this tab yet - need to fetch
      return true
    },

    // Helper method for adding new date range types in the future
    addCustomDateRangeType(tabName, dateRangeFunction) {
      const safeTab = this.getSafeTabKey(tabName)
      if (!safeTab || typeof dateRangeFunction !== 'function') return

      // Example usage: this.addCustomDateRangeType('last-month', dates.lastMonthFormatted)
      // This would allow easy addition of new tabs with custom date ranges
      const nextRange = dateRangeFunction()
      if (safeTab === 'time-entry') this.$set(this.dateRanges, 'time-entry', nextRange)
      if (safeTab === 'mtd') this.$set(this.dateRanges, 'mtd', nextRange)
      if (safeTab === 'last-month') this.$set(this.dateRanges, 'last-month', nextRange)
      if (safeTab === 'billable') this.$set(this.dateRanges, 'billable', nextRange)
      if (safeTab === 'projects') this.$set(this.dateRanges, 'projects', nextRange)
    },

    getSafeTabKey(tabKey) {
      if (typeof tabKey !== 'string') return null
      if (!SAFE_TIME_TAB_KEYS.includes(tabKey)) return null
      return tabKey
    },

    getDateRangeForView(view) {
      const safeView = this.getSafeTabKey(view)
      if (!safeView) return null

      switch (safeView) {
        case 'time-entry':
          return this.dateRanges['time-entry'] || null
        case 'mtd':
          return this.dateRanges.mtd || null
        case 'last-month':
          return this.dateRanges['last-month'] || null
        case 'billable':
          return this.dateRanges.billable || null
        case 'projects':
          return this.dateRanges.projects || null
        default:
          return null
      }
    },

    setDateRangeForView(view, dateRange) {
      const safeView = this.getSafeTabKey(view)
      if (!safeView || !Array.isArray(dateRange) || dateRange.length < 2) return

      const normalizedRange = [dateRange[0], dateRange[1]]
      switch (safeView) {
        case 'time-entry':
          this.$set(this.dateRanges, 'time-entry', normalizedRange)
          break
        case 'mtd':
          this.$set(this.dateRanges, 'mtd', normalizedRange)
          break
        case 'last-month':
          this.$set(this.dateRanges, 'last-month', normalizedRange)
          break
        case 'billable':
          this.$set(this.dateRanges, 'billable', normalizedRange)
          break
        case 'projects':
          this.$set(this.dateRanges, 'projects', normalizedRange)
          break
        default:
          break
      }
    },

    openTimeEntryFormCreate(weekdate) {
      return this.$store.dispatch('internal/openTimeEntryFormCreate', weekdate, { root: true })
    },
    openTimeEntryFormUpdate(entry) {
      return this.$store.dispatch('internal/openTimeEntryFormUpdate', entry, { root: true })
    },
    openTimeEntryFormDuplicate(entry, dateValue) {
      // Create a duplicate entry with the same data but for the current date
      const safeEntry = entry && typeof entry === 'object' ? entry : {}
      const clientId = safeEntry.client_id || safeEntry.clientId || null
      const projectId = safeEntry.project_id || safeEntry.projectId || null
      const serviceId = safeEntry.service_id || safeEntry.serviceId || null
      const timeClassificationId = safeEntry.time_classification_id || safeEntry.timeClassificationId || null
      const personId = safeEntry.person_id || safeEntry.personId || null
      const duration = safeEntry.duration || safeEntry.timeEntryDuration || null

      const duplicateEntry = {
        client_id: clientId,
        project_id: projectId,
        service_id: serviceId,
        time_classification_id: timeClassificationId,
        person_id: personId,
        duration,
        description: safeEntry.description || safeEntry.notes || '',

        // Preserve display/auxiliary fields for modal UX.
        assignment: safeEntry.assignment || null,
        client: safeEntry.client || null,
        clientId,
        clientName: safeEntry.clientName || null,
        notes: safeEntry.notes || '',
        personCode: safeEntry.personCode || null,
        projectId,
        projectName: safeEntry.projectName || null,
        serviceCode: safeEntry.serviceCode || serviceId,
        serviceName: safeEntry.serviceName || null,
        status: safeEntry.status || null,
        teamCode: safeEntry.teamCode || null,
        teamId: safeEntry.teamId || null,
        timeClassificationId,
        timeClassificationName: safeEntry.timeClassificationName || null,
        timeEntryDuration: duration,
        timeEntryId: null, // Remove ID so it creates a new entry
        time_entry_date: dates.getFormattedDate(dateValue || dates.now()),
        timeEntryDate: dateValue || dates.now(),
      }

      return this.$store.dispatch('internal/openTimeEntryFormCreate', duplicateEntry, { root: true })
    },
    openTimeEntryFormDelete(entry) {
      return this.$store.dispatch('internal/openTimeEntryFormDelete', entry, { root: true })
    },

    // Bulk operation methods
    isEntrySelected(entryId) {
      return this.selectedEntries.includes(entryId)
    },

    toggleEntrySelection(entryId, selected) {
      if (selected) {
        if (!this.selectedEntries.includes(entryId)) {
          this.selectedEntries.push(entryId)
        }
      } else {
        this.selectedEntries = this.selectedEntries.filter((id) => id !== entryId)
      }
    },

    areAllDayEntriesSelected(entries) {
      const filteredEntries = this.getFilteredEntries(entries)

      if (filteredEntries.length === 0) return false

      return filteredEntries.every((entry) => this.selectedEntries.includes(entry.timeEntryId))
    },

    areSomeDayEntriesSelected(entries) {
      const filteredEntries = this.getFilteredEntries(entries)

      return filteredEntries.some((entry) => this.selectedEntries.includes(entry.timeEntryId))
    },

    toggleDaySelection(entries, selected) {
      const filteredEntries = this.getFilteredEntries(entries)
      if (selected) {
        // Add all entries from this day
        filteredEntries.forEach((entry) => {
          if (!this.selectedEntries.includes(entry.timeEntryId)) {
            this.selectedEntries.push(entry.timeEntryId)
          }
        })
      } else {
        // Remove all entries from this day
        const entryIds = filteredEntries.map((e) => e.timeEntryId)

        this.selectedEntries = this.selectedEntries.filter((id) => !entryIds.includes(id))
      }
    },

    clearSelection() {
      this.selectedEntries = []
    },

    openBulkDeleteDialog() {
      this.bulkOperationType = 'delete'
      this.bulkOperationDialog = true
    },

    openBulkCopyDialog() {
      this.bulkOperationType = 'copy'
      this.copyToDate = dates.getFormattedDate(dates.now())
      this.bulkOperationDialog = true
    },

    async confirmBulkOperation() {
      if (this.bulkOperationType === 'delete') {
        await this.bulkDeleteEntries()
      } else if (this.bulkOperationType === 'copy') {
        await this.bulkCopyEntries()
      }

      this.bulkOperationDialog = false
      this.clearSelection()
    },

    async bulkDeleteEntries() {
      const entriesToDelete = this.timesByTab[this.currentTabId].filter((entry) => this.selectedEntries.includes(entry.timeEntryId))

      try {
        // Delete each entry in parallel
        await Promise.all(
          entriesToDelete.map((entry) => this.$store.dispatch('internal/openTimeEntryFormDelete', entry, { root: true })),
        )

        // Refresh data - skip background fetch since we're only updating current tab
        await this.fetchTimeData({ skipBackgroundFetch: true })

        this.$store.commit('tools/snackbar', true, { root: true })

        this.$store.commit('tools/snack', {
          message: `Successfully deleted ${entriesToDelete.length} ${entriesToDelete.length === 1 ? 'entry' : 'entries'}`,
          status: 'success',
        }, { root: true })
      } catch (error) {
        this.$store.commit('tools/snackbar', true, { root: true })

        this.$store.commit('tools/snack', {
          message: `Error deleting entries: ${error.message}`,
          status: 'error',
        }, { root: true })
      }
    },

    async bulkCopyEntries() {
      const entriesToCopy = this.times.filter((entry) => this.selectedEntries.includes(entry.timeEntryId))

      try {
        // Copy each entry to the new date in parallel
        await Promise.all(
          entriesToCopy.map((entry) => {
            const safeEntry = entry && typeof entry === 'object' ? entry : {}
            const clientId = safeEntry.client_id || safeEntry.clientId || null
            const projectId = safeEntry.project_id || safeEntry.projectId || null
            const serviceId = safeEntry.service_id || safeEntry.serviceId || null
            const timeClassificationId = safeEntry.time_classification_id || safeEntry.timeClassificationId || null
            const personId = safeEntry.person_id || safeEntry.personId || null
            const duration = safeEntry.duration || safeEntry.timeEntryDuration || null

            const duplicateEntry = {
              client_id: clientId,
              project_id: projectId,
              service_id: serviceId,
              time_classification_id: timeClassificationId,
              person_id: personId,
              duration,
              description: safeEntry.description || safeEntry.notes || '',

              assignment: safeEntry.assignment || null,
              client: safeEntry.client || null,
              clientId,
              clientName: safeEntry.clientName || null,
              notes: safeEntry.notes || '',
              personCode: safeEntry.personCode || null,
              projectId,
              projectName: safeEntry.projectName || null,
              serviceCode: safeEntry.serviceCode || serviceId,
              serviceName: safeEntry.serviceName || null,
              status: safeEntry.status || null,
              teamCode: safeEntry.teamCode || null,
              teamId: safeEntry.teamId || null,
              timeClassificationId,
              timeClassificationName: safeEntry.timeClassificationName || null,
              timeEntryDuration: duration,
              timeEntryId: null,
              time_entry_date: this.copyToDate,
              timeEntryDate: dates.get(this.copyToDate),
            }

            return this.$store.dispatch('internal/openTimeEntryFormCreate', duplicateEntry, { root: true })
          }),
        )

        // Refresh data - skip background fetch since we're only updating current tab
        await this.fetchTimeData({ skipBackgroundFetch: true })

        this.$store.commit('tools/snackbar', true, { root: true })

        this.$store.commit('tools/snack', {
          message: `Successfully copied ${entriesToCopy.length} ${entriesToCopy.length === 1 ? 'entry' : 'entries'} to ${this.copyToDate}`,
          status: 'success',
        }, { root: true })
      } catch (error) {
        this.$store.commit('tools/snackbar', true, { root: true })

        this.$store.commit('tools/snack', {
          message: `Error copying entries: ${error.message}`,
          status: 'error',
        }, { root: true })
      }
    },

    async timeShiftLeft() {
      // Clear any custom date range for time-entry to use timeShift-based navigation
      // This means time-shift will override custom ranges when navigating.
      if (this.currentTabId === 'time-entry' && this.hasCustomTimeEntryRange) {
        delete this.dateRanges['time-entry']
      }

      // Get current timeShift value (handle null case for custom ranges)
      const currentShift = this.timeShift !== null ? this.timeShift : 0

      // Use the computed property setter to update both store and URL
      this.timeShift = currentShift - 1

      // Fetch new week data when in time-entry view
      // Skip background fetch - only refresh Time Entry tab
      if (this.currentTabId === 'time-entry') {
        await this.fetchTimeData({ skipBackgroundFetch: true })
      }
    },
    async timeShiftRight() {
      // Clear any custom date range for time-entry to use timeShift-based navigation
      // This means time-shift will override custom ranges when navigating.
      if (this.currentTabId === 'time-entry' && this.hasCustomTimeEntryRange) {
        delete this.dateRanges['time-entry']
      }

      // Get current timeShift value (handle null case for custom ranges)
      const currentShift = this.timeShift !== null ? this.timeShift : 0

      // Use the computed property setter to update both store and URL
      this.timeShift = currentShift + 1

      // Fetch new week data when in time-entry view
      // Skip background fetch - only refresh Time Entry tab
      if (this.currentTabId === 'time-entry') {
        await this.fetchTimeData({ skipBackgroundFetch: true })
      }
    },

    async onApplyDateRange(dateRange) {
      this.applyingDateFilter = true

      try {
        const currentView = this.currentTabId

        if (currentView === 'time-entry') {
          // For time-entry view, check if this matches a standard Monday-Sunday week
          const startDate = dates.get(dateRange[0])

          // Set endDate safely, defaulting to startDate if invalid.
          const endDateDefault = dates.get(dateRange[0])
          const endDate = dates.get(dateRange[1]) || endDateDefault

          // Quick check: is this a Monday to Sunday range?
          const isMonday = startDate.day() === 1 // Monday = 1 in dayjs
          const isSunday = endDate.day() === 0 // Sunday = 0 in dayjs
          const daysDiff = endDate.diff(startDate, 'days')
          const isExactWeek = isMonday && isSunday && daysDiff === 6

          if (isExactWeek) {
            // Calculate the number of weeks difference from current week.
            const timeShift = dates.getWeeksFromNow(startDate)

            // Calculate the week dates for the given timeShift.
            const currentWeek = dates.getWeek(timeShift)
            const weeksDiff = Math.round(startDate.diff(currentWeek[0], 'weeks', true))

            // Use timeShift navigation for standard weeks
            this.timeShift = weeksDiff
          } else {
            // Custom date range - use URL parameters for precise bookmarking
            const cleanedQuery = this.cleanQueryParameters(this.$route.query)

            // Clear timeShift parameter and set custom range
            delete cleanedQuery.weekOffset

            cleanedQuery.startDate = dates.getFormattedDate(dateRange[0])
            cleanedQuery.endDate = dates.getFormattedDate(dateRange[1])

            // Update URL to reflect custom date range
            this.$router.push({ query: cleanedQuery })
          }
        } else {
          // For other views (mtd, billable, projects), store date range and update URL
          const formattedStart = dates.getFormattedDate(dateRange[0])
          const formattedEnd = dates.getFormattedDate(dateRange[1])

          this.setDateRangeForView(currentView, [formattedStart, formattedEnd])

          // Update URL with date range parameters
          const cleanedQuery = this.cleanQueryParameters(this.$route.query)
          cleanedQuery.startDate = formattedStart
          cleanedQuery.endDate = formattedEnd

          // Update URL to reflect date range
          this.$router.push({ query: cleanedQuery })
        }

        // Re-fetch data with new date range
        // Skip background fetch - only refresh current tab
        await this.fetchTimeData({ skipBackgroundFetch: true })
      } catch (error) {
        // Error handling is done in fetchTimeData
        // Silently continue - error display handled in fetchTimeData
      } finally {
        this.applyingDateFilter = false
      }
    },

    async onClearDateRange() {
      this.applyingDateFilter = true

      try {
        const currentView = this.currentTabId

        // Clear URL date parameters for all views
        const cleanedQuery = this.cleanQueryParameters(this.$route.query)
        delete cleanedQuery.startDate
        delete cleanedQuery.endDate

        if (currentView === 'time-entry') {
          // For time-entry view, also clear weekOffset parameter and reset to current week
          delete cleanedQuery.weekOffset

          // Update store
          if (this.$store.hasModule && this.$store.hasModule('internal')) {
            this.$store.commit('internal/timeShift', 0)
          }
        } else {
          // For other views, reset to default date range
          this.setDateRangeForView(currentView, this.getDefaultDateRangeForView(currentView))
        }

        // Update URL
        this.$router.push({ query: cleanedQuery })

        // Re-fetch data with default date range
        // Skip background fetch - only refresh current tab
        await this.fetchTimeData({ skipBackgroundFetch: true })
      } catch (error) {
        // Error handling is done in fetchTimeData
        // Silently continue - error display handled in fetchTimeData
      } finally {
        this.applyingDateFilter = false
      }
    },

    sameMonthText: (monday, sunday) => `${dates.getFormattedDate(monday, 'MMM DD')}-${dates.getFormattedDate(sunday, 'DD, YYYY')}`,
    crossMonthText: (monday = {}, sunday = {}) => {
      const { $y: startYear } = monday
      const { $y: endYear } = sunday
      const sameYear = startYear === endYear
      const startFormat = sameYear ? 'MMM DD' : 'MMM DD, YYYY'
      const endFormat = sameYear ? 'DD, YYYY' : 'MMM DD, YYYY'

      return `${dates.getFormattedDate(monday, startFormat)}-${dates.getFormattedDate(sunday, endFormat)}`
    },

    getEntriesForDay(weekdate) {
      // Normalize both dates to start of day in local timezone before comparing
      // This ensures we're doing a date-only comparison regardless of time components
      const matchDate = (entry) => {
        if (!entry || !entry.timeEntryDate || !weekdate) return false

        // Ensure both have the startOf method (are dayjs objects)
        if (typeof entry.timeEntryDate.startOf !== 'function' || typeof weekdate.startOf !== 'function') {
          return false
        }

        // Normalize both to start of day and compare timestamps
        const entryStartOfDay = entry.timeEntryDate.startOf('day').valueOf()
        const weekdateStartOfDay = weekdate.startOf('day').valueOf()

        return entryStartOfDay === weekdateStartOfDay
      }

      return this.times.filter(matchDate)
    },

    /**
     * Group entries within a day for calendar view nested grouping
     * @param {Array} entries - time entries for a single day
     * @returns {Array} - grouped entries or single ungrouped list
     */
    getGroupedEntriesForDay(entries) {
      if (!this.hasActiveGrouping || !entries || entries.length === 0) {
        return [{ groupName: null, isPartOfGroup: false, items: entries || [] }]
      }

      return groupUtil.byProperty(entries, this.currentGroupByProperties)
    },

    /**
     * Check if a calendar group is collapsed
     * @param {number} dayValue - day timestamp
     * @param {string} groupName - group name
     */
    isCalendarGroupCollapsed(dayValue, groupName) {
      const key = `${dayValue}:${groupName}`
      return !!this.collapsedCalendarGroups[key]
    },

    /**
     * Toggle calendar group collapsed state
     * @param {number} dayValue - day timestamp
     * @param {string} groupName - group name
     */
    toggleCalendarGroup(dayValue, groupName) {
      const key = `${dayValue}:${groupName}`
      this.$set(this.collapsedCalendarGroups, key, !this.collapsedCalendarGroups[key])
    },

    /**
     * Get total duration for entries in a group
     * @param {Array} entries - time entries
     * @returns {number} - total minutes
     */
    getGroupTotalMinutes(entries) {
      return (entries || []).reduce((total, entry) => total + (entry.duration || 0), 0)
    },
    getAllEntriesFromCurrentWeek() {
      return this.currentWeek.reduce((entries, day) => {
        entries = entries.concat(day.entries)

        return entries
      }, [])
    },

    getAllEntriesFromTimeView() {
      // Get all entries from the current week for the time-entry view
      if (this.currentWeek && this.currentWeek.week && Array.isArray(this.currentWeek.week)) {
        return this.currentWeek.week.reduce((entries, day) => {
          entries = entries.concat(day.entries || [])
          return entries
        }, [])
      }
      return []
    },

    /**
     * @function setEntryClass
     * @desc Determines the class for a week's date entry based on
     * dark mode, locked status, and index in the week.
     * @param weekdate - moment date object
     * @param index - index of the entry in the week
     */
    setEntryClass(weekdate, index) {
      const { lockedClass, lockedDates } = this
      const color = this.themeSurfaceColor('white', 'vuetifyblack')

      const calendarDate = weekdate.date.valueOf()
      const isLocked = lockedDates.includes(calendarDate)
      const colored = isLocked ? lockedClass : color
      const uncolored = isLocked ? lockedClass : color
      const striping = index % 2 === 0 ? colored : uncolored

      return striping
    },

    durationFormatter(duration) {
      return durations.durationFormatter(duration)
    },

    /**
     * Get color for time classification chip
     * @param {String} classification - Time classification name
     * @returns {String} Vuetify color name
     */
    getTimeClassificationColor(classification) {
      const colors = {
        BILLABLE: 'success',
        'NON-BILLABLE': 'primary',
        PROJECT: 'info',
        ADX: 'grey darken-1',
        'ADX-PG': 'grey darken-2',
      }
      return colors[classification] || 'grey'
    },

    /**
     * Format date for display
     * @param {String|Number} date - Date to format
     * @returns {String} Formatted date string
     */
    formatDate(date) {
      if (!date) return 'N/A'
      return dates.formatDate(date, 'MM/DD/YYYY')
    },

    getTotal(entries) {
      const seed = 0

      return entries.reduce((total, entry) => {
        total += entry.duration

        return total
      }, seed)
    },

    async getProjects() {
      // Fetch projects if needed.
      if (!this.projectsResponse || this.projectsResponse.length === 0) {
        this.loadingProjects = true
        try {
          const apiResponse = await api.getProjects()
          const { content, error } = apiResponse
          const isSuccessResponse = Array.isArray(content)
          const foundError = !isSuccessResponse ? error || content : error

          if (foundError) {
            return { error: foundError }
          }

          const projects = isSuccessResponse ? content : []

          this.$store.commit('tools/projectsResponse', projects)
        } finally {
          this.loadingProjects = false
        }
      }

      return Promise.resolve(this.projectsResponse)
    },

    async getServices() {
      // Fetch services if needed.
      if (!this.servicesResponse || this.servicesResponse.length === 0) {
        this.loadingServices = true
        try {
          const apiResponse = await api.getServices()
          const { content, error } = apiResponse
          const isSuccessResponse = Array.isArray(content)
          const foundError = !isSuccessResponse ? error || content : error

          if (foundError) {
            return { error: foundError }
          }

          const services = isSuccessResponse ? content : []

          this.$store.commit('tools/servicesResponse', services)
        } finally {
          this.loadingServices = false
        }
      }

      return Promise.resolve(this.servicesResponse)
    },

    /**
     * Fetch time types for filter dropdowns and time entry forms
     * Component-level data management (self-managed view pattern)
     */
    async getTimeTypes() {
      if (this.timeTypes.length > 0) return // Already loaded

      this.loadingTimeTypes = true

      try {
        const response = await api.getTimeTypes()

        if (response?.content) {
          this.timeTypes = response.content
        }
      } catch (error) {
        // Silent fail - filters will show empty, not critical
      } finally {
        this.loadingTimeTypes = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~src/styles/variables";

// Calendar view nested grouping styles
.calendar-group-section {
  border-left: 3px solid var(--v-primary-base);
  background: rgba(var(--v-primary-base), 0.02);
  border-radius: 0 8px 8px 0;
  padding-left: 8px;
}

.calendar-group-header {
  cursor: pointer;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.03);
  transition: background-color 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.06);
  }
}

.transition-rotate {
  transition: transform 0.2s ease;
}

.rotate-90 {
  transform: rotate(90deg);
}

// Dark mode adjustments for calendar grouping
.theme--dark {
  .calendar-group-section {
    background: rgba(255, 255, 255, 0.02);
  }

  .calendar-group-header {
    background: rgba(255, 255, 255, 0.05);

    &:hover {
      background: rgba(255, 255, 255, 0.08);
    }
  }
}

// Enhanced summary cards styling
.summary-cards-container {
  .summary-card-col {
    display: flex;
    flex-direction: column;
  }

  .summary-card {
    height: 100%;
    transition: all 0.3s ease;
    border-radius: 12px !important;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
    }

    .summary-title {
      min-height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .summary-subtitle {
      min-height: 20px;
      line-height: 1.2;
    }

    .summary-value {
      line-height: 1.1;
      letter-spacing: -0.5px;
    }
  }
}

.hours-label {
  font-size: small;
  margin-left: 1ch;
}

.daily-total {
  font-size: 14px;
  text-transform: uppercase;
  font-weight: 500;
}

.search-field {
  ::v-deep .v-input__control {
    min-height: auto;
  }

  ::v-deep .v-text-field__details {
    display: none;
  }
}

.quick-filter-select {
  ::v-deep .v-input__control {
    min-height: auto;
  }

  ::v-deep .v-text-field__details {
    display: none;
  }

  &.compact {
    ::v-deep .v-select__selection {
      font-size: 0.875rem;
    }
  }
}

.clear-filters-btn {
  white-space: nowrap;
  min-width: auto !important;
  padding: 0 8px !important;
}

.filter-active {
  background-color: rgba(25, 118, 210, 0.08) !important;
  border-color: var(--v-primary-base) !important;
}

.v-data-table {
  width: 100%;
  border: 1px solid rgba(0, 0, 0, 0.08);

  // General styling (less specific, must come first)
  .v-btn {
    margin-top: -3px;
  }

  ::v-deep th {
    font-size: 13px;
    font-weight: bold !important;
    word-wrap: break-word;
    word-break: break-word;
    background-color: #f8f9fa !important;
    border-bottom: 2px solid rgba(0, 0, 0, 0.12) !important;
    color: #424242 !important;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 12px 16px !important;
  }

  ::v-deep td {
    font-size: 13px;
    word-wrap: break-word;
    word-break: break-word;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    padding: 12px 16px !important;
  }

  // Enhanced table styling (less specific tbody tr, must come first)
  &.enhanced-table {
    // Row hover effects
    ::v-deep tbody tr {
      transition: background-color 0.2s ease, box-shadow 0.2s ease;

      &:hover {
        background-color: rgba(25, 118, 210, 0.08) !important;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        transform: translateY(-1px);
      }
    }
  }

  // Striped table styling (more specific nth-of-type, comes after enhanced)
  &.striped-table {
    ::v-deep tbody tr:nth-of-type(odd) {
      background-color: rgba(0, 0, 0, 0.02);
    }
  }

  // Entry actions styling (more specific button rules)
  .entry-actions {
    min-width: 160px;
    display: flex;
    justify-content: flex-end;
    gap: 4px;

    .v-btn {
      transition: all 0.2s ease;
    }
  }

  // Time type chip styling
  .time-type-chip {
    font-weight: 600;
    font-size: 12px;
  }

  // Dark mode styling (most specific, comes last)
  .theme--dark & {
    border: 1px solid rgba(255, 255, 255, 0.12);

    ::v-deep th {
      background-color: #2d2d2d !important;
      border-bottom: 2px solid rgba(255, 255, 255, 0.2) !important;
      color: #f5f5f5 !important; // Increased contrast from #e0e0e0 to #f5f5f5
    }

    ::v-deep td {
      border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    }

    &.enhanced-table ::v-deep tbody tr:hover {
      background-color: rgba(25, 118, 210, 0.15) !important;
    }

    &.striped-table ::v-deep tbody tr:nth-of-type(odd) {
      background-color: rgba(255, 255, 255, 0.03);
    }
  }
}

// Clock icon sizing in card headers
.clock-icon-12px {
  font-size: 12px !important;
}

// ============================================================================
// CALENDAR VIEW CARD STYLING (Time Entry tab and List views)
// ============================================================================

.time-entry-card {
  height: 100%;
  transition: all 0.3s ease;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  overflow: hidden;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
    border-color: rgba(var(--v-primary-base), 0.4);
  }

  &.entry-selected {
    border-color: var(--v-primary-base);
    border-width: 2px;
    background-color: rgba(var(--v-primary-base), 0.04);
  }

  &.entry-locked {
    opacity: 0.6;
    background-color: rgba(0, 0, 0, 0.02);
  }

  .v-card__title {
    background: linear-gradient(to bottom, rgba(0, 0, 0, 0.02), transparent);
    min-height: 56px;
  }

  .v-card__text {
    padding-top: 12px !important;
    padding-bottom: 12px !important;
    flex-grow: 1;
  }

  // Time type chip in header - subtle outlined style
  .time-type-chip-header {
    font-size: 0.75rem;
    padding: 0 12px;
    height: 26px;
    letter-spacing: 0.3px;
    font-weight: 500;
    border-width: 1.5px !important;
    background-color: transparent !important;

    // Add subtle background tint on hover
    &:hover {
      opacity: 0.8;
    }
  }

  // Compact version for column calendar view
  .time-type-chip-compact {
    font-size: 0.65rem;
    padding: 0 8px;
    height: 22px;
    letter-spacing: 0.3px;
    font-weight: 500;
    border-width: 1.5px !important;
    background-color: transparent !important;

    &:hover {
      opacity: 0.8;
    }
  }

  // Icon-based info row styling
  .entry-info-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    line-height: 1.5;

    .icon-fixed-width {
      width: 16px;
      margin-top: 3px;
      flex-shrink: 0;
    }

    .entry-info-content {
      flex: 1;
      color: rgba(0, 0, 0, 0.87);

      .client-billing-row {
        display: flex;
        align-items: center;
        gap: 6px;
        min-width: 0;
      }

      .client-name-text {
        flex: 1 1 auto;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .billing-code-badge {
        flex: 0 0 auto;
        white-space: nowrap;
        padding: 1px 6px;
        border-radius: 999px;
        border: 1px solid rgba(var(--v-primary-base), 0.35);
        color: rgba(var(--v-primary-base), 1);
        background-color: rgba(var(--v-primary-base), 0.08);
      }

      // Ensure appropriate font weights
      .body-2 {
        font-weight: 500; // Medium weight for all body-2 text in info rows

        &.font-weight-bold {
          font-weight: 700; // Bold for emphasis
        }

        &.font-weight-medium {
          font-weight: 500; // Medium weight
        }
      }

      .caption {
        font-weight: 500; // Medium weight for captions

        &.font-weight-medium {
          font-weight: 500;
        }
      }
    }
  }

  // Notes display styling
  .entry-notes-display {
    padding: 10px 12px;
    background-color: rgba(0, 0, 0, 0.02);
    border-radius: 8px;
    border-left: 3px solid rgba(var(--v-primary-base), 0.3);

    .work-notes-text {
      font-style: italic;
      color: rgba(0, 0, 0, 0.65);
      line-height: 1.5;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
      margin: 0;
    }
  }

  .entry-detail {
    .detail-label {
      font-weight: 600;
      font-size: 0.7rem;
      letter-spacing: 0.8px;
      margin-bottom: 4px;
      opacity: 0.7;
      color: #5e6769; // coolgray for light mode
    }

    .detail-value {
      color: rgba(0, 0, 0, 0.87);
      word-break: break-word;
      line-height: 1.5;

      &.work-notes-text {
        font-style: italic;
        color: rgba(0, 0, 0, 0.65);
        line-height: 1.5;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
    }
  }

  .v-divider {
    margin: 0;
  }

  .v-card__actions {
    margin-top: auto;
    background: rgba(0, 0, 0, 0.02);
    border-top: 1px solid rgba(0, 0, 0, 0.08);

    .v-btn {
      transition: all 0.2s ease;
      // Increased minimum touch target size for accessibility
      min-width: 44px;
      min-height: 44px;
    }

    // Ensure proper spacing for icon groups
    .d-flex.align-center {
      gap: 4px;
    }

    // Subtle action button colors (grey-blue for better brand cohesion)
    .action-btn {
      color: $color-monochrome-actiongray !important;
    }
  }
}

// Action button hover states - must come after base styles for proper specificity
.time-entry-card .v-card__actions .action-btn.action-btn-duplicate:hover:not(:disabled) {
  color: #4caf50 !important; // Green on hover
}

.time-entry-card .v-card__actions .action-btn.action-btn-edit:hover:not(:disabled) {
  color: #1976d2 !important; // Blue on hover
}

.time-entry-card .v-card__actions .action-btn.action-btn-delete:hover:not(:disabled) {
  color: #f44336 !important; // Red on hover
}

// Column Calendar View Styling
.time-entries-columns {
  display: flex;
  gap: 0;
  overflow-x: auto; // Enable horizontal scrolling
  -webkit-overflow-scrolling: touch; // Smooth scrolling on iOS

  .col {
    flex: 1 1 140px; // Allow grow, shrink, with base of 140px
    min-width: 140px; // Ensure minimum readable width
    max-width: none; // Allow columns to expand on larger screens
    padding-left: 0;
    padding-right: 0;
  }

  > .col:not(:last-child) {
    border-right: 1px solid rgba(0, 0, 0, 0.12);
  }

  > .col:last-child {
    padding-right: 0;
  }

  .day-column-header {
    border-radius: 12px 12px 0 0;
    border-bottom: 3px solid var(--v-primary-base);
    margin-bottom: 0;

    .v-card__title {
      background: linear-gradient(to bottom, rgba(var(--v-primary-base), 0.08), rgba(var(--v-primary-base), 0.02));
      padding: 12px !important;
    }

    .full-width {
      width: 100%;
    }

    // Day column date styling
    .day-column-date {
      color: #055289; // darkestblue for light mode

      &.locked {
        color: #5e6769; // coolgray for locked dates
      }
    }
  }

  .day-column-entries {
    min-height: 200px;
    padding: 4px 8px 4px 8px;
  }

  .time-entry-card-compact {
    border-radius: 8px;
    transition: all 0.2s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
    }

    &.entry-selected {
      border-color: var(--v-primary-base);
      border-width: 2px;
      background-color: rgba(var(--v-primary-base), 0.04);
    }

    .card-header-compact {
      min-height: auto;
      padding: 6px 8px !important;
    }

    // Enhanced styling for cleaner card layout
    .entry-duration-display {
      display: flex;
      align-items: center;
      padding: 4px 0;
    }

    .entry-info-row {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      line-height: 1.4;

      .icon-fixed-width {
        width: 14px;
        margin-top: 2px;
        flex-shrink: 0;
      }

      .entry-info-content {
        flex: 1;
        color: rgba(0, 0, 0, 0.87);

        // Ensure appropriate font weights for compact view
        .body-2 {
          font-weight: 500; // Medium weight

          &.font-weight-bold {
            font-weight: 700;
          }

          &.font-weight-medium {
            font-weight: 500;
          }
        }

        .caption {
          font-weight: 500; // Medium weight for captions

          &.font-weight-medium {
            font-weight: 500;
          }
        }
      }
    }

    .entry-notes-display {
      padding: 8px;
      background-color: rgba(0, 0, 0, 0.02);
      border-radius: 6px;
      border-left: 3px solid rgba(var(--v-primary-base), 0.3);

      .work-notes-text {
        font-style: italic;
        color: rgba(0, 0, 0, 0.65);
        line-height: 1.4;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        margin: 0;
      }
    }

    .entry-detail-compact {
      .detail-label {
        font-weight: 600;
        font-size: 0.65rem;
        letter-spacing: 0.5px;
        margin-bottom: 2px;
        opacity: 0.7;
        color: #5e6769; // coolgray for light mode
      }

      .detail-value {
        color: rgba(0, 0, 0, 0.87);
        word-break: break-word;
        line-height: 1.4;

        &.work-notes-text {
          font-style: italic;
          color: rgba(0, 0, 0, 0.65);
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      }
    }
  }
}

// Empty state styling
.empty-state-text {
  color: #5e6769; // coolgray for light mode
}

// Enhanced search and filter section styling (higher specificity)
.search-filter-section {
  border-radius: 8px !important;

  .search-field-enhanced {
    ::v-deep .v-input__control {
      min-height: 42px;
      max-height: 42px;
      border-radius: 6px;
      min-width: 180px;
    }

    ::v-deep .v-input__slot {
      padding: 0 12px;
      background: rgba(255, 255, 255, 0.9) !important;
      border: 1px solid rgba(0, 0, 0, 0.12);
      border-radius: 6px;
      transition: all 0.2s ease;

      &:hover {
        border-color: rgba(25, 118, 210, 0.4);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      }
    }

    ::v-deep .v-text-field__slot input {
      font-weight: 500;
      font-size: 0.875rem;
      padding: 0;
      margin-top: -1px;
      line-height: 1.4;

      &::placeholder {
        color: rgba(0, 0, 0, 0.54);
        font-weight: 400;
        opacity: 1;
      }
    }

    ::v-deep .v-input__icon--prepend-inner {
      margin-top: -1px;
      margin-right: 8px;
      color: rgba(0, 0, 0, 0.54);
    }

    ::v-deep .v-input__icon--append {
      margin-top: -1px;
      color: rgba(0, 0, 0, 0.54);
    }

    // Focus state
    ::v-deep .v-input--is-focused .v-input__slot {
      border-color: var(--v-primary-base) !important;
      box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.1);
    }
  }

  .filter-button-enhanced {
    font-weight: 500;
    letter-spacing: 0.25px;
    height: 36px !important;
    text-transform: none;
    border-radius: 6px;
    min-width: 120px;

    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      transform: translateY(-1px);
      transition: all 0.2s ease;
    }

    &.filter-active {
      background-color: rgba(25, 118, 210, 0.08) !important;
      border-color: var(--v-primary-base) !important;
      color: var(--v-primary-base) !important;

      .v-chip {
        background-color: var(--v-primary-base) !important;
        color: white !important;
      }
    }
  }

  // Controls container styling
  .controls-container {
    // Desktop: horizontal layout with date picker on the right
    display: flex;
    align-items: center;
    gap: 16px;

    .search-filter-row {
      flex-grow: 1;

      .col {
        display: flex;
        align-items: center;

        &.flex-shrink-0 {
          flex-shrink: 0;
        }

        &.flex-grow-1 {
          flex-grow: 1;
        }
      }
    }

    .date-picker-row {
      flex-shrink: 0;

      .col {
        display: flex;
        align-items: center;
      }
    }
  }
}

// Large screen: max-width for search field
@media (min-width: 1200px) {
  .search-filter-section {
    .search-field-enhanced {
      ::v-deep .v-input__control {
        max-width: 400px;
      }
    }
  }
}

// Tab styling (must come before .time-view-header-compact for specificity)
.time-view-tabs {
  ::v-deep .v-tab {
    text-transform: none;
    font-weight: 500;
  }

  ::v-deep .v-tab--active {
    font-weight: 600;
  }
}

// General button styles (must come before pseudo-class selectors)
.week-container .week-navigation-group .v-btn {
  transition: all 0.2s ease;
}

// View Mode Toggle Styles (must come before .time-view-header-compact)
.v-btn-toggle.v-btn-toggle--borderless .v-btn {
  border-radius: 0 !important;
}

.v-btn-toggle.v-btn-toggle--borderless .v-btn:first-child {
  border-radius: 4px 0 0 4px !important;
}

.v-btn-toggle.v-btn-toggle--borderless .v-btn:last-child {
  border-radius: 0 4px 4px 0 !important;
}

// General button hover states (must come after base button styles)
.v-data-table .entry-actions .v-btn:hover {
  transform: scale(1.1);
}

.week-container .week-navigation-group .v-btn:hover {
  transform: scale(1.1);
}

.time-entry-card .v-card__actions .v-btn:hover {
  transform: scale(1.15);
}

.time-entry-card .v-card__actions .v-btn:focus-visible {
  outline: 2px solid var(--v-primary-base);
  outline-offset: 2px;
}

// ============================================================================
// COMPACT HEADER STYLES (Moderate Specificity - Before Dark Theme Overrides)
// ============================================================================

// Remove old header styles (keeping for backward compatibility if needed)
.time-view-header {
  display: none; // Hide if old structure accidentally renders
}

// Compact header styling
.time-view-header-compact {
  .tabs-section {
    background: white;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    overflow: hidden; // Ensures children respect border-radius
  }

  .header-toolbar {
    min-height: 56px !important;
    max-height: 56px !important;
    background: white !important;
  }

  .header-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: rgba(var(--v-primary-base), 0.08);
  }

  .time-view-tabs-inline {
    ::v-deep .v-tabs-bar {
      height: 56px !important;
      background-color: white !important;
    }

    ::v-deep .v-tab {
      font-size: 0.875rem;
      font-weight: 500;
      letter-spacing: 0.25px;
      text-transform: none;
      min-width: 100px;
      padding: 0 16px;
      height: 56px;

      &:hover {
        background-color: rgba(var(--v-primary-base), 0.04);
      }
    }

    ::v-deep .v-tabs-slider {
      height: 3px !important;
    }
  }

  // Tab navigation arrows - extra small
  .tab-nav-arrow {
    font-size: 0.75rem !important; // 12px - smaller than xs
    opacity: 0.7;
    transition: opacity 0.2s;

    &:hover {
      opacity: 1;
    }

    // Override Vuetify's .v-icon__component sizing
    &.v-slide-group.v-item-group > .v-slide-group__next,
    &.v-slide-group.v-item-group > .v-slide-group__prev {
      height: 14px !important;
      width: 14px !important;
    }
  }

  // Tab count chips styling
  .tab-count-chip {
    background-color: #e0e0e0 !important;
    border-color: #bdbdbd !important;
    color: #616161 !important;
    font-weight: 600;
  }

  .theme--dark .time-view-tabs-inline {
    ::v-deep .v-tabs-bar {
      background-color: #2d2d2d !important;
    }

    .tab-count-chip {
      background-color: #424242 !important;
      border-color: #616161 !important;
      color: #e0e0e0 !important;
    }
  }

  .add-time-btn-compact {
    font-weight: 600;
    letter-spacing: 0.3px;
    text-transform: none;
    height: 38px !important;
    padding: 0 20px !important;
    border-radius: 6px;
  }

  .action-buttons-container {
    display: flex;
    align-items: center;
    min-width: 400px; // Reserve space for smooth transitions between states
  }

  .controls-bar {
    border-radius: 0; // No border-radius since it's inside .tabs-section
    margin: 0; // No margin since it's inside .tabs-section

    .controls-row {
      row-gap: 4px;
    }

    .controls-view-mode,
    .controls-date-nav {
      margin-left: 0;
    }

    @media (min-width: 960px) {
      .controls-view-mode {
        margin-left: auto;
      }

      .controls-date-nav {
        margin-left: 8px;
      }
    }

    @media (max-width: 959px) {
      .controls-date-nav {
        justify-content: flex-start !important;
      }
    }

    ::v-deep .v-expand-transition-enter-active,
    ::v-deep .v-expand-transition-leave-active {
      transition: all 0.3s ease;
    }

    .search-field-compact {
      ::v-deep .v-input__control {
        min-height: 38px !important;
      }

      ::v-deep .v-input__slot {
        min-height: 38px !important;
        padding: 0 12px !important;
        border-radius: 6px;
      }

      ::v-deep fieldset {
        border-color: rgba(0, 0, 0, 0.12);
      }

      .search-icon {
        color: rgba(0, 0, 0, 0.54);
        margin-right: 8px;
      }
    }

    .filter-btn-compact {
      text-transform: none;
      letter-spacing: 0.25px;
      font-weight: 500;
      height: 36px !important;
      transition: all 0.2s ease;

      &.filter-active {
        background-color: rgba(var(--v-primary-base), 0.08);
      }
    }

    // View mode toggles
    .view-mode-toggle {
      ::v-deep .v-btn {
        transition: all 0.2s ease;
      }
    }

    // Active Saved Filter Indicator - Must come before filter-select
    .active-filter-chip {
      font-size: 0.875rem;
      height: 28px;

      ::v-deep .v-chip__content {
        display: flex;
        align-items: center;
      }
    }

    // Expandable Filter Section Styles
    .filter-select {
      ::v-deep .v-input__control {
        min-height: 53px !important;
      }

      ::v-deep .v-input__slot {
        min-height: 53px !important;
        padding: 0 12px !important;
        border-radius: 6px;
      }

      ::v-deep fieldset {
        border-color: rgba(0, 0, 0, 0.12);
        border-width: 1px;
      }

      ::v-deep .v-label {
        font-size: 0.875rem;
        font-weight: 500;
        letter-spacing: 0.25px;
        color: rgba(0, 0, 0, 0.6);
      }

      ::v-deep .v-label--active {
        font-size: 0.75rem;
        font-weight: 600;
      }

      // Font Awesome prepend inner icons
      .filter-icon {
        font-size: 16px;
        color: rgba(0, 0, 0, 0.54);
        margin-top: 2px;
        margin-right: 8px;
      }

      ::v-deep .v-select__selections {
        padding: 0;
        line-height: 1.5;

        input {
          font-size: 0.875rem;
          font-weight: 500;
        }
      }

      ::v-deep .v-select__selection {
        font-size: 0.875rem;
        font-weight: 500;
        color: rgba(0, 0, 0, 0.87);
        margin-top: 2px;
      }

      // Multi-select chips
      ::v-deep .v-chip {
        max-width: 100%;

        .v-chip__content {
          display: inline-block;
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }

      // Dropdown menu list items with checkboxes
      ::v-deep .v-list-item {
        min-height: 40px;

        .v-list-item__action {
          margin-right: 8px;
          min-width: auto;
        }

        .v-list-item__content {
          overflow: hidden;
        }

        .v-list-item__title {
          font-size: 0.875rem;
          line-height: 1.5;
        }
      }
    }
  }
}

// Dark theme support for compact header (must come before hover selectors)
.theme--dark .time-view-header-compact {
  .tabs-section {
    background: #2d2d2d;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  }

  .header-toolbar {
    background: #2d2d2d !important;
  }

  .header-icon-wrapper {
    background: rgba(var(--v-primary-base), 0.15);
  }

  .controls-bar {
    background: rgba(255, 255, 255, 0.05);

    .search-field-compact {
      ::v-deep fieldset {
        border-color: rgba(255, 255, 255, 0.12);
      }

      .search-icon {
        color: rgba(255, 255, 255, 0.54);
      }
    }

    .filter-btn-compact {
      &.filter-active {
        background-color: rgba(var(--v-primary-base), 0.15);
      }
    }

    // Dark theme filter section styles
    .filter-select {
      ::v-deep fieldset {
        border-color: rgba(255, 255, 255, 0.12);
      }

      ::v-deep .v-label {
        color: rgba(255, 255, 255, 0.7);
      }

      .filter-icon {
        color: rgba(255, 255, 255, 0.54);
      }

      ::v-deep .v-select__selection {
        color: rgba(255, 255, 255, 0.87);
      }
    }
  }
}

// Interactive states for compact header
// Using selector lists to avoid specificity conflicts

// Light theme: Focus states for filter selects
.time-view-header-compact .controls-bar .filter-select ::v-deep .v-input--is-focused {
  fieldset {
    border-color: var(--v-primary-base);
    border-width: 2px;
  }

  .v-label {
    color: var(--v-primary-base);
  }

  .filter-icon {
    color: var(--v-primary-base);
  }
}

// Light theme: Hover states for both search and filter
.time-view-header-compact .controls-bar .search-field-compact ::v-deep .v-input__slot:hover fieldset,
.time-view-header-compact .controls-bar .filter-select ::v-deep .v-input__slot:hover fieldset {
  border-color: rgba(var(--v-primary-base), 0.5);
}

// Dark theme: Focus states for filter selects
.theme--dark .time-view-header-compact .controls-bar .filter-select ::v-deep .v-input--is-focused {
  fieldset {
    border-color: var(--v-primary-base);
    border-width: 2px;
  }

  .v-label {
    color: var(--v-primary-base);
  }

  .filter-icon {
    color: var(--v-primary-base);
  }
}

// Dark theme: Hover states for both search and filter
.theme--dark .time-view-header-compact .controls-bar .search-field-compact ::v-deep .v-input__slot:hover fieldset,
.theme--dark .time-view-header-compact .controls-bar .filter-select ::v-deep .v-input__slot:hover fieldset {
  border-color: rgba(var(--v-primary-base), 0.5);
}

// Responsive adjustments for compact header
@media (max-width: 960px) {
  .time-view-header-compact {
    .header-toolbar {
      height: auto !important;
      min-height: 48px !important;
      max-height: none !important;
    }

    .header-icon-wrapper {
      width: 36px;
      height: 36px;
    }

    .time-view-tabs-inline {
      ::v-deep .v-tabs-bar {
        height: 48px !important;
      }

      ::v-deep .v-tab {
        font-size: 0.813rem;
        min-width: 80px;
        padding: 0 12px;
        height: 48px;
      }
    }

    .add-time-btn-compact {
      height: 36px !important;
      padding: 0 16px !important;
      font-size: 0.875rem;

      .ml-2 {
        margin-left: 4px !important;
      }
    }
  }
}

@media (max-width: 768px) {
  .time-view-header-compact {
    .header-icon-wrapper {
      width: 32px;
      height: 32px;
      margin-right: 8px !important;
    }

    .time-view-tabs-inline {
      ::v-deep .v-tabs-bar {
        height: 44px !important;
      }

      ::v-deep .v-tab {
        font-size: 0.75rem;
        min-width: 70px;
        padding: 0 8px;
        height: 44px;
      }

      ::v-deep .v-tabs-slider {
        height: 2px !important;
      }
    }

    .add-time-btn-compact {
      padding: 0 12px !important;
      min-width: auto !important;
    }

    .header-actions {
      .mr-1, .mr-2 {
        margin-right: 4px !important;
      }

      .action-buttons-container {
        min-width: 250px; // Reserve space for smooth transitions
        display: flex;
        align-items: center;
        justify-content: flex-end;
      }
    }

    .controls-bar {
      .search-field-compact {
        ::v-deep .v-input__control {
          min-height: 36px !important;
        }

        ::v-deep .v-input__slot {
          min-height: 36px !important;
        }
      }

      .filter-btn-compact {
        height: 30px !important;
        font-size: 0.813rem;
      }
    }
  }
}

// Dark mode support for search and filter
.theme--dark .search-filter-section {
  .search-field-enhanced {
    ::v-deep .v-input__slot {
      background: rgba(48, 48, 48, 0.9) !important;
      border-color: rgba(255, 255, 255, 0.12);

      &:hover {
        border-color: rgba(25, 118, 210, 0.6);
      }
    }

    ::v-deep .v-input__icon--prepend-inner,
    ::v-deep .v-input__icon--append {
      color: rgba(255, 255, 255, 0.7);
    }

    ::v-deep .v-text-field__slot input::placeholder {
      color: rgba(255, 255, 255, 0.7);
    }
  }

  .filter-button-enhanced.filter-active {
    background-color: rgba(25, 118, 210, 0.15) !important;
  }
}

// Responsive adjustments for search and filter bar
@media (max-width: 768px) {
  .search-filter-section {
    .controls-container {
      // Mobile: stack vertically with search+filter first, then date picker
      flex-direction: column;
      align-items: stretch;
      gap: 12px;

      .search-filter-row {
        // Keep search and filter side-by-side on mobile
        order: 1;

        .col {
          max-width: 100%;

          &.pl-4 {
            padding-left: 16px;
          }
        }
      }

      .date-picker-row {
        // Date picker comes second and takes full width
        order: 2;

        .col {
          flex-grow: 1;
          max-width: 100%;
        }
      }
    }    .search-field-enhanced {
      ::v-deep .v-input__control {
        min-height: 40px;
        max-height: 40px;
      }
    }

    .filter-button-enhanced {
      height: 34px !important;
      min-width: 100px;
      font-size: 0.875rem;
    }
  }
}

@media (max-width: 1024px) {
  .search-filter-section {
    .filter-button-enhanced {
      min-width: 100px;

      span {
        display: none;
      }

      // Show only icon on tablet sizes
      .fa-filter {
        margin-right: 0 !important;
      }
    }
  }
}// Responsive adjustments for summary cards
@media (max-width: 768px) {
  .summary-cards-container {
    .summary-card {
      .summary-value {
        font-size: 1.5rem !important;
      }
    }
  }

  .search-filter-section {
    .v-col {
      margin-bottom: 8px;
    }
  }

  // Card actions responsive adjustments for mobile
  .time-entry-card .v-card__actions,
  .time-entry-card-compact .v-card__actions {
    padding: 8px 12px;

    .v-btn.v-btn--icon {
      // Slightly larger touch targets on mobile
      min-width: 48px;
      min-height: 48px;
    }

    // Adjust spacing between action buttons on mobile
    .d-flex.align-center {
      gap: 8px;
    }
  }

  // Responsive styling for column calendar view on smaller screens
  .time-entries-columns {
    .col {
      min-width: 120px; // Slightly smaller minimum width on mobile
    }

    .day-column-header {
      .v-card__title {
        padding: 8px !important; // Reduce padding
      }

      .day-column-date {
        font-size: 0.65rem; // Slightly smaller text
      }

      .v-chip {
        font-size: 0.7rem; // Smaller chip text
        height: 22px; // Smaller chip height
      }
    }

    .day-column-entries {
      padding: 2px 4px; // Less padding on mobile
    }

    .time-entry-card-compact {
      .card-header-compact {
        padding: 4px 6px !important; // More compact padding
      }

      .entry-info-row {
        gap: 4px; // Reduce gap between icon and text

        .icon-fixed-width {
          width: 12px; // Smaller icons
        }
      }
    }
  }
}

// Global icon selectors (lower specificity, must come before specific filter icon selectors)
.v-data-table .v-icon {
  font-size: 18px;
}

.filter-menu {
  max-width: 360px;

  .v-card-title {
    padding-bottom: 8px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  }

  .v-card-text {
    padding-top: 16px;
  }

  .v-select {
    .v-icon {
      opacity: 0.6;
    }
  }
}

// Filter select dropdown icon styles (higher specificity, must come after global icon selectors)
.time-view-header-compact .controls-bar .filter-select ::v-deep .v-input__append-inner {
  margin-top: 10px !important;

  .v-icon {
    font-size: 20px !important;
    color: rgba(0, 0, 0, 0.54);
  }
}

.theme--dark .time-view-header-compact .controls-bar .filter-select ::v-deep .v-input__append-inner .v-icon {
  color: rgba(255, 255, 255, 0.54);
}

// Week container styling
.week-container {
  width: 100%;

  .container {
    max-width: 100%;
  }

  // Locked week warning cards - softer background
  .v-card.warning {
    background-color: #fff6ea !important;
    border-color: #f0c674 !important;

    .v-card__text {
      color: #8b6914 !important; // Darker text for better contrast
    }
  }

  // Week navigation group styling
  .week-navigation-group {
    .white-space-nowrap {
      white-space: nowrap;
    }
  }
}

.tab-button {
  min-width: 120px;
}

// Select column styling
::v-deep .select-column {
  width: 50px !important;
  min-width: 50px !important;
  max-width: 50px !important;
  padding-left: 8px !important;
  padding-right: 8px !important;
}

// Stacked Progress Bar Styling
.time-breakdown-card {
  border-radius: 8px !important;

  .time-breakdown-title {
    color: #055289; // darkestblue for light mode
  }
}

.stacked-progress-container {
  position: relative;
}

.stacked-progress-bar {
  display: flex;
  width: 100%;
  height: 24px;
  border-radius: 16px;
  overflow: hidden;
  background-color: #f5f5f5; // Light gray background for empty state
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }
}

// Dark mode for Time Breakdown (must come after base styles)
.theme--dark .time-breakdown-card {
  .time-breakdown-title {
    color: #90caf9 !important; // Light blue with good contrast on dark background
  }

  .stacked-progress-bar {
    background-color: #424242 !important; // Darker gray for dark mode empty state
  }
}

.progress-segment {
  height: 100%;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;

  &:hover {
    filter: brightness(1.1);
    z-index: 1;
  }

  &:first-child {
    border-top-left-radius: 16px;
    border-bottom-left-radius: 16px;
  }

  &:last-child {
    border-top-right-radius: 16px;
    border-bottom-right-radius: 16px;
  }

  // Subtle separator between segments
  &:not(:last-child)::after {
    content: '';
    position: absolute;
    right: 0;
    top: 10%;
    height: 80%;
    width: 1px;
    background: rgba(255, 255, 255, 0.3);
  }
}

// Color classes for each time type - Monochromatic scheme using hydrogen.js colors
.segment-billable {
  background: linear-gradient(135deg, #055289 0%, #0062a8 100%); // darkestblue -> darkblue
}

.segment-project {
  background: linear-gradient(135deg, #007dd6 0%, #0094ff 100%); // blue -> brightblue
}

.segment-nonbillable {
  background: linear-gradient(135deg, #0094ff 0%, #00b6ff 100%); // brightblue -> babyblue
}

.segment-adx {
  background: linear-gradient(135deg, #647b8d 0%, #a5b1b9 100%); // gray -> midgray
}

.segment-adxpg {
  background: linear-gradient(135deg, #5e6769 0%, #647b8d 100%); // coolgray -> gray
}

// Legend styling
.legend-container,
.legend-container-inline {
  gap: 4px;

  .legend-chip {
    font-size: 11px !important;
    height: 22px !important;
    padding: 0 8px !important;

    &.segment-billable {
      background: #055289 !important; // darkestblue
    }

    &.segment-project {
      background: #007dd6 !important; // blue
    }

    &.segment-nonbillable {
      background: #0094ff !important; // brightblue
    }

    &.segment-adx {
      background: #647b8d !important; // gray
    }

    &.segment-adxpg {
      background: #5e6769 !important; // coolgray
    }
  }
}

// Responsive adjustments
@media (max-width: 768px) {
  .stacked-progress-bar {
    height: 28px;
    border-radius: 14px;
  }

  .legend-container,
  .legend-container-inline {
    .legend-chip {
      font-size: 10px !important;
      height: 20px !important;
    }
  }
}

// Tablet and smaller - stack legend below on smaller screens
@media (max-width: 960px) {
  .time-breakdown-card {
    .align-center {
      flex-direction: column;
      align-items: flex-start !important;

      .col-auto:last-child {
        width: 100%;
        margin-top: 8px;

        .legend-container-inline {
          justify-content: flex-start !important;
        }
      }
    }
  }
}

// Dark mode for locked week warnings
.theme--dark .week-container {
  .v-card.warning {
    background-color: #4a3f2a !important; // Darker warm background for dark mode
    border-color: #8b7a4f !important;

    .v-card__text {
      color: #f0c674 !important; // Lighter text for dark mode
    }
  }
}

// Dark mode card styling
.theme--dark .time-entry-card {
  background-color: #2d2d2d;
  border-color: rgba(255, 255, 255, 0.12);

  &:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4) !important;
    border-color: rgba(var(--v-primary-base), 0.5);
  }

  &.entry-selected {
    background-color: rgba(var(--v-primary-base), 0.15);
  }

  &.entry-locked {
    opacity: 0.6;
    background-color: rgba(255, 255, 255, 0.05);
  }

  .v-card__title {
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0.05), transparent);
    border-bottom-color: rgba(255, 255, 255, 0.12);
  }

  .entry-date-badge {
    .day-of-week {
      color: rgba(255, 255, 255, 0.6);
    }

    .short-date {
      color: rgba(255, 255, 255, 0.87);
    }
  }

  // Dark mode for icon-based info rows
  .entry-info-row {
    .entry-info-content {
      color: rgba(255, 255, 255, 0.87);

      .billing-code-badge {
        border-color: rgba(100, 181, 246, 0.45);
        color: #90caf9;
        background-color: rgba(100, 181, 246, 0.14);
      }
    }
  }

  // Dark mode for notes display
  .entry-notes-display {
    background-color: rgba(255, 255, 255, 0.05);
    border-left-color: rgba(var(--v-primary-base), 0.5);

    .work-notes-text {
      color: rgba(255, 255, 255, 0.7);
    }
  }

  .entry-detail {
    .detail-label {
      color: #b0bec5 !important; // Lighter gray for better contrast in dark mode
    }

    .detail-value {
      color: rgba(255, 255, 255, 0.87);

      &.work-notes-text {
        color: rgba(255, 255, 255, 0.7);
      }
    }
  }

  .v-card__actions {
    background: rgba(255, 255, 255, 0.05);
    border-top-color: rgba(255, 255, 255, 0.12);

    // Dark mode subtle action button colors (light blue-grey)
    .action-btn {
      color: $color-monochrome-actiongraylite !important;
    }
  }

  // Make outlined time chips more visible in dark mode
  .time-type-chip-header,
  .time-type-chip-compact {
    border-width: 2px !important; // Slightly thicker border for better visibility

    // Lighten the colors for better contrast in dark mode
    &.v-chip--outlined {
      filter: brightness(1.4) saturate(0.9);
    }
  }
}

// Dark mode for Column Calendar View
.theme--dark .time-entries-columns {
  > .col:not(:last-child) {
    border-right-color: rgba(255, 255, 255, 0.12);
  }

  .day-column-header {
    background-color: #2d2d2d;
    border-color: rgba(255, 255, 255, 0.12);

    .v-card__title {
      background: linear-gradient(to bottom, rgba(var(--v-primary-base), 0.15), rgba(var(--v-primary-base), 0.05));
    }

    // Day column date styling for dark mode
    .day-column-date {
      color: #90caf9 !important; // Light blue with good contrast

      &.locked {
        color: #b0bec5 !important; // Lighter gray for locked dates in dark mode
      }
    }
  }

  .time-entry-card-compact {
    background-color: #2d2d2d;
    border-color: rgba(255, 255, 255, 0.12);

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4) !important;
      border-color: rgba(var(--v-primary-base), 0.5);
    }

    &.entry-selected {
      background-color: rgba(var(--v-primary-base), 0.15);
    }

    .entry-info-row {
      .entry-info-content {
        color: rgba(255, 255, 255, 0.87);
      }
    }

    .entry-notes-display {
      background-color: rgba(255, 255, 255, 0.05);

      .work-notes-text {
        color: rgba(255, 255, 255, 0.7);
      }
    }

    .entry-detail-compact .detail-value {
      color: rgba(255, 255, 255, 0.87);

      &.work-notes-text {
        color: rgba(255, 255, 255, 0.7);
      }
    }

    .entry-detail-compact .detail-label {
      color: #b0bec5 !important; // Lighter gray for better contrast in dark mode
    }
  }
}

// Dark mode for empty state text
.theme--dark .empty-state-text {
  color: #b0bec5 !important; // Lighter gray for better contrast in dark mode
}

// Clickable empty state styling
.empty-state-clickable {
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid transparent !important;

  // Override Vuetify's default hover shadow
  &::before {
    display: none !important;
  }

  // Override Vuetify's focus state box-shadow
  &.v-sheet.v-card--hover:focus {
    box-shadow: none !important;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(25, 118, 210, 0.12) !important;
    background-color: rgba(25, 118, 210, 0.04) !important;
    border-color: rgba(25, 118, 210, 0.2) !important;

    .coolgray--text {
      color: var(--v-primary-base) !important;
      transition: color 0.3s ease;
    }
  }

  &:focus {
    outline: none;
    box-shadow: 0 8px 24px rgba(25, 118, 210, 0.12) !important;
    background-color: rgba(25, 118, 210, 0.04) !important;
    border-color: rgba(25, 118, 210, 0.2) !important;
  }

  &:active {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(25, 118, 210, 0.15) !important;
  }
}

// Dark mode specific styling
.theme--dark .empty-state-clickable {
  &.v-sheet.v-card--hover:focus {
    box-shadow: none !important;
  }

  &:hover {
    box-shadow: 0 8px 24px rgba(255, 255, 255, 0.08) !important;
    background-color: rgba(255, 255, 255, 0.05) !important;
    border-color: rgba(255, 255, 255, 0.1) !important;

    .coolgray--text {
      color: rgba(255, 255, 255, 0.9) !important;
    }
  }

  &:focus {
    box-shadow: 0 8px 24px rgba(255, 255, 255, 0.08) !important;
    background-color: rgba(255, 255, 255, 0.05) !important;
    border-color: rgba(255, 255, 255, 0.1) !important;
  }
}

// ============================================================================
// HOVER STATES - Must come LAST to maintain proper CSS specificity order
// All :hover pseudo-class selectors must come after all base button selectors
// ============================================================================

// ============================================================================
// FILTER SAVE BUTTON GROUP - Flattened to satisfy specificity ordering
// ============================================================================

// Save/Save As Button Group - Base styles
.time-view-header-compact .controls-bar .filter-save-group {
  border: none;
  box-shadow: none;
}

.time-view-header-compact .controls-bar .filter-save-group ::v-deep .v-btn {
  border: 1px solid rgba(var(--v-primary-base), 0.5);
  text-transform: none;
  font-weight: 500;
  height: 32px;
  padding: 0 12px;
}

// Dark mode hovers - Must come after base filter-save-group
.theme--dark .v-data-table .entry-actions .v-btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.theme--dark .time-entry-card .v-card__actions .v-btn:focus-visible {
  outline: 2px solid var(--v-primary-base);
  outline-offset: 2px;
}

// Filter button pseudo-class styles - After dark theme base selectors
.time-view-header-compact .controls-bar .filter-save-group ::v-deep .v-btn:hover {
  background-color: rgba(var(--v-primary-base), 0.04);
}

.time-view-header-compact .controls-bar .filter-save-group ::v-deep .v-btn:not(:first-child) {
  border-left: none;
}

.time-view-header-compact .controls-bar .filter-save-group ::v-deep .v-btn:first-child {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.time-view-header-compact .controls-bar .filter-save-group ::v-deep .v-btn:last-child {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.time-view-header-compact .controls-bar .filter-save-group ::v-deep .v-btn:only-child {
  border-radius: 4px;
}

// Dark theme button group
.theme--dark .time-view-header-compact .controls-bar .filter-save-group ::v-deep .v-btn {
  border-color: rgba(var(--v-primary-base), 0.5);
}

// Dark mode action button hover states - Most specific, comes last
.theme--dark .time-entry-card .v-card__actions .action-btn.action-btn-duplicate:hover:not(:disabled) {
  color: #81c784 !important; // Light green on hover
}

.theme--dark .time-entry-card .v-card__actions .action-btn.action-btn-edit:hover:not(:disabled) {
  color: #64b5f6 !important; // Light blue on hover
}

.theme--dark .time-entry-card .v-card__actions .action-btn.action-btn-delete:hover:not(:disabled) {
  color: #e57373 !important; // Light red on hover
}

// Hover states - MUST come last for specificity order
.time-view-header-compact .controls-bar .filter-btn-compact:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

.time-view-header-compact .controls-bar .view-mode-toggle ::v-deep .v-btn:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

.theme--dark .time-view-header-compact .controls-bar .filter-btn-compact:hover {
  background-color: rgba(255, 255, 255, 0.04);
}

.theme--dark .time-view-header-compact .controls-bar .view-mode-toggle ::v-deep .v-btn:hover {
  background-color: rgba(255, 255, 255, 0.04);
}

.theme--dark .time-view-header-compact .controls-bar .filter-save-group ::v-deep .v-btn:hover {
  background-color: rgba(var(--v-primary-base), 0.08);
}
</style>
